import { fabric } from 'fabric'
import opentype from 'opentype.js'
import { CIRCLE, FILL, LINE, RECTANGLE, TEXT, TRIANGLE } from './defaultShapes'
import { useEffect, useMemo, useState } from 'react'
import {
    collectFontFamiliesFromJSON,
    ensureFontsLoaded,
    isFontLoaded,
    refreshCanvasFonts,
    EDITOR_FONTS,
} from './fonts'
import "./fonts.css"
import { exportOffsetSVG, loadOffsetBackground, PX_PER_MM } from './offsetLayout'

// El editor está calibrado a 0.2645833333 mm/px (96 dpi); se usa el mismo
// factor que StepPopover/InputSpinner2 para que las medidas coincidan.

// Limites del zoom del lienzo, compartidos por la rueda, el gesto de dos
// dedos y el encuadre automatico.
//
// El suelo era 0.2, que servia mientras una base no pasaba de 1000 mm. Con
// rotulos de varios metros ya no: 5000 mm son ~18900 px, y al 20% no caben
// en ninguna pantalla. A 0.02 entra un diseno de casi 10 m.
const MIN_CANVAS_ZOOM = 0.02;
const MAX_CANVAS_ZOOM = 8;

// Caché de fuentes opentype para "vectorizar texto" (enfoque cliente): cada
// familia se descarga y parsea una sola vez, y se comparte entre canvases.
const opentypeFontCache = new Map();
const loadOpentypeFont = (family) => {
    if (opentypeFontCache.has(family)) return opentypeFontCache.get(family);
    const file = EDITOR_FONTS[family];
    if (!file) return Promise.reject(new Error(`Fuente "${family}" no registrada`));
    const promise = new Promise((resolve, reject) => {
        opentype.load(`/fonts/${file}.ttf`, (err, font) => (err ? reject(err) : resolve(font)));
    });
    opentypeFontCache.set(family, promise);
    return promise;
};

// ── Ranura de colgado (keyhole) ─────────────────────────────────────────────
const KEYHOLE_DIRECTION_ANGLE_DEG = { d: 0, r: -90, u: 180, l: 90 };
// keyhole_<n>_h<headTenthsMm>_s<shankTenthsMm>_l<slotTenthsMm>_d<downupleftright>
const KEYHOLE_ID_RE = /^keyhole_(\d+)_h(\d+)_s(\d+)_l(\d+)_d([dulr])$/i;

// ── Avellanado pasante (screw) ─────────────────────────────────────────────
// La alternativa a la ranura: en vez de colgar la pieza de un tornillo ya
// puesto, se atornilla la caja a la pared. El marcador es un donut — el
// círculo exterior es la cabeza cónica y el interior la rosca — porque es
// exactamente lo que se ve del agujero mirando la caja por dentro.
//
// Mismo canal que el keyhole: las dos cotas viajan en el `id` en décimas de
// milímetro, no en el tamaño dibujado, porque el backend escala todo el SVG
// (`xy_scale`) y agrandar el panel no puede agrandar un tornillo real.
// screw_<n>_h<headTenthsMm>_s<shankTenthsMm>
const SCREW_ID_RE = /^screw_(\d+)_h(\d+)_s(\d+)$/i;

// ── Pin de Crocs ───────────────────────────────────────────────────────────
// Una seta que sale de la cara de atrás: cuello del grosor de la tira y cabeza
// más ancha que el agujero de la Croc. Mismo canal que el tornillo, todas las
// cotas en el id (décimas de mm), y el modo: `i` integrado en la pieza, `a`
// impreso aparte y encajado en un agujero ciego.
// crocs_<n>_h<cabeza>_n<cuello>_l<largo del cuello>_t<grosor de cabeza>_m<i|a>
const CROCS_ID_RE = /^crocs_(\d+)_h(\d+)_n(\d+)_l(\d+)_t(\d+)_m([ia])$/i;

// insert_<n>_<magnet|nfc>_<circle|square>_s<sizeTenthsMm>[_h<heightTenthsMm>]_t<thicknessTenthsMm>_z<bottomTenthsMm>
const INTERNAL_ELEMENT_ID_RE = /^insert_(\d+)_(magnet|nfc)_(circle|square)_s(\d+)(?:_h(\d+))?_t(\d+)_z(\d+)$/i;

// ── Silbato ────────────────────────────────────────────────────────────────
// El silbato es un conjunto de restas que el usuario acota pieza a pieza:
// rampa de entrada, canal, labio, cámara de resonancia y ranura de salida.
// En Z se comporta como un imán — una única banda intermedia para todas las
// piezas, sin nada pasante.
//
// Los valores por defecto son los medidos en `scripts/silbato/SilbatoNuevo.svg`
// del backend, que es la referencia de la que salen los nombres de las piezas.
// Ahí el labio lleva su ángulo en el propio `transform="rotate(...)"`, así que
// aquí es un parámetro y no una geometría congelada.
const SILBATO_PIECE_NAMES = ['camara', 'salida', 'entrada_canal', 'entrada_rampa', 'labio'];
// insert_<n>_silbato_<pieza>_t<ductTenthsMm>_z<floorTenthsMm>
// Sólo viaja en el id lo que el backend necesita: qué pieza es (de ahí saca su
// papel y su banda en Z) y las dos cotas verticales. Todo lo demás es geometría
// en XY, que el editor ya ha resuelto en los contornos que exporta.
const SILBATO_ID_RE = new RegExp(
    `^insert_(\\d+)_silbato_(${SILBATO_PIECE_NAMES.join('|')})_t(\\d+)_z(\\d+)$`, 'i',
);

const SILBATO_DEFAULTS = {
    scale: 0.35,
    mirrored: false,
    // Cámara de resonancia: es la que manda en el tono.
    chamberWidthMm: 17.9,
    chamberLengthMm: 95.9,
    // Ranura de salida del sonido.
    outletLengthMm: 89.1,
    outletWidthMm: 15.0,
    // Entrada de aire: canal estrecho + rampa por la que se sopla.
    inletWidthMm: 4.8,
    inletLengthMm: 47.7,
    rampAngleDeg: 50.9,
    rampLengthMm: 42.4,
    rampMouthWidthMm: 28.0,
    // Labio: lo que parte el chorro. El ángulo es el dato sensible.
    lipAngleDeg: 53.3,
    lipLengthMm: 52.6,
    lipThicknessMm: 9.9,
    // Cotas en Z, en mm reales (no se escalan con `scale`).
    ductMm: 3,
    floorMm: 1,
};

// El canal de entrada y la punta del labio nacen dentro de la ranura de salida:
// ese cruce es la garganta del silbato. Las dos fracciones son las medidas en
// el SVG de referencia, expresadas sobre el ancho de la ranura para que la
// unión siga cerrada al cambiar sus cotas.
const SILBATO_JUNCTION = { inletTop: 0.383, lipTip: 0.324 };

const SILBATO_LIMITS = {
    scale: [0.05, 4],
    chamberWidthMm: [2, 200], chamberLengthMm: [2, 400],
    outletLengthMm: [2, 400], outletWidthMm: [0.6, 100],
    inletWidthMm: [0.6, 60], inletLengthMm: [2, 300],
    rampAngleDeg: [20, 160], rampLengthMm: [2, 300], rampMouthWidthMm: [1, 200],
    lipAngleDeg: [5, 85], lipLengthMm: [2, 300], lipThicknessMm: [0.6, 100],
    ductMm: [0.6, 100], floorMm: [0, 100],
};

const clampSilbatoParam = (key, value, fallback) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return fallback;
    const range = SILBATO_LIMITS[key];
    return range ? Math.min(Math.max(number, range[0]), range[1]) : number;
};

function normalizeSilbatoParams(params = {}) {
    const next = { ...SILBATO_DEFAULTS };
    Object.keys(SILBATO_DEFAULTS).forEach((key) => {
        if (key === 'mirrored') return;
        if (params[key] === undefined) return;
        next[key] = clampSilbatoParam(key, params[key], SILBATO_DEFAULTS[key]);
    });
    next.mirrored = Boolean(params.mirrored);
    return next;
}

/**
 * Contornos del silbato en mm, ya escalados y espejados.
 *
 * Se construye con la esquina inferior derecha de la cámara de resonancia como
 * origen, porque es la única esquina que comparten cámara y ranura de salida en
 * el SVG de referencia; al final se traslada todo para que la caja envolvente
 * empiece en (0,0). Devolver puntos (y no cadenas `d`) es lo que permite
 * espejar sin recurrir a `flipX`, que fabric no propaga al SVG que exporta.
 */
function buildSilbatoGeometry(rawParams) {
    const p = normalizeSilbatoParams(rawParams);
    const strip = (from, to, widthStart, widthEnd) => {
        const angle = Math.atan2(to[1] - from[1], to[0] - from[0]);
        const nx = -Math.sin(angle);
        const ny = Math.cos(angle);
        return [
            [from[0] + nx * widthStart / 2, from[1] + ny * widthStart / 2],
            [to[0] + nx * widthEnd / 2, to[1] + ny * widthEnd / 2],
            [to[0] - nx * widthEnd / 2, to[1] - ny * widthEnd / 2],
            [from[0] - nx * widthStart / 2, from[1] - ny * widthStart / 2],
        ];
    };
    const box = (x, y, w, h) => [[x, y], [x + w, y], [x + w, y + h], [x, y + h]];

    // Cruce de las tres vías, sobre el borde izquierdo de la cámara.
    const junctionX = -p.chamberWidthMm;
    const inletTopY = -SILBATO_JUNCTION.inletTop * p.outletWidthMm;
    // La rampa arranca sobre el fondo del conducto, no sobre una perpendicular
    // a su propio eje: si no, al inclinarla una esquina se sale de la pared del
    // canal y la otra se queda corta, y ese escalón corta el paso del aire.
    //
    // Y no basta con compartir la arista: dos restas que sólo se tocan dejan
    // una junta de ancho cero que sobrevive al booleano como una lámina de
    // material, y esa lámina tapona el paso. Por eso la rampa mete un tramo
    // recto dentro del conducto —del ancho exacto del canal, así que no
    // ensancha nada— y lo que se cruza es área, no una línea. Es lo mismo que
    // hace el SVG de referencia, donde la boquilla arranca dentro del conducto.
    const inletBottomY = inletTopY + p.inletLengthMm;
    const rampOverlap = Math.min(p.inletWidthMm * 0.8, p.inletLengthMm * 0.5);
    const rampAngle = (p.rampAngleDeg * Math.PI) / 180;
    const rampMouth = [
        junctionX + Math.cos(rampAngle) * p.rampLengthMm,
        inletBottomY + Math.sin(rampAngle) * p.rampLengthMm,
    ];
    const rampNormal = [-Math.sin(rampAngle), Math.cos(rampAngle)];
    const rampLeft = junctionX - p.inletWidthMm / 2;
    const rampRight = junctionX + p.inletWidthMm / 2;
    const rampPoints = [
        [rampLeft, inletBottomY - rampOverlap],
        [rampRight, inletBottomY - rampOverlap],
        [rampRight, inletBottomY],
        [rampMouth[0] - rampNormal[0] * p.rampMouthWidthMm / 2, rampMouth[1] - rampNormal[1] * p.rampMouthWidthMm / 2],
        [rampMouth[0] + rampNormal[0] * p.rampMouthWidthMm / 2, rampMouth[1] + rampNormal[1] * p.rampMouthWidthMm / 2],
        [rampLeft, inletBottomY],
    ];
    const lipTip = [junctionX, -SILBATO_JUNCTION.lipTip * p.outletWidthMm];
    const lipAngle = (p.lipAngleDeg * Math.PI) / 180;
    const lipEnd = [
        lipTip[0] - Math.cos(lipAngle) * p.lipLengthMm,
        lipTip[1] - Math.sin(lipAngle) * p.lipLengthMm,
    ];

    const pieces = [
        { piece: 'camara', role: 'cámara de resonancia', points: box(junctionX, -p.chamberLengthMm, p.chamberWidthMm, p.chamberLengthMm) },
        { piece: 'salida', role: 'salida de aire', points: box(-p.outletLengthMm, -p.outletWidthMm, p.outletLengthMm, p.outletWidthMm) },
        { piece: 'entrada_canal', role: 'canal de entrada', points: box(junctionX - p.inletWidthMm / 2, inletTopY, p.inletWidthMm, p.inletLengthMm) },
        { piece: 'entrada_rampa', role: 'rampa de soplado', points: rampPoints },
        { piece: 'labio', role: 'labio', points: strip(lipTip, lipEnd, p.lipThicknessMm, p.lipThicknessMm) },
    ];

    const all = pieces.flatMap(({ points }) => points);
    const minX = Math.min(...all.map(([x]) => x));
    const maxX = Math.max(...all.map(([x]) => x));
    const minY = Math.min(...all.map(([, y]) => y));
    const maxY = Math.max(...all.map(([, y]) => y));
    const scale = p.scale;

    return {
        params: p,
        widthMm: (maxX - minX) * scale,
        heightMm: (maxY - minY) * scale,
        pieces: pieces.map(({ piece, role, points }) => ({
            piece,
            role,
            points: points.map(([x, y]) => [
                (p.mirrored ? maxX - x : x - minX) * scale,
                (y - minY) * scale,
            ]),
        })),
    };
}

// ── Abrebotellas ───────────────────────────────────────────────────────────
// Una arandela M4 que se mete a mano en una pausa (como el imán) y asoma unos
// milímetros dentro de un rebaje abierto en la cara trasera. El rebaje es hondo
// junto a la arandela y sube en rampa hasta la superficie en el otro extremo:
// la tapa entra inclinada por la rampa y su falda engancha el borde de la
// arandela. Referencia: `scripts/Prototipo Abridor botellines.svg` del backend.
//
// insert_<n>_abridor_arandela_o<exterior>_i<interior>_t<grosor>_z<desde atrás>
// insert_<n>_abridor_rebaje_d<profundidad>_r<rampa>          (rebaje recto)
// insert_<n>_abridor_rebaje_p<s>x<profundidad>_<s>x<prof>...  (rebaje redondo, rectas)
// insert_<n>_abridor_rebaje_c<s>x<profundidad>_<s>x<prof>...  (rebaje redondo, curva)
// (décimas de mm). La dirección de la rampa no viaja en el id: el backend la
// saca del centro de la arandela al del rebaje, así sirve con el grupo girado.
const ABRIDOR_ID_RE = /^insert_(\d+)_abridor_(?:arandela_o(\d+)_i(\d+)_t(\d+)_z(\d+)|rebaje_(?:d\d+_r\d+|[pc]\d+x\d+(?:_\d+x\d+)*))$/i;

const ABRIDOR_WASHERS = {
    din9021: { label: 'M4 ancha (DIN 9021)', outerMm: 12, innerMm: 4.3, thicknessMm: 1 },
    din125: { label: 'M4 normal (DIN 125)', outerMm: 9, innerMm: 4.3, thicknessMm: 0.8 },
};

// Valores medidos en el boceto. Hay que ajustarlos imprimiendo con tapas
// reales; el ancho cubre una chapa corona (~32 mm) con algo de holgura.
// Dos formas de rebaje, para compararlas imprimiendo:
// - recto: rectángulo con un tramo hondo junto a la arandela y una rampa.
// - redondo: semicírculo del diámetro de la tapa junto a la arandela y un cuerpo
//   del mismo ancho con un perfil de profundidades libre. `s` se mide desde el
//   centro del círculo hacia fuera; en el semicírculo manda el primer punto.
const ABRIDOR_POCKET_SHAPES = ['redondo', 'recto'];
// Rampa hasta la cresta de apoyo, cresta, bajada y meseta hasta el final.
const ABRIDOR_DEFAULT_PROFILE = [
    { s: 0, depthMm: 4.5 },
    { s: 10, depthMm: 0.6 },
    { s: 13, depthMm: 0.6 },
    { s: 16, depthMm: 2 },
    { s: 24, depthMm: 2 },
];
const ABRIDOR_PROFILE_POINTS = [2, 8];
// Paso del muestreo de la curva; el backend usa el mismo (`PROFILE_SAMPLE_STEP_MM`).
const ABRIDOR_PROFILE_SAMPLE_STEP_MM = 0.25;

/**
 * El perfil tal como se va a vaciar: los puntos unidos con rectas o, con
 * `smooth`, con una curva suave que pasa por todos ellos.
 *
 * La curva es una cúbica de Hermite monótona por tramos (Fritsch–Butland, la
 * de PCHIP): entre dos puntos nunca se pasa de ellos, así que no sale por
 * encima de la superficie ni por debajo del fondo, y un tramo plano sigue
 * plano. Es **la misma fórmula** que `smooth_profile` del builder del backend:
 * la gráfica del panel y el 3D tienen que coincidir.
 */
function sampleAbridorProfile(profile, smooth, step = ABRIDOR_PROFILE_SAMPLE_STEP_MM) {
    const points = profile.map(point => [point.s, point.depthMm]);
    if (!smooth || points.length < 3) return profile.map(point => ({ ...point }));
    const h = points.slice(1).map(([x], k) => x - points[k][0]);
    const delta = h.map((width, k) => (points[k + 1][1] - points[k][1]) / width);
    const slopes = [delta[0]];
    for (let k = 1; k < points.length - 1; k += 1) {
        slopes.push(delta[k - 1] * delta[k] <= 0
            ? 0
            : (3 * (h[k - 1] + h[k])) / ((2 * h[k] + h[k - 1]) / delta[k - 1] + (h[k] + 2 * h[k - 1]) / delta[k]));
    }
    slopes.push(delta[delta.length - 1]);
    const sampled = [{ s: points[0][0], depthMm: points[0][1] }];
    h.forEach((width, k) => {
        const count = Math.max(Math.ceil(width / step), 1);
        for (let i = 1; i <= count; i += 1) {
            const t = i / count;
            const h00 = 2 * t ** 3 - 3 * t ** 2 + 1;
            const h10 = t ** 3 - 2 * t ** 2 + t;
            const h01 = -2 * t ** 3 + 3 * t ** 2;
            const h11 = t ** 3 - t ** 2;
            sampled.push({
                s: points[k][0] + t * width,
                depthMm: h00 * points[k][1] + h10 * width * slopes[k] + h01 * points[k + 1][1] + h11 * width * slopes[k + 1],
            });
        }
    });
    return sampled;
}

const ABRIDOR_DEFAULTS = {
    pocketShape: 'redondo',
    washer: 'din9021',
    // Lo que asoma la arandela dentro del rebaje: es lo que engancha la tapa.
    protrusionMm: 2,
    pocketWidthMm: 34,
    // Tramo hondo pegado a la arandela y rampa hasta la superficie.
    flatMm: 13,
    rampMm: 10,
    depthMm: 4.5,
    // Altura de la arandela medida desde la cara trasera: es el material que la
    // tapa y la retiene al hacer palanca. Con 1,5 mm se despegaba.
    washerZMm: 2,
    // Rebaje redondo: diámetro de la tapa (chapa corona ~32,1 mm), largo del
    // cuerpo y perfil de profundidades.
    capDiameterMm: 34,
    bodyLengthMm: 24,
    profile: ABRIDOR_DEFAULT_PROFILE,
    // Unir los puntos del perfil con una curva suave en vez de con rectas.
    profileSmooth: true,
};

const ABRIDOR_LIMITS = {
    protrusionMm: [0.5, 4],
    pocketWidthMm: [10, 60],
    flatMm: [0, 60],
    rampMm: [0, 60],
    depthMm: [0.6, 30],
    washerZMm: [0, 30],
    capDiameterMm: [20, 60],
    bodyLengthMm: [0, 60],
};
const ABRIDOR_PROFILE_DEPTH_LIMITS = [0, 30];

function normalizeAbridorProfile(rawProfile, bodyLengthMm) {
    const [minDepth, maxDepth] = ABRIDOR_PROFILE_DEPTH_LIMITS;
    const points = (Array.isArray(rawProfile) ? rawProfile : [])
        .map(point => ({ s: Number(point?.s), depthMm: Number(point?.depthMm) }))
        .filter(point => Number.isFinite(point.s) && Number.isFinite(point.depthMm))
        .map(point => ({
            s: Math.round(Math.min(Math.max(point.s, 0), bodyLengthMm) * 10) / 10,
            depthMm: Math.round(Math.min(Math.max(point.depthMm, minDepth), maxDepth) * 10) / 10,
        }))
        .sort((a, b) => a.s - b.s)
        // Dos puntos en el mismo s no dicen nada: se queda el primero.
        .filter((point, i, all) => i === 0 || point.s > all[i - 1].s)
        .slice(0, ABRIDOR_PROFILE_POINTS[1]);
    if (points.length >= ABRIDOR_PROFILE_POINTS[0]) return points;
    return ABRIDOR_DEFAULT_PROFILE
        .map(point => ({ ...point, s: Math.min(point.s, bodyLengthMm) }))
        .filter((point, i, all) => i === 0 || point.s > all[i - 1].s);
}

function normalizeAbridorParams(params = {}) {
    const next = { ...ABRIDOR_DEFAULTS };
    Object.keys(ABRIDOR_LIMITS).forEach((key) => {
        if (params[key] === undefined) return;
        const number = Number(params[key]);
        if (!Number.isFinite(number)) return;
        const [min, max] = ABRIDOR_LIMITS[key];
        next[key] = Math.min(Math.max(number, min), max);
    });
    if (ABRIDOR_WASHERS[params.washer]) next.washer = params.washer;
    // Un diseño guardado antes de que existiera la forma redonda trae sus cotas
    // pero no `pocketShape`: era recto y tiene que seguir siéndolo.
    if (ABRIDOR_POCKET_SHAPES.includes(params.pocketShape)) next.pocketShape = params.pocketShape;
    else if (params.washer !== undefined) next.pocketShape = 'recto';
    next.profile = normalizeAbridorProfile(params.profile ?? ABRIDOR_DEFAULT_PROFILE, next.bodyLengthMm);
    // Lo mismo con la curva: un perfil guardado cuando sólo había rectas sigue
    // con rectas.
    if (typeof params.profileSmooth === 'boolean') next.profileSmooth = params.profileSmooth;
    else if (params.washer !== undefined) next.profileSmooth = false;
    // Si asomara más de media arandela, se saldría del macizo que la sujeta.
    next.protrusionMm = Math.min(next.protrusionMm, ABRIDOR_WASHERS[next.washer].outerMm / 2 - 0.5);
    // Un rebaje sin largo no es un rebaje.
    if (next.flatMm + next.rampMm < 2) next.flatMm = 2 - next.rampMm;
    return next;
}

const circlePoints = (cx, cy, radius, segments = 48) => Array.from({ length: segments }, (_, i) => {
    const angle = (2 * Math.PI * i) / segments;
    return [cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius];
});

/**
 * Rebaje redondo: cuerpo arriba (su extremo lejano en y = 0), el centro del
 * círculo de la tapa en y = bodyLengthMm, el semicírculo debajo y la arandela
 * asomando `protrusionMm` dentro del arco.
 */
function buildRoundAbridorGeometry(p, washer) {
    const radius = p.capDiameterMm / 2;
    const width = Math.max(p.capDiameterMm, washer.outerMm);
    const cx = width / 2;
    const cy = p.bodyLengthMm;
    const arc = Array.from({ length: 49 }, (_, i) => {
        const angle = (Math.PI * i) / 48;
        return [cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius];
    });
    const body = p.bodyLengthMm > 1e-6 ? [[cx - radius, 0], [cx + radius, 0]] : [];
    const center = [cx, cy + radius + washer.outerMm / 2 - p.protrusionMm];
    return {
        params: p,
        washer,
        widthMm: width,
        heightMm: center[1] + washer.outerMm / 2,
        pocketLengthMm: p.bodyLengthMm + radius,
        pieces: [
            { piece: 'rebaje', role: 'rebaje', contours: [[...body, ...arc]] },
            {
                piece: 'arandela',
                role: 'arandela',
                contours: [
                    circlePoints(center[0], center[1], washer.outerMm / 2),
                    circlePoints(center[0], center[1], washer.innerMm / 2).reverse(),
                ],
            },
        ],
    };
}

/**
 * Contornos del abrebotellas en mm, con la caja envolvente empezando en (0,0).
 *
 * El rebaje va arriba, con la rampa en su borde superior, y la arandela debajo,
 * centrada y asomando `protrusionMm` por encima de la pared del rebaje. Cada
 * pieza trae una lista de contornos: la arandela lleva el agujero como segundo
 * contorno, para que se vea como lo que es.
 */
function buildAbridorGeometry(rawParams) {
    const p = normalizeAbridorParams(rawParams);
    const washer = ABRIDOR_WASHERS[p.washer];
    if (p.pocketShape === 'redondo') return buildRoundAbridorGeometry(p, washer);
    const pocketLength = p.flatMm + p.rampMm;
    const width = Math.max(p.pocketWidthMm, washer.outerMm);
    const pocketX = (width - p.pocketWidthMm) / 2;
    const center = [width / 2, pocketLength + washer.outerMm / 2 - p.protrusionMm];
    return {
        params: p,
        washer,
        widthMm: width,
        heightMm: pocketLength + washer.outerMm - p.protrusionMm,
        pieces: [
            {
                piece: 'rebaje',
                role: 'rebaje',
                contours: [[
                    [pocketX, 0], [pocketX + p.pocketWidthMm, 0],
                    [pocketX + p.pocketWidthMm, pocketLength], [pocketX, pocketLength],
                ]],
            },
            {
                piece: 'arandela',
                role: 'arandela',
                // El agujero en sentido contrario: con relleno `nonzero` queda hueco.
                contours: [
                    circlePoints(center[0], center[1], washer.outerMm / 2),
                    circlePoints(center[0], center[1], washer.innerMm / 2).reverse(),
                ],
            },
        ],
    };
}

/**
 * Path `d` for a single shape uniting the head circle and the slot rectangle.
 *
 * The backend rejected two overlapping-but-separate shapes for this: a real
 * union, not a group, so what the user sees IS the one solid piece that gets
 * cut, not two shapes that happen to overlap on screen.
 *
 * Built pointing "down" (the rect below the circle) and then rotated into the
 * requested direction, using the standard "circle union rounded silhouette"
 * construction: the two points where the rectangle's sides meet the circle,
 * an arc the long way around the far side of the circle between them, and the
 * rectangle's remaining three edges.
 */
function buildKeyholePathD(headDiameterMm, shankDiameterMm, slotLengthMm, direction = 'd') {
    const headRadius = headDiameterMm / 2;
    // Keep the rectangle narrower than the circle -- past that it isn't a
    // keyhole silhouette, it's just two overlapping shapes with a lump.
    const shankWidth = Math.min(Math.max(shankDiameterMm, 0.1), headDiameterMm * 0.9);
    const meetY = Math.sqrt(Math.max(headRadius * headRadius - (shankWidth / 2) ** 2, 0));
    // Purely cosmetic: how far the tail visibly pokes out past the head
    // circle. The real cut length is whatever ends up encoded in the id.
    const tail = Math.max(slotLengthMm - headRadius, headRadius * 0.5);
    const rectEndY = meetY + tail;

    const angle = ((KEYHOLE_DIRECTION_ANGLE_DEG[direction] ?? 0) * Math.PI) / 180;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const rotate = (x, y) => [x * cos - y * sin, x * sin + y * cos];
    const toPx = (mm) => (mm * PX_PER_MM).toFixed(3);

    const [x1, y1] = rotate(shankWidth / 2, meetY);
    const [x2, y2] = rotate(-shankWidth / 2, meetY);
    const [x3, y3] = rotate(-shankWidth / 2, rectEndY);
    const [x4, y4] = rotate(shankWidth / 2, rectEndY);
    const radiusPx = toPx(headRadius);

    // large-arc=1 sweep=0: the long way round, away from the tail. sweep=1
    // takes the short way and eats the circle instead of enclosing it —
    // verified by measuring the resulting area against pi*r^2.
    return `M${toPx(x1)} ${toPx(y1)} `
        + `A${radiusPx} ${radiusPx} 0 1 0 ${toPx(x2)} ${toPx(y2)} `
        + `L${toPx(x3)} ${toPx(y3)} `
        + `L${toPx(x4)} ${toPx(y4)} Z`;
}

/** id -> {headDiameterMm, shankDiameterMm, slotLengthMm, direction}, or null. */
function parseKeyholeId(id) {
    const match = KEYHOLE_ID_RE.exec(String(id || ''));
    if (!match) return null;
    const [, index, head, shank, slot, direction] = match;
    return {
        index,
        headDiameterMm: Number(head) / 10,
        shankDiameterMm: Number(shank) / 10,
        slotLengthMm: Number(slot) / 10,
        direction,
    };
}

function buildKeyholeId(index, headDiameterMm, shankDiameterMm, slotLengthMm, direction) {
    const tenths = (mm) => Math.max(1, Math.round(mm * 10));
    return `keyhole_${index}_h${tenths(headDiameterMm)}_s${tenths(shankDiameterMm)}`
        + `_l${tenths(slotLengthMm)}_d${direction}`;
}

/**
 * El donut del avellanado: circunferencia exterior (cabeza) y la interior
 * (rosca) como agujero.
 *
 * Un único `<path>` con dos subtrazados de sentido opuesto, no dos círculos
 * superpuestos: así el hueco central es un agujero de verdad — se ve el fondo
 * a través de él — y el backend recibe una sola figura con su id, que es lo
 * único que sobrevive al viaje por Inkscape.
 */
function buildScrewPathD(headDiameterMm, shankDiameterMm) {
    // La rosca siempre más estrecha que la cabeza: si no, no hay cono donde
    // apoyar y el donut se vería como un círculo suelto.
    const headRadius = Math.max(headDiameterMm, 0.2) / 2;
    const shankRadius = Math.min(Math.max(shankDiameterMm, 0.1), headDiameterMm * 0.9) / 2;
    const toPx = (mm) => (mm * PX_PER_MM).toFixed(3);

    // sweep opuesto en cada anillo: con `nonzero` el interior se resta, y con
    // `evenodd` sale igual, así que el agujero no depende de la regla de
    // relleno con la que lo lea quien lo abra.
    const ring = (radius, sweep) => {
        const r = toPx(radius);
        return `M${toPx(-radius)} 0 A${r} ${r} 0 1 ${sweep} ${toPx(radius)} 0 `
            + `A${r} ${r} 0 1 ${sweep} ${toPx(-radius)} 0 Z`;
    };

    return `${ring(headRadius, 1)} ${ring(shankRadius, 0)}`;
}

/** id -> cotas del pin de Crocs, o null. */
function parseCrocsId(id) {
    const match = CROCS_ID_RE.exec(String(id || ''));
    if (!match) return null;
    const [, index, head, neck, length, headThickness, mode] = match;
    return {
        index,
        headDiameterMm: Number(head) / 10,
        neckDiameterMm: Number(neck) / 10,
        neckLengthMm: Number(length) / 10,
        headThicknessMm: Number(headThickness) / 10,
        mode: mode.toLowerCase(),
    };
}

function buildCrocsId(index, { headDiameterMm, neckDiameterMm, neckLengthMm, headThicknessMm, mode }) {
    const tenths = (mm) => Math.max(1, Math.round(mm * 10));
    return `crocs_${index}_h${tenths(headDiameterMm)}_n${tenths(neckDiameterMm)}`
        + `_l${tenths(neckLengthMm)}_t${tenths(headThicknessMm)}_m${mode === 'a' ? 'a' : 'i'}`;
}

/** id -> {headDiameterMm, shankDiameterMm}, or null. */
function parseScrewId(id) {
    const match = SCREW_ID_RE.exec(String(id || ''));
    if (!match) return null;
    const [, index, head, shank] = match;
    return {
        index,
        headDiameterMm: Number(head) / 10,
        shankDiameterMm: Number(shank) / 10,
    };
}

function buildScrewId(index, headDiameterMm, shankDiameterMm) {
    const tenths = (mm) => Math.max(1, Math.round(mm * 10));
    return `screw_${index}_h${tenths(headDiameterMm)}_s${tenths(shankDiameterMm)}`;
}

// ── Vaciado / bajo relieve ─────────────────────────────────────────────────
// El color reservado para vaciado en el SVG exportado al backend.
// Las formas con este fill se tallan en el fondo (boolean difference) pero
// no generan objeto de relleno de color en Blender.
const VACIADO_FILL_COLOR = '#000000';
const HOLE_PREVIEW_FILL = '#ef4444';
// Azulado, para no confundirlo de un vistazo con el gris de la ranura de
// colgado: son dos sistemas de montaje distintos y conviven en el mismo panel.
const SCREW_FILL = '#8ba3c7';
const CROCS_FILL = '#5fae6b';
// Perfil de base del servilletero de paredes: la franja de la pared que entra
// en la base. A diferencia de la ranura o el tornillo se escala con los
// tiradores: el backend lee su caja, no unas cotas en el id.
const NAPKIN_JOINT_FILL = '#f59e0b';
const NAPKIN_JOINT_PREFIX = 'napkinjoint_';
const isNapkinJointObject = (object) => String(object?.id ?? '').startsWith(NAPKIN_JOINT_PREFIX);
const INTERNAL_ELEMENT_COLORS = {
    magnet: '#64748b', nfc: '#06b6d4', silbato: '#9c27b0',
    abridor_arandela: '#1f2937', abridor_rebaje: '#ef4444',
};

// El rebaje se pinta en dos tramos para que se distingan al tocar sus cotas:
// el hondo, macizo y oscuro, pegado a la arandela; la rampa, en degradado que
// se aclara hacia el extremo donde sale a la superficie. Es sólo pintura: el
// backend ignora el relleno de los elementos internos y lee las cotas del id.
const ABRIDOR_POCKET_COLORS = {
    flat: '#b91c1c',
    rampDeep: 'rgba(239, 68, 68, 0.8)',
    rampSurface: 'rgba(239, 68, 68, 0.1)',
};

// Color de una profundidad del rebaje redondo: blanco en la superficie y rojo
// cada vez más oscuro hacia el fondo. El panel pinta su perfil con lo mismo.
function abridorDepthColor(depthMm, maxDepthMm) {
    const t = maxDepthMm > 0 ? Math.min(Math.max(depthMm / maxDepthMm, 0), 1) : 0;
    const stops = [[255, 255, 255], [239, 68, 68], [127, 29, 29]];
    const [from, to, local] = t < 0.5 ? [stops[0], stops[1], t / 0.5] : [stops[1], stops[2], (t - 0.5) / 0.5];
    const mix = from.map((value, i) => Math.round(value + (to[i] - value) * local));
    return `rgb(${mix.join(', ')})`;
}

function abridorPocketFill(params) {
    if (params.pocketShape === 'redondo') {
        const total = params.bodyLengthMm + params.capDiameterMm / 2;
        // Con curva se pinta una parada cada milímetro más o menos: suficiente
        // para que el degradado siga la forma sin cargar cientos de paradas.
        const surface = params.profileSmooth
            ? sampleAbridorProfile(params.profile, true, 1)
            : params.profile;
        const maxDepth = Math.max(...surface.map(point => point.depthMm));
        const color = depthMm => abridorDepthColor(depthMm, maxDepth);
        const first = surface[0];
        const last = surface[surface.length - 1];
        // El degradado va de arriba (extremo lejano, y = 0) hacia la arandela.
        const offsetOf = s => (params.bodyLengthMm - s) / total;
        return new fabric.Gradient({
            type: 'linear',
            gradientUnits: 'percentage',
            coords: { x1: 0, y1: 0, x2: 0, y2: 1 },
            colorStops: [
                { offset: 0, color: color(last.depthMm) },
                ...[...surface].reverse().map(point => ({ offset: offsetOf(point.s), color: color(point.depthMm) })),
                { offset: offsetOf(0), color: color(first.depthMm) },
                { offset: 1, color: color(first.depthMm) },
            ],
        });
    }
    const length = params.flatMm + params.rampMm;
    const rampEnd = length > 0 ? params.rampMm / length : 0;
    return new fabric.Gradient({
        type: 'linear',
        gradientUnits: 'percentage',
        // La rampa está en el borde de arriba del rebaje (y = 0), lejos de la arandela.
        coords: { x1: 0, y1: 0, x2: 0, y2: 1 },
        colorStops: [
            { offset: 0, color: ABRIDOR_POCKET_COLORS.rampSurface },
            { offset: rampEnd, color: ABRIDOR_POCKET_COLORS.rampDeep },
            { offset: rampEnd, color: ABRIDOR_POCKET_COLORS.flat },
            { offset: 1, color: ABRIDOR_POCKET_COLORS.flat },
        ],
    });
}

// ── Abrelatas ──────────────────────────────────────────────────────────────
// Para levantar la anilla de una lata sin uña. Una sola pieza: un rectángulo
// (la huella) que se coloca rebasando el borde del llavero. Diseño completo en
// docs/technical/abrelatas.md.
//
// insert_<n>_abrelatas_cuna_e<filo>_l<rampa>
// insert_<n>_abrelatas_ranura_e<labio>_h<boca>_f<fondo>_p<hondo>
// (décimas de mm). El backend mide todo desde el canto que pisa la huella y saca
// la dirección de lo que sobra fuera, así que el id no lleva ni el ancho ni
// hacia dónde mira: sirve con el grupo girado.
const ABRELATAS_ID_RE = /^insert_(\d+)_abrelatas_(?:cuna_e\d+_l\d+|ranura_e\d+_h\d+_f\d+_p\d+)$/i;

// - cuna: la placa se afina por detrás en rampa hasta dejar `edgeMm` en el canto.
// - ranura: ciega por las dos caras, abierta solo por el canto. El labio de
//   delante mide `edgeMm` en el canto y engorda en rampa hacia dentro, así que
//   la ranura pasa de `slotMm` en la boca a `slotEndMm` a `reachMm` del canto.
//   El techo es plano.
const ABRELATAS_SHAPES = ['cuna', 'ranura'];

// Estimaciones hasta medir latas de 33 cl; mismos valores que el backend.
const ABRELATAS_DEFAULTS = {
    openerShape: 'cuna',
    edgeMm: 1,
    rampMm: 15,
    slotMm: 2,
    // Algo más que la chapa de la anilla (~0,3 mm), para que entre hasta el fondo.
    slotEndMm: 0.6,
    reachMm: 8,
    widthMm: 14,
};

const ABRELATAS_LIMITS = {
    edgeMm: [0.6, 2],
    rampMm: [5, 30],
    slotMm: [0.8, 3],
    slotEndMm: [0, 3],
    reachMm: [2, 15],
    widthMm: [8, 40],
};

// Por debajo el backend avisa: el filo se rompe o se dobla, el techo cuelga.
const ABRELATAS_MIN_EDGE_MM = 0.8;
const ABRELATAS_MIN_ROOF_MM = 1.6;
// Lo que la huella se dibuja de más para que quede fuera del llavero. El
// backend no lo usa: sólo necesita que algo sobresalga para encontrar el canto.
const ABRELATAS_OVERSHOOT_MM = 4;
const ABRELATAS_OVERSHOOT_COLOR = 'rgba(148, 163, 184, 0.45)';

function normalizeAbrelatasParams(params = {}) {
    const next = { ...ABRELATAS_DEFAULTS };
    Object.keys(ABRELATAS_LIMITS).forEach((key) => {
        if (params[key] === undefined) return;
        const number = Number(params[key]);
        if (!Number.isFinite(number)) return;
        const [min, max] = ABRELATAS_LIMITS[key];
        // A décimas, que es lo que cabe en el id.
        next[key] = Math.round(Math.min(Math.max(number, min), max) * 10) / 10;
    });
    if (ABRELATAS_SHAPES.includes(params.openerShape)) next.openerShape = params.openerShape;
    // La ranura se estrecha hacia dentro: el fondo nunca más alto que la boca.
    next.slotEndMm = Math.min(next.slotEndMm, next.slotMm);
    return next;
}

/**
 * Huella del abrelatas en mm, con la caja empezando en (0,0).
 *
 * El extremo que tiene que quedar fuera del llavero (el sobrante) va arriba,
 * en y = 0; debajo, lo que pide la forma: la rampa de la cuña o lo que entra
 * la ranura.
 */
function buildAbrelatasGeometry(rawParams) {
    const p = normalizeAbrelatasParams(rawParams);
    const workingMm = p.openerShape === 'cuna' ? p.rampMm : p.reachMm;
    const lengthMm = workingMm + ABRELATAS_OVERSHOOT_MM;
    return {
        params: p,
        widthMm: p.widthMm,
        heightMm: lengthMm,
        overshootMm: ABRELATAS_OVERSHOOT_MM,
        contour: [[0, 0], [p.widthMm, 0], [p.widthMm, lengthMm], [0, lengthMm]],
    };
}

/**
 * Degradado de la huella, con el mismo criterio de color que el rebaje
 * redondo del abrebotellas: blanco en la superficie, rojo oscuro en lo hondo.
 * El sobrante, en gris. La ranura no se abre a ninguna cara: se pinta más
 * clara, y se aclara hacia dentro según se estrecha.
 */
function abrelatasFill(params) {
    const { heightMm, overshootMm } = buildAbrelatasGeometry(params);
    const at = mm => mm / heightMm;
    const color = t => abridorDepthColor(t, 1);
    const overshoot = [
        { offset: 0, color: ABRELATAS_OVERSHOOT_COLOR },
        { offset: at(overshootMm), color: ABRELATAS_OVERSHOOT_COLOR },
    ];
    const working = params.openerShape === 'ranura'
        ? [
            { offset: at(overshootMm), color: color(0.6) },
            { offset: 1, color: color(0.2) },
        ]
        : [
            // Tres paradas porque el color pasa por el rojo a media rampa.
            { offset: at(overshootMm), color: color(1) },
            { offset: at(overshootMm + params.rampMm / 2), color: color(0.5) },
            { offset: 1, color: color(0) },
        ];
    return new fabric.Gradient({
        type: 'linear',
        gradientUnits: 'percentage',
        coords: { x1: 0, y1: 0, x2: 0, y2: 1 },
        colorStops: [...overshoot, ...working],
    });
}

const serializedEditorProperties = [
    "id", "selectable", "hoverCursor", "class", "name", "_vaciadoFill",
    "_geometryRole", "_operationOriginalId", "_operationOriginalClass",
    "_excludeFromRender",
    // Relieve propio de un dibujo, en mm: cuánto entra en el fondo y cuánto
    // sobresale por encima (ver el bloque "Relieve por dibujo" más abajo). Sólo
    // se escriben en el `id` al exportar; aquí viajan como números para que el
    // gestor de capas los lea sin parsear y para que sobrevivan a guardar,
    // recargar y deshacer/rehacer.
    "_drawingDepthMm", "_drawingRaiseMm",
    // En qué cara del llavero va el dibujo. Ausente = la de delante, que es lo
    // que son todos los diseños anteriores a esto.
    "_drawingSide",
    // De qué pieza 3D es el objeto (C3D-22). Viaja al backend como `__obj<uid>`
    // en el id y vuelve en el nombre de la malla, para que el panel de colores
    // 3D pueda repintar un texto sin tocar otro del mismo color. Tiene que
    // sobrevivir a guardar y recargar: si cambiara, la malla ya generada no
    // encontraría su objeto.
    "_partUid",
    // El silbato tiene demasiadas cotas para caber en el id, y el id sólo lleva
    // lo que necesita el backend. Los parámetros de XY viajan aquí para que el
    // panel pueda reabrir un diseño guardado con sus valores.
    "_silbatoParams",
    // Qué silbato es cada pieza. No basta con el grupo de fabric: guardar llama
    // a `saveSVG(true)`, que hace `toActiveSelection()` y deshace los grupos, y
    // sin esta marca el silbato volvía del guardado como cinco elementos
    // sueltos. Con ella se puede reconstruir a partir de las piezas.
    "_silbatoId",
    // Lo mismo para el abrebotellas: arandela y rebaje forman un conjunto.
    "_abridorParams",
    "_abridorId",
    // Y para el abrelatas: el id sólo lleva lo que necesita el backend, aquí
    // va también el ancho y la forma para reabrirlo con sus valores.
    "_abrelatasParams",
    "_abrelatasId",
];

const SILBATO_GROUP_ID = (index) => `silbato_${index}`;
const ABRIDOR_GROUP_ID = (index) => `abridor_${index}`;
const ABRELATAS_GROUP_ID = (index) => `abrelatas_${index}`;

// Elementos internos hechos de varias piezas que se mueven juntas. El grupo
// lleva la marca en `id` (`silbato_<n>`); las piezas, en `idProp`, para poder
// rehacer el grupo cuando el guardado lo deshace.
const ASSEMBLY_KINDS = {
    silbato: { idProp: '_silbatoId', paramsProp: '_silbatoParams', label: 'Silbato', normalize: normalizeSilbatoParams },
    abridor: { idProp: '_abridorId', paramsProp: '_abridorParams', label: 'Abrebotellas', normalize: normalizeAbridorParams },
    // Una sola pieza, pero en grupo como los otros: así se gira, se edita y se
    // rehace al recargar con la misma maquinaria.
    abrelatas: { idProp: '_abrelatasId', paramsProp: '_abrelatasParams', label: 'Abrelatas', normalize: normalizeAbrelatasParams },
};
const assemblyKindOf = (assemblyId) => Object.keys(ASSEMBLY_KINDS)
    .find(kind => String(assemblyId || '').startsWith(`${kind}_`)) || null;
const assemblyIdOf = (object) => {
    for (const { idProp } of Object.values(ASSEMBLY_KINDS)) {
        if (object?.[idProp]) return object[idProp];
    }
    return assemblyKindOf(object?.id) ? object.id : null;
};
const assemblyIndexOf = (assemblyId) => Number(String(assemblyId).split('_').pop());
// Los parámetros pueden estar en el grupo o, si se deshizo, en cualquier pieza.
const assemblyParamsOf = (objects, kind) => {
    const { paramsProp } = ASSEMBLY_KINDS[kind];
    for (const object of objects) {
        if (object?.[paramsProp]) return object[paramsProp];
        const child = object?._objects?.find(piece => piece[paramsProp]);
        if (child) return child[paramsProp];
    }
    return undefined;
};

function createDiagonalPattern(background, stroke) {
    const size = 12;
    const src = document.createElement('canvas');
    src.width = size;
    src.height = size;
    const ctx = src.getContext('2d');
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, size, size);
    ctx.strokeStyle = stroke;
    ctx.lineWidth = 3;
    [[-size, 0], [0, 0], [size, 0]].forEach(([dx]) => {
        ctx.beginPath();
        ctx.moveTo(dx, size);
        ctx.lineTo(dx + size, 0);
        ctx.stroke();
    });
    return new fabric.Pattern({ source: src, repeat: 'repeat' });
}

// Negro imprimible por defecto. No puede ser '#000000': ese valor es justo
// VACIADO_FILL_COLOR, así que una forma negra "normal" se interpretaría como
// vaciado/bajo relieve en el backend. Mismo criterio que 'Black' en la paleta.
const PRINTABLE_BLACK_FILL_COLOR = FILL;

/**
 * Crea un fabric.Pattern con rayas diagonales blancas y grises oscuras.
 * Se usa solo para la representación visual en el canvas; el SVG exportado
 * siempre tendrá fill:#000000 para que el backend lo identifique como vaciado.
 */
function createVaciadoPattern() {
    return createDiagonalPattern('#e8e8e8', '#444444');
}

const createHolePattern = () => createDiagonalPattern('#fee2e2', '#dc2626');

function clearHoleOperation(object) {
    if (object?._geometryRole !== 'hole') return;
    object.id = object._operationOriginalId || undefined;
    object.class = object._operationOriginalClass || undefined;
    delete object._geometryRole;
    delete object._operationOriginalId;
    delete object._operationOriginalClass;
}

/**
 * Recorre recursivamente los objetos de un grupo y aplica vaciado.
 * Guarda el fill original en `_vaciadoFill` para restaurarlo en el export.
 */
function applyVaciadoToObject(object) {
    if (object._objects) {
        object._objects.forEach(applyVaciadoToObject);
    } else {
        if (object.id && String(object?.id ?? '').startsWith('hole') && object._geometryRole !== 'hole') return;
        clearHoleOperation(object);
        object._vaciadoFill = VACIADO_FILL_COLOR;
        object.set({ fill: createVaciadoPattern() });
    }
}

/**
 * Recorre recursivamente y elimina el vaciado, aplicando el color indicado.
 */
function removeVaciadoFromObject(object, color) {
    if (object._objects) {
        object._objects.forEach(o => removeVaciadoFromObject(o, color));
    } else {
        if (object.id && String(object?.id ?? '').startsWith('hole') && object._geometryRole !== 'hole') return;
        clearHoleOperation(object);
        delete object._vaciadoFill;
        object.set({ fill: color });
    }
}

/**
 * Devuelve pares {object, pattern} para todos los objetos con vaciado activo.
 * Útil para intercambiar temporalmente el fill antes de exportar SVG/JSON.
 */
function collectVaciadoObjects(canvas) {
    const list = [];
    const recurse = (obj) => {
        if (obj._objects) {
            obj._objects.forEach(recurse);
        } else if (obj._vaciadoFill) {
            list.push({ obj, pattern: obj.fill });
        }
    };
    canvas.getObjects().forEach(recurse);
    return list;
}

/**
 * Después de loadFromJSON restaura el patrón de vaciado en los objetos
 * que tienen la propiedad `_vaciadoFill` serializada.
 */
function restoreVaciadoPatterns(canvas) {
    const recurse = (obj) => {
        if (obj._objects) {
            obj._objects.forEach(recurse);
        } else if (obj._vaciadoFill === VACIADO_FILL_COLOR) {
            obj.set({ fill: createVaciadoPattern() });
        }
    };
    canvas.getObjects().forEach(recurse);
}

function restoreOperationPatterns(canvas) {
    const recurse = (obj) => {
        if (obj._objects) obj._objects.forEach(recurse);
        if (obj._geometryRole === 'hole') obj.set({ fill: createHolePattern() });
    };
    canvas.getObjects().forEach(recurse);
}

function collectOperationPreviewObjects(canvas) {
    const list = [];
    const recurse = (obj) => {
        if (obj._objects) obj._objects.forEach(recurse);
        if (obj._geometryRole === 'hole') list.push({ obj, pattern: obj.fill, fill: HOLE_PREVIEW_FILL });
    };
    canvas.getObjects().forEach(recurse);
    return list;
}

/**
 * ¿La forma es redonda? Para poner su diámetro en vez de ancho × alto.
 *
 * Los círculos del editor son `path` (ver `addCircle`), así que no basta con
 * mirar el tipo: en un trazado se mira que todos sus puntos queden a la misma
 * distancia del centro. Un cuadrado tiene las esquinas un 41 % más lejos.
 */
function isRoundShape(object, widthMm, heightMm) {
    if (Math.abs(widthMm - heightMm) > Math.max(widthMm, heightMm) * 0.02) return false;
    if (object.type === 'circle' || object.type === 'ellipse') return true;
    if (object.type === 'rect') {
        return (object.rx || 0) >= (object.width / 2) * 0.98 && (object.ry || 0) >= (object.height / 2) * 0.98;
    }
    if (object.type === 'path' && Array.isArray(object.path)) {
        const { x: cx, y: cy } = object.pathOffset || { x: 0, y: 0 };
        const radii = object.path
            .filter(command => command.length >= 3)
            .map(command => Math.hypot(command[command.length - 2] - cx, command[command.length - 1] - cy));
        if (radii.length < 4) return false;
        const mean = radii.reduce((total, radius) => total + radius, 0) / radii.length;
        return mean > 0 && radii.every(radius => Math.abs(radius - mean) <= mean * 0.03);
    }
    return false;
}

const formatHoleMm = (mm) => `${parseFloat(mm.toFixed(1))}`;

// Alto de la etiqueta en pantalla y el hueco que deja bajo la forma cuando
// no cabe dentro: debajo y no encima, porque encima está el tirador de girar,
// y con hueco para no chocar con el tirador del centro de abajo. El ancho
// estimado vale para decidir sin medir el texto.
const HOLE_LABEL_HEIGHT_PX = 18;
const HOLE_LABEL_GAP_PX = 14;
const HOLE_LABEL_CHAR_PX = 7;

/**
 * Texto y posición en pantalla de la medida de cada agujero pasante visible.
 * La medida es la de la propia forma (con su escala y la de sus grupos), no la
 * de su caja, para que un agujero girado no cambie de tamaño.
 *
 * Con `selected`, solo los agujeros que estén en él o dentro de un grupo que
 * lo esté. Si la etiqueta no cabe dentro de la forma en pantalla, va debajo.
 */
function holeMeasureLabels(canvas, selected = null) {
    const labels = [];
    const vpt = canvas.viewportTransform;
    const visit = (object, parentVisible, parentSelected) => {
        const visible = parentVisible && object.visible !== false && !object._excludeFromRender;
        if (!visible) return;
        const isSelected = parentSelected || !selected || selected.has(object);
        if (object._objects?.length) {
            object._objects.forEach(child => visit(child, visible, isSelected));
            return;
        }
        if (object._geometryRole !== 'hole' || !isSelected) return;
        const matrix = object.calcTransformMatrix();
        const { scaleX, scaleY } = fabric.util.qrDecompose(matrix);
        const widthMm = (object.width * Math.abs(scaleX)) / PX_PER_MM;
        const heightMm = (object.height * Math.abs(scaleY)) / PX_PER_MM;
        if (!(widthMm > 0) || !(heightMm > 0)) return;
        const text = isRoundShape(object, widthMm, heightMm)
            ? `Ø ${formatHoleMm((widthMm + heightMm) / 2)}mm`
            : `${formatHoleMm(widthMm)} × ${formatHoleMm(heightMm)}mm`;
        const center = fabric.util.transformPoint(new fabric.Point(matrix[4], matrix[5]), vpt);
        const corners = object.getCoords(true, true).map(point => fabric.util.transformPoint(point, vpt));
        const xs = corners.map(point => point.x);
        const ys = corners.map(point => point.y);
        const fits = Math.max(...xs) - Math.min(...xs) >= text.length * HOLE_LABEL_CHAR_PX + 12
            && Math.max(...ys) - Math.min(...ys) >= HOLE_LABEL_HEIGHT_PX;
        labels.push({
            text,
            x: center.x,
            y: fits ? center.y : Math.max(...ys) + HOLE_LABEL_GAP_PX + HOLE_LABEL_HEIGHT_PX / 2,
            inside: fits,
        });
    };
    canvas.getObjects().forEach(object => visit(object, true, false));
    return labels;
}

function parseSilbatoId(id) {
    const match = SILBATO_ID_RE.exec(String(id || ''));
    if (!match) return null;
    const [, index, piece, duct, floor] = match;
    return {
        index: Number(index),
        kind: 'silbato',
        piece: piece.toLowerCase(),
        ductMm: Number(duct) / 10,
        floorMm: Number(floor) / 10,
    };
}

function parseAbridorId(id) {
    const match = ABRIDOR_ID_RE.exec(String(id || ''));
    if (!match) return null;
    return {
        index: Number(match[1]),
        kind: 'abridor',
        piece: match[2] !== undefined ? 'arandela' : 'rebaje',
    };
}

function parseAbrelatasId(id) {
    const match = ABRELATAS_ID_RE.exec(String(id || ''));
    if (!match) return null;
    return { index: Number(match[1]), kind: 'abrelatas', piece: 'huella' };
}

function parseInternalElementId(id) {
    const silbato = parseSilbatoId(id) || parseAbridorId(id) || parseAbrelatasId(id);
    if (silbato) return silbato;
    const match = INTERNAL_ELEMENT_ID_RE.exec(String(id || ''));
    if (!match) return null;
    const [, index, kind, shape, size, height, thickness, bottom] = match;
    return {
        index: Number(index), kind, shape,
        sizeMm: Number(size) / 10,
        // `s` es el ancho (o el diámetro); sin `_h` la pieza es cuadrada.
        heightMm: Number(height ?? size) / 10,
        thicknessMm: Number(thickness) / 10,
        bottomOffsetMm: Number(bottom) / 10,
    };
}

const idTenths = (value, minimum = 0) => Math.max(minimum, Math.round(Number(value) * 10));

function buildInternalElementId(index, kind, shape, sizeMm, thicknessMm, bottomOffsetMm, heightMm = sizeMm) {
    const rectHeight = shape === 'square' && idTenths(heightMm, 1) !== idTenths(sizeMm, 1)
        ? `_h${idTenths(heightMm, 1)}` : '';
    return `insert_${index}_${kind}_${shape}_s${idTenths(sizeMm, 1)}${rectHeight}`
        + `_t${idTenths(thicknessMm, 1)}_z${idTenths(bottomOffsetMm)}`;
}

// Todas las piezas comparten la misma banda en Z, igual que un imán: se vacía
// un trozo del medio y las lonchas de arriba y de abajo quedan intactas.
function buildSilbatoPieceId(index, piece, { ductMm, floorMm }) {
    return `insert_${index}_silbato_${piece}_t${idTenths(ductMm, 1)}_z${idTenths(floorMm)}`;
}

function buildAbridorPieceIds(index, params, washer) {
    return {
        arandela: `insert_${index}_abridor_arandela_o${idTenths(washer.outerMm, 1)}`
            + `_i${idTenths(washer.innerMm, 1)}_t${idTenths(washer.thicknessMm, 1)}_z${idTenths(params.washerZMm)}`,
        rebaje: params.pocketShape === 'redondo'
            ? `insert_${index}_abridor_rebaje_${params.profileSmooth ? 'c' : 'p'}${params.profile
                .map(point => `${idTenths(point.s)}x${idTenths(point.depthMm)}`).join('_')}`
            : `insert_${index}_abridor_rebaje_d${idTenths(params.depthMm, 1)}_r${idTenths(params.rampMm)}`,
    };
}

function buildAbrelatasId(index, params) {
    return params.openerShape === 'ranura'
        ? `insert_${index}_abrelatas_ranura_e${idTenths(params.edgeMm, 1)}_h${idTenths(params.slotMm, 1)}`
            + `_f${idTenths(params.slotEndMm)}_p${idTenths(params.reachMm, 1)}`
        : `insert_${index}_abrelatas_cuna_e${idTenths(params.edgeMm, 1)}_l${idTenths(params.rampMm, 1)}`;
}

const normalizeHexColor = (color) => {
    if (typeof color !== 'string') return null;
    const value = color.trim().toLowerCase();
    if (/^#[0-9a-f]{6}$/.test(value)) return value;
    if (/^#[0-9a-f]{3}$/.test(value)) {
        return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`;
    }
    return null;
};

const getObjectFillColor = (object) => {
    if (!object) return null;
    if (object._objects) {
        for (const child of object._objects) {
            const childColor = getObjectFillColor(child);
            if (childColor) return childColor;
        }
    }
    return normalizeHexColor(object._vaciadoFill || object.fill);
};

const applyFillColor = (object, color) => {
    if (!object || !color) return;
    if (object._objects) {
        object._objects.forEach(child => applyFillColor(child, color));
        return;
    }
    delete object._vaciadoFill;
    clearHoleOperation(object);
    object.set({ fill: color });
};

// Oscuro = contrasta más con blanco que con negro (luminancia relativa por
// debajo de 0,179, el punto de cruce de WCAG): lo que se ponga encima,
// claro.
const isDarkColor = (hex) => {
    const match = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
    if (!match) return false;
    const [r, g, b] = [0, 2, 4].map(i => parseInt(match[1].slice(i, i + 2), 16) / 255)
        .map(c => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
    return 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.179;
};

const objectHasDifferentFill = (object, color) => {
    if (!object) return false;
    if (object._objects) {
        return object._objects.some(child => objectHasDifferentFill(child, color));
    }
    return normalizeHexColor(object._vaciadoFill || object.fill) !== normalizeHexColor(color);
};
// ── Fin Vaciado ────────────────────────────────────────────────────────────

// Compartidas entre `buildEditor` y los helpers de modulo (p. ej.
// `centerDrawingsOnCanvas`): solo dependen del objeto, no del editor.
const isTextObject = (object) => object?.type === TEXT.type || object?.type === 'i-text' || object?.type === 'text';

// que su bounding box vertical no es la tinta real de las letras — un
// texto en mayúsculas "flota" hacia arriba dentro de su propia caja.
// Se renderiza el objeto (con su transform actual) a un canvas recortado
// a `getBoundingRect(true, true)` — el mismo cálculo que usa fabric por
// dentro para `toCanvasElement` — y se mide dónde empieza y acaba la
// tinta real, fila a fila.
// `horizontal`: mide tambien izquierda y derecha por columnas. Alinear y
// distribuir piden solo el vertical a proposito — su queja era el alto de
// linea — y con el horizontal cambiarian de comportamiento. Lo pide el
// centrado del Offset, donde el fondo viene del contorno de las letras y
// cualquier espaciado lateral que sobre en la caja se convierte en desfase.
const getTextInkBounds = (object, { horizontal = false } = {}) => {
    try {
        const boundsRect = object.getBoundingRect(true, true);
        if (!boundsRect.width || !boundsRect.height) return null;

        const rendered = object.toCanvasElement({ enableRetinaScaling: false });
        const { width, height } = rendered;
        if (!width || !height) return null;
        const ctx = rendered.getContext('2d');
        const { data } = ctx.getImageData(0, 0, width, height);

        const hasInkAtRow = (y) => {
            const rowStart = y * width * 4;
            for (let x = 0; x < width; x++) {
                if (data[rowStart + x * 4 + 3] > 10) return true;
            }
            return false;
        };
        const hasInkAtColumn = (x) => {
            for (let y = 0; y < height; y++) {
                if (data[(y * width + x) * 4 + 3] > 10) return true;
            }
            return false;
        };

        let inkTop = null;
        for (let y = 0; y < height; y++) {
            if (hasInkAtRow(y)) { inkTop = y; break; }
        }
        if (inkTop === null) return null; // texto vacío o invisible

        let inkBottom = height - 1;
        for (let y = height - 1; y >= inkTop; y--) {
            if (hasInkAtRow(y)) { inkBottom = y; break; }
        }

        const scaleY = boundsRect.height / height;
        const bounds = {
            left: boundsRect.left,
            top: boundsRect.top + inkTop * scaleY,
            right: boundsRect.left + boundsRect.width,
            bottom: boundsRect.top + (inkBottom + 1) * scaleY,
        };
        if (!horizontal) return bounds;

        let inkLeft = null;
        for (let x = 0; x < width; x++) {
            if (hasInkAtColumn(x)) { inkLeft = x; break; }
        }
        if (inkLeft === null) return bounds;

        let inkRight = width - 1;
        for (let x = width - 1; x >= inkLeft; x--) {
            if (hasInkAtColumn(x)) { inkRight = x; break; }
        }

        const scaleX = boundsRect.width / width;
        return {
            ...bounds,
            left: boundsRect.left + inkLeft * scaleX,
            right: boundsRect.left + (inkRight + 1) * scaleX,
        };
    } catch {
        return null; // cualquier fallo de render cae al bounding box normal
    }
};


const buildEditor = (canvas, clipboard, _setClipboard, fillColor, strokeColor, _setFillColor, _setStrokeColor, scaleStep, window, onHistoryChange) => {

    let selectedNestedObjects = new Set();
    let selectingNestedObject = false;
    let clipboardObject = clipboard || null;
    let clipboardPasteCount = 0;

    const clearNestedSelection = () => {
        if (selectingNestedObject || selectedNestedObjects.size === 0) return;
        selectedNestedObjects = new Set();
        canvas.fire('layer:nested-selection');
    };

    canvas.on('selection:created', clearNestedSelection);
    canvas.on('selection:updated', clearNestedSelection);
    canvas.on('selection:cleared', clearNestedSelection);

    if (window.innerWidth < 768) {
        canvas.enablePointerEvent = true;
    } else {
        canvas.enablePointerEvent = false;
    }

    /**
     * Run a canvas mutation that is ours, not the user's.
     *
     * Removing the active object fires `selection:cleared` synchronously, and
     * Customizer reads that as "the user deselected" and closes the right-hand
     * panel. When we remove a marker only to immediately put an edited one
     * back, that panel is exactly where the user is typing. The flag lets the
     * handler tell the two apart (same idea as `__customiz3rMultitouch`).
     *
     * Synchronous by design so the flag cannot leak: `selection:cleared` is
     * emitted inside the call, and `finally` clears it even if that throws.
     */
    const asInternalEdit = (mutate) => {
        canvas.__customiz3rInternalEdit = true;
        try {
            return mutate();
        } finally {
            canvas.__customiz3rInternalEdit = false;
        }
    };

    const cloneFabricObject = (object) => (
        new Promise((resolve) => object.clone(resolve, serializedEditorProperties))
    );

    const offsetClonedObject = (object, offset) => {
        object.set({
            left: (object.left || 0) + offset,
            top: (object.top || 0) + offset,
        });
    };

    /**
     * Build a keyhole marker as one united path and hand it to `place`.
     *
     * Async because Fabric parses SVG asynchronously; callers do their own
     * positioning in the callback since "centre it" and "restore its previous
     * centre" differ between adding and resizing.
     */
    const placeKeyholeShape = (index, headDiameterMm, shankDiameterMm, slotLengthMm, direction, place) => {
        const id = buildKeyholeId(index, headDiameterMm, shankDiameterMm, slotLengthMm, direction);
        const pathD = buildKeyholePathD(headDiameterMm, shankDiameterMm, slotLengthMm, direction);
        const svg = `<svg xmlns="http://www.w3.org/2000/svg">`
            + `<path id="${id}" class="keyhole" fill="#b8b8b8" fill-opacity="0.85" d="${pathD}"/>`
            + `</svg>`;

        fabric.loadSVGFromString(svg, (objects, options) => {
            const obj = fabric.util.groupSVGElements(objects, options);
            obj.id = id;
            obj.class = 'keyhole';
            obj.hoverCursor = 'move';
            // Size is set numerically in the panel, not by dragging: a drag
            // would turn the circle into an ellipse rather than resize it.
            obj.lockScalingX = true;
            obj.lockScalingY = true;
            obj.lockRotation = true;
            obj.setControlsVisibility({
                tl: false, tr: false, bl: false, br: false,
                ml: false, mr: false, mt: false, mb: false, mtr: false,
            });
            place(obj);
        });
    };

    /**
     * Build a countersunk-screw marker (the donut) and hand it to `place`.
     *
     * Same shape of API as `placeKeyholeShape`, and for the same reasons:
     * fabric parses SVG asynchronously, and adding vs. resizing want different
     * positioning once the object exists.
     */
    const placeScrewShape = (index, headDiameterMm, shankDiameterMm, place) => {
        const id = buildScrewId(index, headDiameterMm, shankDiameterMm);
        const pathD = buildScrewPathD(headDiameterMm, shankDiameterMm);
        const svg = `<svg xmlns="http://www.w3.org/2000/svg">`
            + `<path id="${id}" class="screw" fill="${SCREW_FILL}" fill-opacity="0.85" `
            + `fill-rule="evenodd" d="${pathD}"/>`
            + `</svg>`;

        fabric.loadSVGFromString(svg, (objects, options) => {
            const obj = fabric.util.groupSVGElements(objects, options);
            obj.id = id;
            obj.class = 'screw';
            obj.hoverCursor = 'move';
            // Igual que la ranura: el tamaño se teclea en el panel. Arrastrando
            // un tirador el círculo se volvería un óvalo y el tornillo real no
            // cambiaría de diámetro.
            obj.lockScalingX = true;
            obj.lockScalingY = true;
            obj.lockRotation = true;
            obj.setControlsVisibility({
                tl: false, tr: false, bl: false, br: false,
                ml: false, mr: false, mt: false, mb: false, mtr: false,
            });
            place(obj);
        });
    };

    /**
     * El marcador del pin de Crocs: el mismo donut que el tornillo (cabeza por
     * fuera, cuello por dentro), en verde para distinguirlo.
     */
    const placeCrocsShape = (index, sizes, place) => {
        const id = buildCrocsId(index, sizes);
        const pathD = buildScrewPathD(sizes.headDiameterMm, sizes.neckDiameterMm);
        const svg = `<svg xmlns="http://www.w3.org/2000/svg">`
            + `<path id="${id}" class="crocs" fill="${CROCS_FILL}" fill-opacity="0.85" `
            + `fill-rule="evenodd" d="${pathD}"/>`
            + `</svg>`;

        fabric.loadSVGFromString(svg, (objects, options) => {
            const obj = fabric.util.groupSVGElements(objects, options);
            obj.id = id;
            obj.class = 'crocs';
            obj.hoverCursor = 'move';
            obj.lockScalingX = true;
            obj.lockScalingY = true;
            obj.lockRotation = true;
            obj.setControlsVisibility({
                tl: false, tr: false, bl: false, br: false,
                ml: false, mr: false, mt: false, mb: false, mtr: false,
            });
            place(obj);
        });
    };

    /**
     * Deja un marcador nuevo dentro del fondo, no donde caiga.
     *
     * Antes se escalonaban 60 px a la derecha por marcador, partiendo de
     * `top: 40` en coordenadas de lienzo — un punto que no tiene nada que ver
     * con dónde está la pieza. Con dos o tres marcadores, o con un diseño
     * pequeño, los siguientes salían fuera del fondo, y eso el usuario solo lo
     * descubría al generar el 3D ("marker falls outside the box cavity").
     *
     * Ahora se cascadean alrededor del centro del fondo y se recortan para que
     * el marcador **entero** quepa en su caja. Que caiga dentro de la caja no
     * garantiza que quepa en la cavidad (una letra estrecha no tiene hueco para
     * una cabeza de 8 mm), pero eso es cosa del backend, que sabe medirlo; aquí
     * basta con no dejarlos en el limbo.
     */
    const placeMarkerInsideBackground = (obj, order = 0) => {
        const background = canvas.getObjects().find(isBackgroundShapeObject);
        const bounds = background?.getBoundingRect(true);
        const halfWidth = obj.getScaledWidth() / 2;
        const halfHeight = obj.getScaledHeight() / 2;

        if (!bounds || !bounds.width || !bounds.height) {
            canvas.centerObject(obj);
            obj.setCoords();
            return;
        }

        // Cascada corta y cíclica: que uno nuevo no quede escondido justo
        // detrás del anterior, sin alejarse del centro de la pieza.
        const step = 12 * (order % 5);
        const target = new fabric.Point(
            Math.min(
                Math.max(bounds.left + bounds.width / 2 + step, bounds.left + halfWidth),
                bounds.left + bounds.width - halfWidth,
            ),
            Math.min(
                Math.max(bounds.top + bounds.height / 2 + step, bounds.top + halfHeight),
                bounds.top + bounds.height - halfHeight,
            ),
        );
        obj.setPositionByOrigin(target, 'center', 'center');
        obj.setCoords();
    };

    const placeInternalElementShape = (settings, place) => {
        const { index, kind, shape, sizeMm, thicknessMm, bottomOffsetMm, name } = settings;
        const heightMm = Number(settings.heightMm) || sizeMm;
        const id = buildInternalElementId(index, kind, shape, sizeMm, thicknessMm, bottomOffsetMm, heightMm);
        const sizePx = Math.max(Number(sizeMm), 0.1) * PX_PER_MM;
        const heightPx = Math.max(Number(heightMm), 0.1) * PX_PER_MM;
        const common = {
            id,
            class: 'internal_element',
            name: name || `${kind === 'magnet' ? 'Imán' : 'NFC'} ${index}`,
            fill: INTERNAL_ELEMENT_COLORS[kind] || INTERNAL_ELEMENT_COLORS.magnet,
            opacity: 0.72,
            stroke: '#ffffff',
            strokeWidth: 1.5,
            hoverCursor: 'move',
            lockScalingX: true,
            lockScalingY: true,
            lockRotation: true,
        };
        const obj = shape === 'square'
            ? new fabric.Rect({ ...common, width: sizePx, height: heightPx, originX: 'center', originY: 'center' })
            : new fabric.Circle({ ...common, radius: sizePx / 2, originX: 'center', originY: 'center' });
        obj.setControlsVisibility({
            tl: false, tr: false, bl: false, br: false,
            ml: false, mr: false, mt: false, mb: false, mtr: false,
        });
        place(obj);
    };

    const placeSilbatoShape = (settings, place) => {
        const { index, name } = settings;
        const geometry = buildSilbatoGeometry(settings);
        const params = geometry.params;

        // El espejo ya viene aplicado a los puntos, no con `flipX` del grupo:
        // fabric no propaga `flipX` a los `d` que escribe `toSVG()`, así que el
        // backend recibiría siempre el silbato sin espejar.
        const toPath = (points) => points
            .map(([x, y], i) => `${i ? 'L' : 'M'}${(x * PX_PER_MM).toFixed(3)} ${(y * PX_PER_MM).toFixed(3)}`)
            .join(' ') + ' Z';

        // Cada pieza se lleva su marca y sus cotas: si el grupo se deshace (lo
        // hace el guardado), el silbato se puede reconstruir desde cualquiera.
        const pieces = geometry.pieces.map(({ piece, role, points }) => new fabric.Path(toPath(points), {
            id: buildSilbatoPieceId(index, piece, params),
            class: 'internal_element',
            name: `Silbato ${index} · ${role}`,
            fill: INTERNAL_ELEMENT_COLORS.silbato,
            opacity: 0.72,
            // Sin borde: las piezas comparten aristas a propósito (el fondo del
            // conducto con la rampa, la cámara con la ranura), y un contorno
            // por pieza dibujaba una costura en juntas que están pegadas.
            objectCaching: false,
            _silbatoId: SILBATO_GROUP_ID(index),
            _silbatoParams: params,
        }));

        const group = new fabric.Group(pieces, {
            id: SILBATO_GROUP_ID(index),
            class: 'internal_element',
            name: name || `Silbato ${index}`,
            hoverCursor: 'move',
            _silbatoId: SILBATO_GROUP_ID(index),
            _silbatoParams: params,
            // El tamaño se controla desde el panel, porque cada cota tiene su
            // propio efecto acústico; estirar el grupo a mano descuadraría el
            // labio respecto a la cámara sin que el panel se entere.
            lockScalingX: true,
            lockScalingY: true,
        });
        group.setControlsVisibility({
            tl: false, tr: false, bl: false, br: false,
            ml: false, mr: false, mt: false, mb: false,
        });
        place(group);
    };

    const placeAbridorShape = (settings, place) => {
        const { index, name } = settings;
        const geometry = buildAbridorGeometry(settings);
        const params = geometry.params;
        const ids = buildAbridorPieceIds(index, params, geometry.washer);
        const toPath = (contours) => contours.map(points => points
            .map(([x, y], i) => `${i ? 'L' : 'M'}${(x * PX_PER_MM).toFixed(3)} ${(y * PX_PER_MM).toFixed(3)}`)
            .join(' ') + ' Z').join(' ');

        // Primero el rebaje y encima la arandela, para que se vea lo que asoma.
        const pieces = geometry.pieces.map(({ piece, role, contours }) => new fabric.Path(toPath(contours), {
            id: ids[piece],
            class: 'internal_element',
            name: `Abrebotellas ${index} · ${role}`,
            fill: piece === 'rebaje' ? abridorPocketFill(params) : INTERNAL_ELEMENT_COLORS.abridor_arandela,
            opacity: piece === 'rebaje' ? 0.75 : 0.9,
            stroke: piece === 'rebaje' ? '#ffffff' : null,
            strokeWidth: piece === 'rebaje' ? 1 : 0,
            fillRule: 'nonzero',
            objectCaching: false,
            _abridorId: ABRIDOR_GROUP_ID(index),
            _abridorParams: params,
        }));

        const group = new fabric.Group(pieces, {
            id: ABRIDOR_GROUP_ID(index),
            class: 'internal_element',
            name: name || `Abrebotellas ${index}`,
            hoverCursor: 'move',
            _abridorId: ABRIDOR_GROUP_ID(index),
            _abridorParams: params,
            // Las cotas salen del panel: estirarlo a mano cambiaría el tamaño
            // de una arandela que es una pieza comprada.
            lockScalingX: true,
            lockScalingY: true,
        });
        group.setControlsVisibility({
            tl: false, tr: false, bl: false, br: false,
            ml: false, mr: false, mt: false, mb: false,
        });
        place(group);
    };

    const placeAbrelatasShape = (settings, place) => {
        const { index, name } = settings;
        const geometry = buildAbrelatasGeometry(settings);
        const params = geometry.params;
        const toPath = points => points
            .map(([x, y], i) => `${i ? 'L' : 'M'}${(x * PX_PER_MM).toFixed(3)} ${(y * PX_PER_MM).toFixed(3)}`)
            .join(' ') + ' Z';

        const piece = new fabric.Path(toPath(geometry.contour), {
            id: buildAbrelatasId(index, params),
            class: 'internal_element',
            name: `Abrelatas ${index} · huella`,
            fill: abrelatasFill(params),
            opacity: 0.8,
            stroke: '#ffffff',
            strokeWidth: 1,
            objectCaching: false,
            _abrelatasId: ABRELATAS_GROUP_ID(index),
            _abrelatasParams: params,
        });

        const group = new fabric.Group([piece], {
            id: ABRELATAS_GROUP_ID(index),
            class: 'internal_element',
            name: name || `Abrelatas ${index}`,
            hoverCursor: 'move',
            _abrelatasId: ABRELATAS_GROUP_ID(index),
            _abrelatasParams: params,
            // El ancho y el largo salen del panel; estirarlo a mano no cambiaría
            // el id y el lienzo mentiría sobre el 3D.
            lockScalingX: true,
            lockScalingY: true,
        });
        group.setControlsVisibility({
            tl: false, tr: false, bl: false, br: false,
            ml: false, mr: false, mt: false, mb: false,
        });
        place(group);
    };

    const ASSEMBLY_PLACERS = { silbato: placeSilbatoShape, abridor: placeAbridorShape, abrelatas: placeAbrelatasShape };

    /**
     * Si la huella de un abrelatas rebasa el borde del llavero como debe.
     *
     * Pinta los fondos en un lienzo aparte y mira los píxeles bajo los dos
     * extremos de la huella, así vale para cualquier silueta. Devuelve
     * 'ok', 'inside' (no llega al borde), 'outside' (fuera del llavero) o
     * 'crossing' (el extremo interior también se sale), o null sin fondo.
     * Es lo mismo que comprueba el backend, para avisar antes de generar.
     */
    const abrelatasEdgeStatus = (pieces) => {
        const huella = pieces.flatMap(object => object._objects || [object])
            .find(object => ABRELATAS_ID_RE.test(String(object.id || '')));
        const backgrounds = canvas.getObjects()
            .filter(object => String(object?.id ?? '').startsWith('background') && object.visible !== false);
        if (!huella || !backgrounds.length) return null;

        const rects = backgrounds.map(object => object.getBoundingRect(true, true));
        const left = Math.min(...rects.map(rect => rect.left));
        const top = Math.min(...rects.map(rect => rect.top));
        const right = Math.max(...rects.map(rect => rect.left + rect.width));
        const bottom = Math.max(...rects.map(rect => rect.top + rect.height));
        const scale = Math.min(1, 1024 / Math.max(right - left, bottom - top, 1));
        const element = fabric.util.createCanvasElement();
        element.width = Math.ceil((right - left) * scale) + 2;
        element.height = Math.ceil((bottom - top) * scale) + 2;
        const ctx = element.getContext('2d');
        ctx.scale(scale, scale);
        ctx.translate(-left, -top);
        const skipOffscreen = canvas.skipOffscreen;
        canvas.skipOffscreen = false;
        try {
            backgrounds.forEach(object => object.render(ctx));
        } finally {
            canvas.skipOffscreen = skipOffscreen;
        }
        const pixels = ctx.getImageData(0, 0, element.width, element.height).data;

        const matrix = huella.calcTransformMatrix();
        const { widthMm, heightMm } = buildAbrelatasGeometry(huella._abrelatasParams);
        const isInside = ([xMm, yMm]) => {
            const point = fabric.util.transformPoint(new fabric.Point(
                xMm * PX_PER_MM - huella.pathOffset.x,
                yMm * PX_PER_MM - huella.pathOffset.y,
            ), matrix);
            const x = Math.round((point.x - left) * scale);
            const y = Math.round((point.y - top) * scale);
            if (x < 0 || y < 0 || x >= element.width || y >= element.height) return false;
            return pixels[(y * element.width + x) * 4 + 3] > 0;
        };
        // Medio milímetro hacia dentro de cada esquina, para no leer el borde.
        const across = [0.5, widthMm / 2, widthMm - 0.5];
        const outer = across.filter(x => isInside([x, 0.5])).length;
        const inner = across.filter(x => isInside([x, heightMm - 0.5])).length;
        if (outer === across.length) return 'inside';
        if (inner < across.length) return isInside([widthMm / 2, heightMm / 2]) ? 'crossing' : 'outside';
        return 'ok';
    };

    /**
     * Vuelve a juntar en un grupo las piezas sueltas de cada silbato.
     *
     * Guardar el diseño deshace los grupos (`saveSVG(true)` →
     * `toActiveSelection()`), así que un diseño reabierto trae las piezas al
     * mismo nivel que todo lo demás: el panel las contaba de una en una y
     * arrastrar una movía sólo esa. Se reconstruye por `_silbatoId`.
     */
    /**
     * Corre una reconstrucción de grupos sin ensuciar el diseño.
     *
     * Rehacer un grupo quita y pone objetos, y eso dispara los eventos con los
     * que Customizer marca "sin guardar". Recomponer lo que rompimos nosotros
     * no es una edición del usuario: sin esta marca, guardar dejaba el
     * indicador en sucio justo después de guardar.
     *
     * Es a propósito más estrecha que `asInternalEdit`: aquella cubre cosas
     * como borrar un imán, que sí tienen que marcar el diseño como modificado.
     */
    const asGroupRestore = (restore) => {
        canvas.__customiz3rRestoringGroups = true;
        // Fabric repinta en cada `add`/`remove`, y recomponer un grupo son
        // varios seguidos: sin esto el lienzo parpadea y los objetos se ven
        // desaparecer y volver mientras se guarda. Un solo repintado al final.
        const renderOnAddRemove = canvas.renderOnAddRemove;
        canvas.renderOnAddRemove = false;
        try {
            return restore();
        } finally {
            canvas.renderOnAddRemove = renderOnAddRemove;
            canvas.__customiz3rRestoringGroups = false;
            canvas.requestRenderAll();
        }
    };

    // Los grupos de elementos internos no se redimensionan a mano: sus cotas se
    // escriben en el panel. Al rehacerlos hay que devolverles esa configuración,
    // que no viaja en las propiedades serializadas.
    const applyGroupChrome = (group) => {
        if (group.class !== 'internal_element') return;
        group.setControlsVisibility({
            tl: false, tr: false, bl: false, br: false,
            ml: false, mr: false, mt: false, mb: false,
        });
    };

    /**
     * Apunta los grupos del lienzo para poder rehacerlos.
     *
     * `toActiveSelection()` no deselecciona un grupo: lo destruye, y deja a sus
     * hijos sueltos al mismo nivel que todo lo demás. Guardar hacía eso y el
     * lienzo se quedaba así, de modo que el usuario guardaba y sus grupos se
     * deshacían. Aquí se anotan antes; `restoreGroups` los rehace después.
     */
    const captureGroups = () => canvas.getObjects()
        .filter(object => object.type === 'group' && object._objects?.length)
        .map(group => {
            const props = {};
            [...serializedEditorProperties,
                'lockScalingX', 'lockScalingY', 'lockRotation', 'opacity', 'visible',
            ].forEach((key) => {
                if (group[key] !== undefined) props[key] = group[key];
            });
            // El sitio en la pila se apunta con lo demás: `canvas.add` deja el
            // grupo rehecho arriba del todo, y un objeto que reaparece delante
            // de lo que antes lo tapaba es justo el "hace cosas raras".
            return {
                group,
                children: group._objects.slice(),
                props,
                index: canvas.getObjects().indexOf(group),
            };
        });

    const restoreGroups = (captured) => asGroupRestore(() => {
        let restored = 0;
        captured.forEach(({ group, children, props, index }) => {
            // Si el grupo sigue en pie, nadie lo rompió y no hay nada que hacer.
            if (canvas.getObjects().includes(group)) return;
            const alive = children.filter(child => canvas.getObjects().includes(child));
            // Un grupo de una pieza no merece rehacerse, salvo el abrelatas, que
            // es así: suelto perdería el bloqueo de escala y su marca de grupo.
            if (alive.length < (assemblyIdOf(group) ? 1 : 2)) return;
            canvas.remove(...alive);
            const rebuilt = new fabric.Group(alive, props);
            applyGroupChrome(rebuilt);
            rebuilt.setCoords();
            canvas.add(rebuilt);
            if (index >= 0) canvas.moveTo(rebuilt, Math.min(index, canvas.getObjects().length - 1));
            restored += 1;
        });
        // El fondo y las capas fijas vuelven al suelo: reinsertar cambia
        // índices, y nada puede quedar por debajo de ellas.
        if (restored) pinFloorLayers(canvas);
        return restored;
    });

    const regroupAssemblyPieces = () => asGroupRestore(regroupAssemblyPiecesUnguarded);

    // Vale para todo elemento de varias piezas (silbato, abrebotellas).
    const regroupAssemblyPiecesUnguarded = () => {
        const loose = new Map();
        canvas.getObjects().forEach((object) => {
            if (object.type === 'group') return;
            const assemblyId = assemblyIdOf(object);
            if (!assemblyId) return;
            if (!loose.has(assemblyId)) loose.set(assemblyId, []);
            loose.get(assemblyId).push(object);
        });
        if (!loose.size) return;
        loose.forEach((pieces, assemblyId) => {
            const kind = assemblyKindOf(assemblyId);
            if (!kind) return;
            const { idProp, paramsProp, label } = ASSEMBLY_KINDS[kind];
            const index = assemblyIndexOf(assemblyId);
            const params = assemblyParamsOf(pieces, kind);
            // El grupo se queda donde estaba la pieza más baja, y no arriba del
            // todo: aparecer delante de lo que antes lo tapaba se ve como si el
            // diseño se hubiera reordenado solo.
            const stackAt = Math.min(...pieces.map(piece => canvas.getObjects().indexOf(piece)));
            canvas.remove(...pieces);
            const group = new fabric.Group(pieces, {
                id: assemblyId,
                class: 'internal_element',
                name: `${label} ${index}`,
                hoverCursor: 'move',
                [idProp]: assemblyId,
                [paramsProp]: params,
                lockScalingX: true,
                lockScalingY: true,
            });
            group.setControlsVisibility({
                tl: false, tr: false, bl: false, br: false,
                ml: false, mr: false, mt: false, mb: false,
            });
            group.setCoords();
            canvas.add(group);
            if (stackAt >= 0) canvas.moveTo(group, Math.min(stackAt, canvas.getObjects().length - 1));
        });
        pinFloorLayers(canvas);
    };

    // Todos los objetos de un conjunto: el grupo si sobrevivió, o sus piezas.
    const assemblyObjects = (assemblyId) => canvas.getObjects()
        .filter(object => assemblyIdOf(object) === assemblyId);

    let lastActiveTextObject = null;
    const clampZoom = (zoom) => Math.min(Math.max(zoom, MIN_CANVAS_ZOOM), MAX_CANVAS_ZOOM);
    const zoomCanvasToPoint = (point, nextZoom) => {
        canvas.zoomToPoint(point, clampZoom(nextZoom));
        canvas.requestRenderAll();
    };
    const zoomCanvasBy = (factor, point = null) => {
        const zoomPoint = point || new fabric.Point(canvas.getWidth() / 2, canvas.getHeight() / 2);
        zoomCanvasToPoint(zoomPoint, canvas.getZoom() * factor);
    };
    const isBackgroundObject = (object) => String(object?.id ?? '').startsWith('background');
    // Las reglas de medida (líneas rojas + etiquetas "Xmm"/"Ymm" de
    // `makeRulesForObjects`) llevan id con prefijo "background" pero se
    // dibujan desplazadas fuera de la forma real: si entran en la caja de
    // "alinear respecto al fondo" la desplazan y falsean el resultado.
    const isBackgroundShapeObject = (object) => isBackgroundObject(object)
        && !String(object?.id ?? '').startsWith('background_line')
        && !String(object?.id ?? '').startsWith('background_text');
    // Cubre tanto el agujero del llavero (id) como la ranura de colgado de la
    // caja de luz (class) — antes solo miraba el id y la ranura caía en el
    // "catch-all" de más abajo, mal etiquetada como "Grupo" en el panel de
    // capas y sin excluirse del encuadre automático de vista.
    const isHoleObject = (object) => object?._geometryRole === 'hole'
        || String(object?.id ?? '').startsWith('hole') || object?.class === 'keyhole'
        || object?.class === 'screw' || isNapkinJointObject(object);
    const isInternalElementObject = (object) => object?.class === 'internal_element'
        || String(object?.id || '').startsWith('insert_')
        || object?.class === 'crocs';
    const isFixedObject = (object) => object?.class === 'drawing_doesnt_move';
    const isVisibleObject = (object) => object && object.visible !== false;
    const isEditableViewportObject = (object) => (
        isVisibleObject(object) &&
        !isBackgroundObject(object) &&
        !isHoleObject(object)
    );
    // Categoría del gestor de capas: fondo / agujeros / decoración fija del
    // layout base / todo lo demás (dibujos: formas, texto, SVGs del usuario).
    const getObjectCategory = (object) => {
        if (isBackgroundObject(object)) return 'background';
        if (isHoleObject(object)) return 'holes';
        if (isInternalElementObject(object)) return 'internal';
        if (isFixedObject(object)) return 'fixed';
        return 'drawings';
    };
    // El alto de línea de fabric reserva hueco para ascendentes/descendentes
    // de la fuente aunque el texto no los use (mayúsculas, sin tildes...), así
    // `tightenTextInk`: para texto, usa la tinta real en vez del alto de
    // línea de la fuente — solo lo piden alinear/distribuir (ver arriba).
    const getObjectBounds = (object, { tightenTextInk = false } = {}) => {
        object.setCoords();
        if (tightenTextInk && isTextObject(object)) {
            const inkBounds = getTextInkBounds(object);
            if (inkBounds) return inkBounds;
        }
        const bounds = object.getBoundingRect(true, true);
        return {
            left: bounds.left,
            top: bounds.top,
            right: bounds.left + bounds.width,
            bottom: bounds.top + bounds.height,
        };
    };
    const getObjectsBounds = (objects, options) => {
        if (!objects.length) return null;

        const bounds = objects.map(object => getObjectBounds(object, options));
        const left = Math.min(...bounds.map(bound => bound.left));
        const top = Math.min(...bounds.map(bound => bound.top));
        const right = Math.max(...bounds.map(bound => bound.right));
        const bottom = Math.max(...bounds.map(bound => bound.bottom));

        return {
            left,
            top,
            width: Math.max(right - left, 1),
            height: Math.max(bottom - top, 1),
            centerX: (left + right) / 2,
            centerY: (top + bottom) / 2,
        };
    };
    // Caja objetivo para "alinear respecto a": la propia selección, el primer
    // objeto que entró en ella, el de mayor área, o el fondo del diseño.
    const getAlignmentTargetBounds = (referenceMode, selectedObjects) => {
        const inkOptions = { tightenTextInk: true };
        if (referenceMode === 'background') {
            const backgroundObjects = canvas.getObjects().filter(isBackgroundShapeObject);
            return getObjectsBounds(backgroundObjects) || getObjectsBounds(selectedObjects, inkOptions);
        }
        if (referenceMode === 'first') {
            return selectedObjects[0] ? getObjectsBounds([selectedObjects[0]], inkOptions) : null;
        }
        if (referenceMode === 'biggest') {
            const biggest = selectedObjects.reduce((best, object) => {
                const bounds = getObjectBounds(object, inkOptions);
                const area = (bounds.right - bounds.left) * (bounds.bottom - bounds.top);
                return !best || area > best.area ? { object, area } : best;
            }, null);
            return biggest ? getObjectsBounds([biggest.object], inkOptions) : null;
        }
        return getObjectsBounds(selectedObjects, inkOptions);
    };

    const getViewportFitObjects = () => {
        const selectedObjects = canvas.getActiveObjects().filter(isEditableViewportObject);
        if (selectedObjects.length) return selectedObjects;

        const editableObjects = canvas.getObjects().filter(isEditableViewportObject);
        if (editableObjects.length) return editableObjects;

        const baseObjects = canvas.getObjects().filter(object => isVisibleObject(object) && object.id === 'background');
        if (baseObjects.length) return baseObjects;

        return canvas.getObjects().filter(isVisibleObject);
    };

    const nextCustomHoleIndex = () => {
        const used = [];
        const collect = (object) => {
            const match = /^hole_custom_(\d+)$/i.exec(String(object?.id || ''));
            if (match) used.push(Number(match[1]));
            object?._objects?.forEach(collect);
        };
        canvas.getObjects().forEach(collect);
        return used.length ? Math.max(...used) + 1 : 1;
    };

    const applyHoleOperation = (object, counter) => {
        if (!object || isBackgroundObject(object) || isInternalElementObject(object)) return;
        if (object._objects?.length) {
            object._objects.forEach(child => applyHoleOperation(child, counter));
            return;
        }
        if (object._geometryRole !== 'hole') {
            object._operationOriginalId = object.id || '';
            object._operationOriginalClass = object.class || '';
            object.id = `hole_custom_${counter.value++}`;
            object.class = 'technical_hole';
            object._geometryRole = 'hole';
        }
        delete object._vaciadoFill;
        object.set({ fill: createHolePattern() });
    };
    // El canvas ocupa toda la pantalla, pero la barra izquierda y el panel
    // derecho (cuando está abierto) se dibujan encima suyo — encuadrar en
    // `canvas.getWidth()/2` deja el dibujo centrado en un punto que en
    // pantalla puede quedar tapado por el panel derecho. `insets` (en
    // píxeles CSS, medidos por quien llama a partir del ancho real de esas
    // barras) desplaza el centro de encuadre al hueco realmente visible.
    const resetViewport = ({ leftInset = 0, rightInset = 0, topInset = 0, bottomInset = 0 } = {}) => {
        const bounds = getObjectsBounds(getViewportFitObjects());

        if (!bounds) {
            canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
            canvas.requestRenderAll();
            return;
        }

        const visibleWidth = Math.max(canvas.getWidth() - leftInset - rightInset, 1);
        const visibleHeight = Math.max(canvas.getHeight() - topInset - bottomInset, 1);
        const padding = Math.min(96, Math.max(24, Math.min(visibleWidth, visibleHeight) * 0.12));
        const availableWidth = Math.max(visibleWidth - padding * 2, 1);
        const availableHeight = Math.max(visibleHeight - padding * 2, 1);
        const fitZoom = Math.min(availableWidth / bounds.width, availableHeight / bounds.height, 2.5);
        const zoom = clampZoom(fitZoom);
        const visibleCenterX = leftInset + visibleWidth / 2;
        const visibleCenterY = topInset + visibleHeight / 2;

        canvas.setViewportTransform([
            zoom,
            0,
            0,
            zoom,
            visibleCenterX - bounds.centerX * zoom,
            visibleCenterY - bounds.centerY * zoom,
        ]);
        canvas.requestRenderAll();
    };
    const rememberTextObject = (object) => {
        if (isTextObject(object)) lastActiveTextObject = object;
    };
    const getActiveTextObject = () => {
        const activeText = canvas.getActiveObjects().find(object => isTextObject(object));
        if (activeText) {
            lastActiveTextObject = activeText;
            return activeText;
        }
        if (lastActiveTextObject && canvas.getObjects().includes(lastActiveTextObject)) {
            return lastActiveTextObject;
        }
        return null;
    };

    const applyTextFormatting = (textObject, { text, fontFamily, fontStyle, fontWeight } = {}, { commit = false } = {}) => {
        if (!textObject) return false;
        const changes = {};
        if (text !== undefined) changes.text = text;
        if (fontFamily) changes.fontFamily = fontFamily;
        if (fontStyle) changes.fontStyle = fontStyle;
        if (fontWeight) changes.fontWeight = fontWeight;

        if (!Object.keys(changes).length) return false;
        textObject.set(changes);
        if (typeof textObject.initDimensions === "function") textObject.initDimensions();
        textObject.setCoords();
        canvas.renderAll();
        if (commit) canvas.fire('text:changed', { target: textObject });
        return true;
    };

    // --- Vectorizar texto ---------------------------------------------------
    // Convierte un fabric.IText en contornos vectoriales reales (un único
    // fabric.Path), avisando antes de que la conversión es irreversible (la
    // hace la UI, aquí solo se ejecuta). Dos motores para comparar:
    //  - vectorizeTextClient: opentype.js en el navegador, instantáneo.
    //  - vectorizeTextServer/getVectorizableTextSvg/applyVectorizedTextSvg:
    //    Inkscape en el backend (mismo binario que ya usa la exportación a
    //    3D), vía el endpoint customized-keyrings/vectorize-text/.
    let pendingVectorizeTarget = null;

    const getTextLinesFor = (object) => (
        object.textLines && object.textLines.length ? object.textLines : [object.text || '']
    );

    // Reproduce fabric.Text._renderTextCommon (text.class.js): cada línea se
    // pinta en (left + leftOffset, top + lineHeights + maxHeight), con
    // baseline alfabético, en un espacio local centrado en el objeto. Usar
    // los mismos números para opentype.js hace que el trazado real caiga
    // donde fabric colocaría la línea, no donde cae el alto de línea de la
    // fuente (que es lo que usa `getObjectBounds`/`getTextInkBounds`).
    const getFabricTextLineOrigins = (object) => {
        const left = object._getLeftOffset();
        const top = object._getTopOffset();
        let lineHeights = 0;
        return getTextLinesFor(object).map((line, index) => {
            const heightOfLine = object.getHeightOfLine(index);
            const maxHeight = heightOfLine / object.lineHeight;
            const leftOffset = object._getLineLeftOffset(index);
            const origin = { line, x: left + leftOffset, y: top + lineHeights + maxHeight };
            lineHeights += heightOfLine;
            return origin;
        });
    };

    const swapTextForVector = (textObject, replacement) => {
        replacement.set({ _vectorizedText: true });
        canvas.remove(textObject);
        canvas.add(replacement);
        canvas.setActiveObject(replacement);
        if (lastActiveTextObject === textObject) lastActiveTextObject = null;
        canvas.requestRenderAll();
        canvas.fire('object:modified');
    };

    // Construye el trazado real de un texto y lo devuelve ya colocado, sin
    // tocar el lienzo. Lo usan dos cosas: vectorizar de verdad (sustituyendo
    // el texto) y sólo medir (para saber dónde cae su contorno sin destruir
    // el objeto, que es lo que necesita el centrado del Offset).
    const buildVectorizedText = async (textObject) => {
        if (!textObject) return { ok: false, reason: 'no-text' };
        if (!String(textObject.text || '').trim()) return { ok: false, reason: 'empty' };

        const family = String(textObject.fontFamily || '').trim().replace(/^['"]|['"]$/g, '');
        if (!EDITOR_FONTS[family]) return { ok: false, reason: 'font-unavailable' };

        let font;
        try {
            font = await loadOpentypeFont(family);
        } catch {
            return { ok: false, reason: 'font-unavailable' };
        }

        textObject.setCoords();
        const fontSize = textObject.fontSize;
        const fontScale = 1 / font.unitsPerEm * fontSize;

        // Un fabric.Path por letra, no uno por línea con todos los contornos
        // concatenados: fuentes decorativas (comprobado con "Rye" — la "s"
        // de "Geiser" perdía media letra) dibujan letras contiguas que se
        // solapan a propósito, y un único path con todo junto usa una sola
        // regla de relleno para todo el texto — donde dos letras se
        // solapan, esa regla puede interpretarlo como un hueco en vez de
        // tinta doble y borra parte de una letra. Cada letra en su propio
        // Path (con sus propios contornos internos intactos, para que sus
        // huecos — el interior de una "e" — sigan funcionando) se compone
        // sola: son opacas y del mismo color, así que solaparse no se nota.
        const glyphPathData = [];
        getFabricTextLineOrigins(textObject).forEach(({ line, x: lineX, y: lineY }) => {
            if (!line) return;
            const glyphs = font.stringToGlyphs(line);
            let x = lineX;
            glyphs.forEach((glyph, index) => {
                const d = glyph.getPath(x, lineY, fontSize).toPathData(2);
                if (d.trim()) glyphPathData.push(d);
                x += glyph.advanceWidth * fontScale;
                if (index < glyphs.length - 1) {
                    x += font.getKerningValue(glyph, glyphs[index + 1]) * fontScale;
                }
            });
        });

        if (!glyphPathData.length) return { ok: false, reason: 'empty' };

        // Sin borde: el texto no tiene UI para poner stroke, así que el
        // `strokeWidth` por defecto de fabric (1, aunque `stroke` sea null)
        // no debe colarse en el resultado vectorizado.
        const glyphStyle = {
            fill: textObject.fill,
            opacity: textObject.opacity,
            stroke: null,
            strokeWidth: 0,
        };
        const vectorized = glyphPathData.length === 1
            ? new fabric.Path(glyphPathData[0], glyphStyle)
            : new fabric.Group(glyphPathData.map((d) => new fabric.Path(d, glyphStyle)));

        // El resultado se generó en el espacio local del texto (centrado en
        // su propio origen); un fabric.Path dibuja cada punto en
        // `local - pathOffset` (path.class.js) y un Group recién creado
        // tiene el mismo comportamiento respecto a su propio centro, así
        // que basta con rotar/escalar ese offset con el mismo
        // angle/scale del texto y sumarlo a su centro real en el canvas.
        const localOffset = vectorized.pathOffset || vectorized.getCenterPoint();
        const angleRad = fabric.util.degreesToRadians(textObject.angle || 0);
        const scaleX = textObject.scaleX ?? 1;
        const scaleY = textObject.scaleY ?? 1;
        const scaledOffset = new fabric.Point(localOffset.x * scaleX, localOffset.y * scaleY);
        const rotatedOffset = fabric.util.rotatePoint(scaledOffset, new fabric.Point(0, 0), angleRad);
        const center = textObject.getCenterPoint();
        const newCenter = new fabric.Point(center.x + rotatedOffset.x, center.y + rotatedOffset.y);

        vectorized.set({
            angle: textObject.angle,
            scaleX,
            scaleY,
            skewX: textObject.skewX,
            skewY: textObject.skewY,
            flipX: textObject.flipX,
            flipY: textObject.flipY,
        });
        vectorized.setPositionByOrigin(newCenter, 'center', 'center');
        vectorized.setCoords();

        return { ok: true, vectorized };
    };

    const vectorizeTextClient = async () => {
        const textObject = getActiveTextObject();
        const result = await buildVectorizedText(textObject);
        if (!result.ok) return result;
        swapTextForVector(textObject, result.vectorized);
        return { ok: true };
    };


    // Prepara el SVG que se manda al backend: solo el fragmento del texto
    // activo, envuelto en un documento con el mismo viewBox que el canvas.
    // Guarda el objeto en `pendingVectorizeTarget` porque entre esta llamada
    // y `applyVectorizedTextSvg` hay un viaje de red de por medio.
    const getVectorizableTextSvg = () => {
        const textObject = getActiveTextObject();
        if (!textObject) return null;
        if (!String(textObject.text || '').trim()) return null;

        pendingVectorizeTarget = textObject;
        const width = canvas.getWidth();
        const height = canvas.getHeight();
        return `<?xml version="1.0" encoding="UTF-8"?>\n`
            + `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" `
            + `viewBox="0 0 ${width} ${height}">${textObject.toSVG()}</svg>`;
    };

    // Recibe el SVG ya vectorizado por Inkscape y lo intercambia por el
    // texto guardado en `pendingVectorizeTarget`. El grupo cargado no llega
    // centrado en su posición de página real (fabric.util.groupSVGElements
    // recoloca al importar, igual que hace `getBaseLayoutToCanvas` con los
    // layouts base), así que se reancla al centro real del texto original —
    // el ángulo/escala ya vienen "horneados" en la geometría que devolvió
    // Inkscape, no hace falta copiarlos a mano como en el enfoque opentype.
    const applyVectorizedTextSvg = (svgString) => {
        const textObject = pendingVectorizeTarget;
        pendingVectorizeTarget = null;
        if (!textObject || !canvas.getObjects().includes(textObject)) return false;

        const center = textObject.getCenterPoint();
        fabric.loadSVGFromString(svgString, (objects, options) => {
            const parsed = objects.filter(Boolean);
            parsed.forEach(sanitizeObjectIds);
            if (!parsed.length) return;
            // Sin borde: Inkscape puede arrastrar el `stroke-width` que
            // traía el `<text>` original aunque no tuviera color de trazo,
            // y fabric.Group no reescribe el stroke de cada hijo por su
            // cuenta, así que se limpia objeto a objeto antes de agrupar.
            parsed.forEach((object) => object.set({ stroke: null, strokeWidth: 0 }));
            const group = fabric.util.groupSVGElements(parsed, options);
            group.set({ stroke: null, strokeWidth: 0 });
            group.setPositionByOrigin(center, 'center', 'center');
            group.setCoords();
            swapTextForVector(textObject, group);
        });
        return true;
    };

    /**
     * Borra las piezas elegidas dentro de un grupo o de un SVG.
     *
     * `canvas.remove()` no vale: esas piezas no están en el lienzo, están
     * dentro del grupo. Hay que sacarlas de su padre directo con
     * `removeWithUpdate`, que además recalcula la caja del grupo para que no
     * quede con el tamaño de antes. Un grupo que se queda sin hijos se va
     * detrás, y se sigue hacia arriba porque al quitarlo puede vaciar al suyo.
     */
    const deleteNestedSelection = () => {
        const removed = [...selectedNestedObjects];
        const orphaned = new Set();

        // Borrar es una edición nuestra, no un "el usuario ha deseleccionado":
        // sin esta marca, el `selection:cleared` que suelta fabric cerraría el
        // panel de capas justo desde el que se está borrando.
        asInternalEdit(() => {
            removed.forEach(object => {
                const parent = object.group;
                if (!parent) {
                    canvas.remove(object);
                    return;
                }
                parent.removeWithUpdate(object);
                orphaned.add(parent);
            });
            selectedNestedObjects = new Set();

            const pending = [...orphaned];
            while (pending.length > 0) {
                const group = pending.pop();
                if (group._objects && group._objects.length > 0) continue;
                if (group.group) {
                    group.group.removeWithUpdate(group);
                    pending.push(group.group);
                } else {
                    canvas.remove(group);
                }
            }

            // El grupo que queda sigue seleccionado, así se pueden ir borrando
            // piezas seguidas; solo se suelta si ha desaparecido con ellas.
            const active = canvas.getActiveObject();
            if (active && !canvas.getObjects().includes(active)) canvas.discardActiveObject();
        });

        canvas.requestRenderAll();
        // Ninguno de los dos eventos de fabric salta al tocar el interior de un
        // grupo, así que se avisa a mano: el panel de capas se repinta y el
        // diseño queda marcado como sin guardar.
        canvas.fire('layer:nested-selection');
        canvas.fire('layer:changed');
        const label = removed.length === 1
            ? `${objectTypeLabel(removed[0])} del grupo eliminado`
            : `${removed.length} elementos del grupo eliminados`;
        saveSnapshot(label);
    };

    // ── Esquinas redondeadas de los rectángulos ─────────────────────────────
    // El fondo queda fuera a propósito: su redondeo es un parámetro de la
    // base (`baseLayout.rounded`), no una propiedad del objeto del lienzo.
    const getSelectedRects = () => {
        const objects = selectedNestedObjects.size > 0
            ? [...selectedNestedObjects]
            : canvas.getActiveObjects();
        return objects.filter(object =>
            object?.type === 'rect' && !String(object.id || '').startsWith('background')
        );
    };

    /**
     * Escala real del rectángulo sobre el lienzo.
     *
     * `getObjectScaling` compone también la del grupo o la de la selección
     * múltiple que lo contenga, que es lo que hace que las medidas en mm
     * cuadren con lo que se ve y no solo con la escala propia del objeto.
     *
     * En valor absoluto: espejar (cambiar de cara, o `mirrorActiveObjects`) deja
     * la escala en negativo, y de aquí salen radios y topes en mm, que son
     * medidas y no pueden ser negativas.
     */
    const rectScaling = (rect) => {
        const scaling = rect.getObjectScaling?.() ?? {};
        return {
            x: Math.abs(scaling.scaleX || rect.scaleX || 1),
            y: Math.abs(scaling.scaleY || rect.scaleY || 1),
        };
    };

    /** Radio máximo que admite un rectángulo, en px de lienzo ya escalados. */
    const maxRadiusPx = (rect) => {
        const scale = rectScaling(rect);
        return Math.min(rect.width * scale.x, rect.height * scale.y) / 2;
    };

    /**
     * Radio visible de un rectángulo, en px de lienzo.
     *
     * `rx`/`ry` viven en el espacio propio del objeto, sin escalar, así que
     * hay que multiplicar por la escala para saber qué se está viendo.
     */
    const visibleRadiusPx = (rect) => {
        const scale = rectScaling(rect);
        return Math.min((rect.rx || 0) * scale.x, (rect.ry || 0) * scale.y);
    };

    // --- Visibilidad por objeto/categoría (gestor de capas) ---
    // Ocultar es una ayuda visual de la sesión de edición actual (como en
    // Photoshop/Illustrator): nunca debe colarse en lo que se manda a
    // fabricar ni sobrevivir a guardar/recargar el diseño. `hiddenObjects`
    // guarda referencias a objetos Fabric, igual que `selectedNestedObjects`
    // más abajo — por eso se resetea en cada carga (loadJSON/loadSnapshot),
    // ya que esas referencias dejan de apuntar a los objetos vivos del lienzo.
    let hiddenObjects = new Set();

    const applyObjectVisibility = () => {
        canvas.getObjects().forEach(obj => {
            const shouldBeVisible = !hiddenObjects.has(obj);
            // Asignación directa, no `.set()`: no debe disparar `object:modified`
            // ni marcar el diseño como "sin guardar".
            if (obj.visible !== shouldBeVisible) obj.visible = shouldBeVisible;
        });
        canvas.requestRenderAll();
    };

    const objectsInCategory = (category) => canvas.getObjects().filter(obj => getObjectCategory(obj) === category);

    const collectRenderExcludedObjects = () => {
        const excluded = [];
        const visit = (object) => {
            if (!object) return;
            if (object._excludeFromRender) excluded.push(object);
            object._objects?.forEach(visit);
        };
        canvas.getObjects().forEach(visit);
        return excluded;
    };

    // --- Cara del llavero (delante / detrás) ---
    // Un llavero se puede dibujar por las dos caras. Para pintar por detrás hay
    // que darle la vuelta, y lo que se voltea es **el mundo**: se espeja cada
    // objeto del lienzo sobre el eje vertical de la placa. El viewport no se
    // toca, así que el zoom, el encuadre y el pan siguen funcionando sin
    // enterarse de nada.
    //
    // Lo que se guarda y lo que se exporta están siempre vistos desde delante —
    // la cara canónica —, así que guardar estando del revés desespeja primero y
    // vuelve a espejar después (`withCanonicalFace`), igual que
    // `withAllObjectsVisible` hace con lo oculto. De ahí sale sola la semántica
    // que hace falta: un texto escrito por detrás se guarda espejado, que es
    // exactamente como se lee al mirar el llavero desde delante.
    //
    // El eje se recuerda en el canvas (mismo patrón que `__cotaDisplayMode`) en
    // vez de recalcularse al desandar: así ir y volver deja las cosas donde
    // estaban aunque el fondo haya cambiado de tamaño por el camino.
    const FRONT_FACE = 'front';
    const BACK_FACE = 'back';
    const FACE_GHOST_OPACITY = 0.22;

    const getEditingFace = () => canvas.__customiz3rFace || FRONT_FACE;
    const faceOf = (object) => (object?._drawingSide === BACK_FACE ? BACK_FACE : FRONT_FACE);

    const plateMirrorAxis = () => {
        const background = canvas.getObjects().filter(isBackgroundShapeObject);
        const bounds = getObjectsBounds(background.length ? background : canvas.getObjects());
        return bounds ? bounds.centerX : canvas.getWidth() / 2;
    };

    /** Espeja el objeto sobre una vertical, con la propia utilidad de fabric.
     *
     * `addTransformToObject` premultiplica y vuelve a descomponer, que es lo
     * mismo que hace fabric al desagrupar. Sirve igual para objetos rotados o
     * inclinados, donde poner `flipX` a mano se quedaría corto. */
    const mirrorObjectAbout = (object, axisX) => {
        fabric.util.addTransformToObject(object, [-1, 0, 0, 1, 2 * axisX, 0]);
        object.setCoords();
    };

    /** El gemelo del anterior: espeja sobre una horizontal (arriba↔abajo). */
    const mirrorObjectAboutRow = (object, axisY) => {
        fabric.util.addTransformToObject(object, [1, 0, 0, -1, 0, 2 * axisY]);
        object.setCoords();
    };

    // Las reglas de las cotas son sobreimpresión de la interfaz, no diseño: no
    // se espejan. Si se espejaran saldrían con las etiquetas escritas del revés
    // (y en modo "piezas", además, cada regla acabaría midiendo la pieza del
    // otro lado). `refreshBackgroundRulers` las redibuja enteras, así que basta
    // con dejarlas fuera y regenerarlas al cambiar de cara.
    const isRulerObject = (object) => String(object?.id ?? '').startsWith('background_line')
        || String(object?.id ?? '').startsWith('background_text');

    const mirrorCanvasAbout = (axisX) => {
        canvas.discardActiveObject();
        canvas.getObjects()
            .filter(object => !isRulerObject(object))
            .forEach(object => mirrorObjectAbout(object, axisX));
        canvas.requestRenderAll();
    };

    // Los dibujos de la otra cara se quedan a la vista pero atenuados y fuera de
    // alcance: sirven para alinear y no para tocarlos. Se guarda lo que se pisa
    // porque `opacity` y `selectable` sí viajan en el JSON del diseño, y un
    // fantasma no puede quedarse grabado.
    const applyFaceGhosts = () => {
        const face = getEditingFace();
        canvas.getObjects().forEach((object) => {
            if (getObjectCategory(object) !== 'drawings') return;
            const isGhost = faceOf(object) !== face;
            if (isGhost && !object.__faceGhost) {
                object.__faceGhost = { opacity: object.opacity, selectable: object.selectable };
                object.set({ opacity: FACE_GHOST_OPACITY, selectable: false, evented: false });
            } else if (!isGhost && object.__faceGhost) {
                object.set({
                    opacity: object.__faceGhost.opacity ?? 1,
                    selectable: object.__faceGhost.selectable !== false,
                    evented: true,
                });
                delete object.__faceGhost;
            }
        });
        canvas.requestRenderAll();
    };

    const withGhostsCleared = (fn) => {
        const ghosts = canvas.getObjects().filter(object => object.__faceGhost);
        ghosts.forEach(object => object.set({
            opacity: object.__faceGhost.opacity ?? 1,
            selectable: object.__faceGhost.selectable !== false,
        }));
        try {
            return fn();
        } finally {
            ghosts.forEach(object => object.set({ opacity: FACE_GHOST_OPACITY, selectable: false }));
        }
    };

    /** Deja el lienzo visto desde delante mientras dure `fn`.
     *
     * Espejar exige soltar la selección: los objetos de una `ActiveSelection`
     * cuelgan de ella y no del lienzo, así que transformarlos ahí dentro
     * aplicaría el volteo dos veces. Se anota qué había seleccionado y se
     * devuelve al terminar — guardar no puede dejar al usuario sin selección a
     * media faena. */
    const withCanonicalFace = (fn) => withGhostsCleared(() => {
        if (getEditingFace() === FRONT_FACE) return fn();
        const axis = canvas.__customiz3rFaceAxis ?? plateMirrorAxis();
        const selectedBefore = canvas.getActiveObjects();
        mirrorCanvasAbout(axis);
        try {
            return fn();
        } finally {
            mirrorCanvasAbout(axis);
            restoreSelection(selectedBefore);
        }
    });

    /** Vuelve a seleccionar lo que siguiera vivo en el lienzo. */
    const restoreSelection = (objects) => {
        const alive = objects.filter(object => canvas.getObjects().includes(object));
        if (!alive.length) return;
        canvas.setActiveObject(alive.length === 1
            ? alive[0]
            : new fabric.ActiveSelection(alive, { canvas }));
        canvas.requestRenderAll();
    };

    const setEditingFace = (face) => {
        const next = face === BACK_FACE ? BACK_FACE : FRONT_FACE;
        if (next === getEditingFace()) return next;
        const axis = next === BACK_FACE ? plateMirrorAxis() : (canvas.__customiz3rFaceAxis ?? plateMirrorAxis());
        mirrorCanvasAbout(axis);
        canvas.__customiz3rFaceAxis = next === BACK_FACE ? axis : null;
        canvas.__customiz3rFace = next;
        // Sólo al cambiar de cara de verdad, no en el ida y vuelta de
        // `withCanonicalFace`: ahí las reglas ni se han movido, y añadir y
        // quitar objetos en mitad de una exportación es buscarse problemas.
        refreshBackgroundRulers(canvas);
        applyFaceGhosts();
        canvas.fire('face:changed');
        canvas.fire('layer:changed');
        return next;
    };

    // --- Relieve por dibujo (gestor de capas) ---
    // Dos cotas independientes, en mm: cuánto entra el dibujo dentro del fondo
    // (`_drawingDepthMm`) y cuánto sobresale por encima de la cara superior
    // (`_drawingRaiseMm`). Sin valor propio se hereda del grupo que lo contiene;
    // si tampoco lo tiene, la profundidad cae en `drawingDepthMm` (el parámetro
    // global del panel de parámetros 3D) y el saliente en 0, que es como ha sido
    // siempre.
    //
    // Los números viven en las propiedades del objeto y sólo se escriben en el
    // `id` mientras se exporta el SVG, porque el id es el único canal que
    // sobrevive al viaje entero: editor → Inkscape (`svg_to_area_drawing`, dos
    // pasadas con `--export-plain-svg`) → extractor del backend. Es el mismo
    // canal que ya usan los imanes/NFC (`insert_..._z...`) y la ranura de
    // colgado.
    const DRAWING_RELIEF_TOKEN_RE = /__(dep|out)\d+/gi;
    const DRAWING_FACE_TOKEN_RE = /__back(?![a-z0-9])/gi;
    const DRAWING_OBJECT_TOKEN_RE = /__obj[a-z0-9]{6}/gi;
    const PART_UID_RE = /^[a-z0-9]{6}$/;
    const RELIEF_PROPERTIES = [
        { key: '_drawingDepthMm', token: 'dep' },
        { key: '_drawingRaiseMm', token: 'out' },
    ];

    const resolveReliefValue = (object, key) => (
        Number.isFinite(object?.[key]) ? object[key] : null
    );

    /** El valor heredado del ancestro más cercano que lo tenga fijado. */
    const inheritedReliefValue = (object, key) => {
        let parent = object?.group;
        while (parent) {
            const value = resolveReliefValue(parent, key);
            if (value !== null) return value;
            parent = parent.group;
        }
        return null;
    };

    const newPartUid = () => Math.random().toString(36).slice(2, 8).padEnd(6, '0');

    /**
     * Da a cada dibujo de primer nivel su `_partUid`, y uno nuevo al que lo
     * comparta con otro: copiar y pegar clona la propiedad, y dos objetos con
     * el mismo uid volverían a fundirse en una sola pieza 3D. Se queda el
     * primero en la pila, que es el original.
     */
    const ensurePartUids = () => {
        const seen = new Set();
        canvas.getObjects().forEach((object) => {
            if (getObjectCategory(object) !== 'drawings') return;
            let uid = object._partUid;
            if (!PART_UID_RE.test(uid || '') || seen.has(uid)) {
                do { uid = newPartUid(); } while (seen.has(uid));
                // Asignación directa: no es una edición del usuario.
                object._partUid = uid;
            }
            seen.add(uid);
        });
    };

    const findPartObject = (uid) => canvas.getObjects().find(object => (
        uid && object._partUid === uid && getObjectCategory(object) === 'drawings'
    ));

    /**
     * Qué objetos hay que marcar al exportar, con qué cotas y de qué pieza son.
     *
     * Se marcan todos los dibujos, no sólo los que tienen cotas: todos llevan el
     * uid de su objeto de primer nivel, que los hijos heredan.
     *
     * Se resuelve **antes** de desagrupar (`saveSVG(true)`): al deshacer un
     * grupo desaparece el objeto que llevaba las cotas, y con él la herencia de
     * sus hijos. Los hijos en cambio son los mismos objetos antes y después, así
     * que la lista sigue valiendo una vez desagrupado.
     */
    const collectDrawingReliefTargets = () => {
        ensurePartUids();
        const targets = [];
        const visit = (object, inherited, inheritedBack, uid) => {
            const values = {};
            RELIEF_PROPERTIES.forEach(({ key }) => {
                values[key] = resolveReliefValue(object, key) ?? inherited[key];
            });
            // La cara se hereda igual que las cotas: marcar el grupo manda la
            // cara a todos sus paths.
            const onBack = object?._drawingSide === BACK_FACE || inheritedBack;
            targets.push({ object, values, onBack, uid, index: targets.length + 1 });
            object?._objects?.forEach(child => visit(child, values, onBack, uid));
        };
        const nothing = Object.fromEntries(RELIEF_PROPERTIES.map(({ key }) => [key, null]));
        canvas.getObjects().forEach((object) => {
            if (getObjectCategory(object) !== 'drawings') return;
            visit(object, nothing, false, object._partUid);
        });
        return targets;
    };

    const withDrawingReliefStamped = (targets, fn) => {
        if (!targets.length) return fn();
        const previous = targets.map(({ object }) => [object, object.id]);
        targets.forEach(({ object, values, onBack, uid, index }) => {
            // Asignación directa, no `.set()`: marcar para exportar no puede
            // ensuciar el diseño ni disparar una foto del historial.
            const base = String(object.id || `drawing_${index}`)
                .replace(DRAWING_RELIEF_TOKEN_RE, '')
                .replace(DRAWING_FACE_TOKEN_RE, '')
                .replace(DRAWING_OBJECT_TOKEN_RE, '');
            const withCotas = RELIEF_PROPERTIES.reduce((id, { key, token }) => (
                values[key] === null ? id : `${id}__${token}${Math.round(values[key] * 100)}`
            ), base);
            const withObject = uid ? `${withCotas}__obj${uid}` : withCotas;
            object.id = onBack ? `${withObject}__back` : withObject;
        });
        try {
            return fn();
        } finally {
            previous.forEach(([object, id]) => {
                if (id === undefined) delete object.id;
                else object.id = id;
            });
        }
    };

    const withRenderExcludedObjectsSkipped = (fn) => {
        const excluded = collectRenderExcludedObjects();
        const previous = excluded.map(object => [object, object.excludeFromExport]);
        excluded.forEach(object => { object.excludeFromExport = true; });
        try {
            return fn();
        } finally {
            previous.forEach(([object, excludeFromExport]) => {
                if (excludeFromExport === undefined) delete object.excludeFromExport;
                else object.excludeFromExport = excludeFromExport;
            });
        }
    };

    // Revela temporalmente lo oculto para que un guardado/exportación real
    // (saveJSON/saveSVG) o una foto del historial (saveSnapshot) nunca
    // graben `visible:false` — mismo patrón que el intercambio de vaciado
    // (collectVaciadoObjects) que ya usan saveSVG/saveJSON más abajo.
    const withAllObjectsVisible = (fn) => {
        const hidden = canvas.getObjects().filter(obj => obj.visible === false);
        hidden.forEach(obj => { obj.visible = true; });
        try {
            return fn();
        } finally {
            hidden.forEach(obj => { obj.visible = false; });
        }
    };

    // --- History (undo/redo) ---
    // Single array with a pointer: cleaner than two stacks and supports jumpToSnapshot
    const history = [];      // [{ json, label, face, faceAxis }, ...]
    let historyIndex = -1;
    let isLoadingHistory = false;
    let snapshotTimer = null;
    let pendingLabel = 'Cambio';

    const notifyHistoryChange = () => {
        if (onHistoryChange) {
            onHistoryChange({
                entries: history.map(h => h.label),
                currentIndex: historyIndex,
            });
        }
    };

    const saveSnapshot = (label = pendingLabel) => {
        if (isLoadingHistory) return;
        // Discard any redo entries ahead of current position
        history.splice(historyIndex + 1);
        history.push({
            // La foto se guarda en la cara en la que se tomó, y se anota cuál
            // era. El historial es interno: no tiene por qué estar en la cara
            // canónica como el guardado, y desespejar el lienzo entero aquí
            // salía carísimo — esto corre con debounce tras *cada* edición, y
            // volteaba dos veces todos los objetos por cada tecla, tirando de
            // paso la selección del usuario.
            json: withAllObjectsVisible(() => canvas.toJSON(serializedEditorProperties)),
            label,
            face: getEditingFace(),
            faceAxis: canvas.__customiz3rFaceAxis ?? null,
        });
        if (history.length > 50) history.shift();
        historyIndex = history.length - 1;
        notifyHistoryChange();
    };

    const debouncedSaveSnapshot = (label = 'Cambio') => {
        if (isLoadingHistory) return;
        pendingLabel = label;
        if (snapshotTimer) clearTimeout(snapshotTimer);
        snapshotTimer = setTimeout(() => {
            saveSnapshot(pendingLabel);
            snapshotTimer = null;
        }, 150);
    };

    const loadSnapshot = (entry) => {
        const { json, face = FRONT_FACE, faceAxis = null } = entry || {};
        isLoadingHistory = true;
        // Cada foto vuelve tal y como se tomó, en su cara. Deshacer te devuelve
        // al estado en el que estabas, y eso incluye por qué lado mirabas.
        canvas.__customiz3rFace = face;
        canvas.__customiz3rFaceAxis = faceAxis;
        canvas.remove(...canvas.getObjects());
        // `hiddenObjects` guarda referencias a objetos que este salto de
        // historial va a sustituir por instancias nuevas — y ocultar es una
        // ayuda de la sesión actual, no algo que deba sobrevivir a saltar a
        // otro punto del historial. Cada salto empieza con todo visible.
        hiddenObjects = new Set();
        canvas.loadFromJSON(json, () => {
            canvas.getObjects().forEach(sanitizeObjectIds);
            canvas.getObjects().forEach(object => {
                if (object.type === 'text' || object.type === 'i-text') {
                    canvas.remove(object);
                    const newObj = new fabric.IText(object.text, { ...object });
                    canvas.add(newObj);
                }
            });
            restoreVaciadoPatterns(canvas);
            restoreOperationPatterns(canvas);
            applyFaceGhosts();
            canvas.renderAll();
            canvas.fire('face:changed');
            isLoadingHistory = false;
            notifyHistoryChange();
            refreshCanvasFonts(canvas);
        });
    };

    // --- Helpers para labels descriptivos ---
    const objectTypeLabel = (obj) => {
        if (!obj) return 'Objeto';
        if (obj._vectorizedText)               return 'Texto (vectorizado)';
        if (String(obj?.id ?? '').startsWith('background')) return 'Fondo';
        if (obj.class === 'screw')            return 'Tornillo';
        if (obj.class === 'crocs')            return 'Pin Crocs';
        if (isHoleObject(obj))                return 'Agujero';
        if (isInternalElementObject(obj))      return parseInternalElementId(obj.id)?.kind === 'nfc' ? 'NFC' : 'Imán';
        if (String(obj?.id ?? '').startsWith('drawings_'))  return 'SVG';
        switch (obj.type) {
            case 'i-text':
            case 'text':     return 'Texto';
            case 'rect':     return 'Rectángulo';
            case 'triangle': return 'Triángulo';
            case 'circle':   return 'Círculo';
            case 'group':    return 'Grupo';
            case 'path':     return 'Forma';
            case 'image':    return 'Imagen';
            default:         return 'Objeto';
        }
    };

    const actionLabel = (action) => {
        switch (action) {
            case 'drag':                      return 'movido';
            case 'scale':
            case 'scaleX':
            case 'scaleY':
            case 'resizing':                  return 'redimensionado';
            case 'rotate':                    return 'rotado';
            case 'cornerRadius':              return 'redondeado';
            case 'drawingRelief':             return 'con otro relieve';
            case 'drawingFace':               return 'cambiado de cara';
            case 'mirror':                    return 'espejado';
            case 'skewX':
            case 'skewY':                     return 'inclinado';
            default:                          return 'modificado';
        }
    };

    // Snapshot automático en cada cambio significativo del canvas
    canvas.on('object:added', (e) => {
        // Ignorar objetos de fondo y reglas (se añaden durante setBaseLayout y loadJSON)
        if (String(e.target?.id ?? '').startsWith('background')) return;
        if (e.target?.class === 'drawing_doesnt_move') return;
        // Lo que se dibuja con el llavero del revés nace en la cara de atrás.
        // Sólo lo que dibuja el usuario: durante una carga o un salto de
        // historial los objetos ya traen su cara puesta.
        if (!isLoadingHistory && getEditingFace() === BACK_FACE
            && e.target && getObjectCategory(e.target) === 'drawings'
            && e.target._drawingSide === undefined) {
            e.target._drawingSide = BACK_FACE;
        }
        debouncedSaveSnapshot(`${objectTypeLabel(e.target)} añadido`);
    });

    canvas.on('object:removed', (e) => {
        if (String(e.target?.id ?? '').startsWith('background')) return;
        debouncedSaveSnapshot(`${objectTypeLabel(e.target)} eliminado`);
    });

    canvas.on('object:modified', (e) => {
        debouncedSaveSnapshot(`${objectTypeLabel(e.target)} ${actionLabel(e.action)}`);
    });

    canvas.on('text:changed', () => {
        debouncedSaveSnapshot('Texto editado');
    });
    canvas.on('selection:created', (event) => {
        rememberTextObject(event.selected?.[0] || event.target);
    });
    canvas.on('selection:updated', (event) => {
        rememberTextObject(event.selected?.[0] || event.target);
    });
    // --- End History ---

    // --- Medidas de los agujeros pasantes (C3D-26) ---
    // Sobre el rayado rojo de cada agujero seleccionado, su diámetro (si es
    // redondo) o su ancho × alto. Solo de lo seleccionado: con todos a la vez,
    // en agujeros pequeños la etiqueta tapaba la forma. Se pinta en
    // `after:render` sobre el lienzo principal, no como objeto: así no sale en
    // capas, ni en el SVG, ni en las miniaturas (que también pasan por
    // `renderCanvas`, pero con otro contexto).
    canvas.on('after:render', (event) => {
        const ctx = event?.ctx;
        if (!ctx || ctx !== canvas.contextContainer) return;
        const selected = new Set([...canvas.getActiveObjects(), ...selectedNestedObjects]);
        if (!selected.size) return;
        const labels = holeMeasureLabels(canvas, selected);
        if (!labels.length) return;
        ctx.save();
        ctx.font = '600 12px system-ui, -apple-system, "Segoe UI", sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        labels.forEach(({ text, x, y }) => {
            const width = ctx.measureText(text).width + 12;
            const height = HOLE_LABEL_HEIGHT_PX;
            ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
            ctx.strokeStyle = '#dc2626';
            ctx.lineWidth = 1;
            ctx.beginPath();
            if (ctx.roundRect) ctx.roundRect(x - width / 2, y - height / 2, width, height, 9);
            else ctx.rect(x - width / 2, y - height / 2, width, height);
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = '#b91c1c';
            ctx.fillText(text, x, y + 0.5);
        });
        ctx.restore();
    });

    // --- Guía de la servilleta (servilletero de paredes) ---
    // La silueta de la servilleta doblada, a escala, apoyada en el hombro del
    // perfil de base (o en el borde inferior del fondo si no hay perfil). Es
    // solo una referencia: se pinta en `after:render` como las medidas de los
    // agujeros, así que no llega al SVG, al JSON, a las capas ni a miniaturas.
    canvas.on('after:render', (event) => {
        const ctx = event?.ctx;
        const guide = canvas.__napkinGuide;
        if (!guide || !ctx || ctx !== canvas.contextContainer) return;
        const background = canvas.getObjects().find(isBackgroundShapeObject);
        const bounds = background?.getBoundingRect(true);
        if (!bounds?.width) return;
        // La línea de la base: el borde superior del perfil, o sin perfil la
        // banda por defecto del backend (`tongueHeightMm` desde abajo).
        const joint = canvas.getObjects().find(isNapkinJointObject);
        const bottom = bounds.top + bounds.height;
        const shoulder = joint
            ? joint.getBoundingRect(true).top
            : bottom - (Number(guide.tongueHeightMm) || 8) * PX_PER_MM;
        const marginLine = shoulder - (Number(guide.marginMm) || 0) * PX_PER_MM;
        const width = guide.widthMm * PX_PER_MM;
        const height = guide.heightMm * PX_PER_MM;
        const centerX = bounds.left + bounds.width / 2;
        const vpt = canvas.viewportTransform;
        const px = 1 / vpt[0];   // un píxel de pantalla en unidades del lienzo
        ctx.save();
        ctx.transform(vpt[0], vpt[1], vpt[2], vpt[3], vpt[4], vpt[5]);

        // Zona sin dibujos: del margen hacia abajo, rayada. Lo que se dibuje ahí
        // no se imprime (el backend lo recorta), la pared queda lisa.
        ctx.save();
        ctx.beginPath();
        ctx.rect(bounds.left, marginLine, bounds.width, bottom - marginLine);
        ctx.clip();
        ctx.fillStyle = 'rgba(245, 158, 11, 0.10)';
        ctx.fillRect(bounds.left, marginLine, bounds.width, bottom - marginLine);
        ctx.strokeStyle = 'rgba(217, 119, 6, 0.35)';
        ctx.lineWidth = px;
        const step = 8 * px;
        for (let x = bounds.left - (bottom - marginLine); x < bounds.left + bounds.width; x += step) {
            ctx.beginPath();
            ctx.moveTo(x, bottom);
            ctx.lineTo(x + (bottom - marginLine), marginLine);
            ctx.stroke();
        }
        ctx.restore();

        const label = (text, y) => {
            ctx.font = `600 ${11 * px}px system-ui, -apple-system, "Segoe UI", sans-serif`;
            ctx.textBaseline = 'bottom';
            ctx.fillStyle = 'rgba(180, 83, 9, 0.95)';
            ctx.fillText(text, bounds.left + bounds.width + 6 * px, y);
        };
        ctx.setLineDash([]);
        ctx.lineWidth = 1.5 * px;
        ctx.strokeStyle = 'rgba(217, 119, 6, 0.95)';
        ctx.beginPath();
        ctx.moveTo(bounds.left - 12 * px, shoulder);
        ctx.lineTo(bounds.left + bounds.width + 12 * px, shoulder);
        ctx.stroke();
        label('Base de la servilleta', shoulder);
        if (marginLine < shoulder - 0.5 * px) {
            ctx.setLineDash([4 * px, 3 * px]);
            ctx.lineWidth = px;
            ctx.beginPath();
            ctx.moveTo(bounds.left, marginLine);
            ctx.lineTo(bounds.left + bounds.width, marginLine);
            ctx.stroke();
            label(`Límite de dibujos (−${guide.marginMm} mm)`, marginLine - 2 * px);
        }

        ctx.beginPath();
        if (guide.fold === 'square') {
            ctx.rect(centerX - width / 2, shoulder - height, width, height);
        } else {
            ctx.moveTo(centerX - width / 2, shoulder);
            ctx.lineTo(centerX, shoulder - height);
            ctx.lineTo(centerX + width / 2, shoulder);
            ctx.closePath();
        }
        ctx.fillStyle = 'rgba(148, 163, 184, 0.16)';
        ctx.fill();
        ctx.setLineDash([6 * px, 4 * px]);
        ctx.lineWidth = 1.5 * px;
        ctx.strokeStyle = 'rgba(100, 116, 139, 0.85)';
        ctx.stroke();

        // Cotas de la servilleta: ancho por encima, alto a la derecha, y una
        // etiqueta con las dos medidas dentro de la silueta.
        const formatMm = (mm) => `${Math.round(Number(mm) * 10) / 10}`;
        const tick = 4 * px;
        const dimColor = 'rgba(71, 85, 105, 0.95)';
        ctx.setLineDash([]);
        ctx.lineWidth = px;
        ctx.strokeStyle = dimColor;
        ctx.fillStyle = dimColor;
        ctx.font = `600 ${11 * px}px system-ui, -apple-system, "Segoe UI", sans-serif`;
        const topY = shoulder - height - 10 * px;
        const rightX = centerX + width / 2 + 10 * px;
        ctx.beginPath();
        ctx.moveTo(centerX - width / 2, topY);
        ctx.lineTo(centerX + width / 2, topY);
        ctx.moveTo(centerX - width / 2, topY - tick);
        ctx.lineTo(centerX - width / 2, topY + tick);
        ctx.moveTo(centerX + width / 2, topY - tick);
        ctx.lineTo(centerX + width / 2, topY + tick);
        ctx.moveTo(rightX, shoulder);
        ctx.lineTo(rightX, shoulder - height);
        ctx.moveTo(rightX - tick, shoulder);
        ctx.lineTo(rightX + tick, shoulder);
        ctx.moveTo(rightX - tick, shoulder - height);
        ctx.lineTo(rightX + tick, shoulder - height);
        ctx.stroke();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(`${formatMm(guide.widthMm)} mm`, centerX, topY - 3 * px);
        ctx.save();
        ctx.translate(rightX + 4 * px, shoulder - height / 2);
        ctx.rotate(Math.PI / 2);
        ctx.textBaseline = 'bottom';
        ctx.fillText(`${formatMm(guide.heightMm)} mm`, 0, 0);
        ctx.restore();

        // Cota del largo de la base, por debajo del fondo: la pared más lo que
        // sobresale por cada extremo.
        const overhang = Math.max(Number(guide.baseOverhangMm) || 0, 0) * PX_PER_MM;
        const baseLeft = bounds.left - overhang;
        const baseRight = bounds.left + bounds.width + overhang;
        const baseY = bottom + 14 * px;
        ctx.beginPath();
        ctx.moveTo(baseLeft, baseY);
        ctx.lineTo(baseRight, baseY);
        ctx.moveTo(baseLeft, baseY - tick);
        ctx.lineTo(baseLeft, baseY + tick);
        ctx.moveTo(baseRight, baseY - tick);
        ctx.lineTo(baseRight, baseY + tick);
        if (overhang > 0) {
            // Dónde acaba la pared dentro de la base.
            ctx.moveTo(bounds.left, baseY - tick / 2);
            ctx.lineTo(bounds.left, baseY + tick / 2);
            ctx.moveTo(bounds.left + bounds.width, baseY - tick / 2);
            ctx.lineTo(bounds.left + bounds.width, baseY + tick / 2);
        }
        ctx.stroke();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillText(
            `Base ${formatMm((baseRight - baseLeft) / PX_PER_MM)} mm`,
            (baseLeft + baseRight) / 2,
            baseY + 3 * px,
        );
        ctx.restore();
    });

    /** Coloca el perfil de base a todo lo ancho del borde inferior del fondo. */
    const fitNapkinJointToBackground = (obj, heightMm) => {
        const background = canvas.getObjects().find(isBackgroundShapeObject);
        const bounds = background?.getBoundingRect(true);
        const heightPx = Math.max(Number(heightMm) || 8, 1) * PX_PER_MM;
        if (!bounds?.width) {
            canvas.centerObject(obj);
            obj.setCoords();
            return;
        }
        obj.set({
            originX: 'left', originY: 'top', angle: 0, scaleX: 1, scaleY: 1,
            left: bounds.left, top: bounds.top + bounds.height - heightPx,
            width: bounds.width, height: heightPx,
        });
        obj.setCoords();
    };

    // Con nombre, no `return {…}` a secas: algún método llama a otro.
    const editorApi = {
        canvas,
        addCircle: () => {
            const svg = `
            <svg
               width="70mm"
               height="70mm"
               viewBox="0 0 76.525421 76.525421"
               xmlns="http://www.w3.org/2000/svg"
               xmlns:svg="http://www.w3.org/2000/svg">

                <path
                   style="opacity:1;stroke-width:0;fill:${PRINTABLE_BLACK_FILL_COLOR}"
                   d="m 164.61864,159.27966 c 0,1.32074 -0.0669,2.62586 -0.19755,3.91214 -0.13063,1.28628 -0.32497,2.55372 -0.57981,3.79913 -0.25485,1.2454 -0.5702,2.46877 -0.94286,3.66689 -0.37265,1.19812 -0.80261,2.37099 -1.28665,3.51541 -0.48406,1.14442 -1.0222,2.26039 -1.61123,3.34471 -0.58904,1.08431 -1.22896,2.13697 -1.91657,3.15476 -0.68761,1.0178 -1.42291,2.00073 -2.20267,2.94559 -0.77978,0.94487 -1.60403,1.85166 -2.46955,2.71719 -0.86553,0.86552 -1.77232,1.68977 -2.71719,2.46955 -0.94486,0.77977 -1.92779,1.51506 -2.94559,2.20267 -1.01779,0.68761 -2.07045,1.32753 -3.15476,1.91657 -1.08432,0.58903 -2.20029,1.12717 -3.34471,1.61123 -1.14442,0.48405 -2.3173,0.914 -3.51541,1.28665 -1.19812,0.37266 -2.42149,0.68801 -3.66689,0.94286 -1.24541,0.25484 -2.51285,0.44918 -3.79913,0.57981 -1.28628,0.13063 -2.5914,0.19755 -3.91214,0.19755 -1.32074,0 -2.62586,-0.0669 -3.91214,-0.19755 -1.28628,-0.13063 -2.55372,-0.32497 -3.79913,-0.57981 -1.24541,-0.25485 -2.46877,-0.5702 -3.66689,-0.94286 -1.19812,-0.37265 -2.37099,-0.8026 -3.51541,-1.28665 -1.14442,-0.48406 -2.26039,-1.0222 -3.34471,-1.61123 -1.08431,-0.58904 -2.13697,-1.22896 -3.15477,-1.91657 -1.01779,-0.68761 -2.00072,-1.4229 -2.94559,-2.20267 -0.94486,-0.77977 -1.85166,-1.60402 -2.717184,-2.46955 -0.865523,-0.86552 -1.689773,-1.77232 -2.469545,-2.71719 -0.779771,-0.94486 -1.515065,-1.92779 -2.202673,-2.94559 -0.687609,-1.01779 -1.327533,-2.07045 -1.916568,-3.15476 -0.589034,-1.08432 -1.127179,-2.20029 -1.611229,-3.34471 -0.484049,-1.14442 -0.914004,-2.31729 -1.286658,-3.51541 -0.372654,-1.19812 -0.688006,-2.42149 -0.942853,-3.66689 -0.254847,-1.24541 -0.449188,-2.51285 -0.579817,-3.79913 -0.130629,-1.28628 -0.197546,-2.5914 -0.197546,-3.91214 0,-1.32074 0.06692,-2.62586 0.197546,-3.91214 0.130629,-1.28628 0.32497,-2.55372 0.579816,-3.79913 0.254847,-1.2454 0.5702,-2.46877 0.942854,-3.66689 0.372654,-1.19812 0.802608,-2.37099 1.286658,-3.51541 0.484049,-1.14442 1.022194,-2.26039 1.611229,-3.34471 0.589034,-1.08431 1.228959,-2.13697 1.916567,-3.15476 0.687609,-1.0178 1.422902,-2.00073 2.202673,-2.94559 0.779772,-0.94487 1.604022,-1.85167 2.469546,-2.71719 0.865524,-0.86553 1.772324,-1.68978 2.717184,-2.46955 0.94487,-0.77977 1.9278,-1.51506 2.94559,-2.20267 1.0178,-0.68761 2.07046,-1.32753 3.15477,-1.91657 1.08431,-0.58903 2.20029,-1.12717 3.34471,-1.61123 1.14442,-0.48405 2.31729,-0.914 3.51541,-1.28665 1.19812,-0.37266 2.42148,-0.68801 3.66689,-0.94286 1.24541,-0.25484 2.51285,-0.44918 3.79913,-0.57981 1.28628,-0.13063 2.5914,-0.19755 3.91214,-0.19755 1.32074,0 2.62586,0.0669 3.91214,0.19755 1.28628,0.13063 2.55372,0.32497 3.79913,0.57981 1.2454,0.25485 2.46877,0.5702 3.66689,0.94286 1.19811,0.37265 2.37099,0.8026 3.51541,1.28665 1.14442,0.48406 2.26039,1.0222 3.34471,1.61123 1.08431,0.58904 2.13697,1.22896 3.15476,1.91657 1.0178,0.68761 2.00073,1.4229 2.94559,2.20267 0.94487,0.77978 1.85167,1.60403 2.71719,2.46955 0.86552,0.86553 1.68977,1.77232 2.46955,2.71719 0.77976,0.94486 1.51506,1.92779 2.20267,2.94559 0.68761,1.01779 1.32753,2.07045 1.91657,3.15476 0.58903,1.08432 1.12717,2.20029 1.61123,3.34471 0.48404,1.14442 0.914,2.31729 1.28665,3.51541 0.37266,1.19812 0.68801,2.42149 0.94286,3.66689 0.25484,1.24541 0.44918,2.51285 0.57981,3.79913 0.13063,1.28628 0.19755,2.5914 0.19755,3.91214 z" />
            </svg>
            `;

            fabric.loadSVGFromString(svg, function(objects, options) {
                let obj = fabric.util.groupSVGElements(objects, options);
                obj.name = 'Círculo';
                obj.scaleToWidth(50);
                canvas.add(obj).centerObject(obj).renderAll();
            });
        },
        addRectangle: () => {
            const object = new fabric.Rect({
                ...RECTANGLE,
                left: (canvas.getWidth() / 2) - RECTANGLE.width / 2,
                top: (canvas.getHeight() / 2) - RECTANGLE.height / 2,
                fill: FILL, strokeWidth: 0,
                name: 'Rectángulo',
            })
            canvas.add(object)
        },
        takeSnapshot: () => saveSnapshot('Vista 3D'),
        undo: () => {
            if (historyIndex <= 0) return;
            historyIndex--;
            loadSnapshot(history[historyIndex]);
        },
        redo: () => {
            if (historyIndex >= history.length - 1) return;
            historyIndex++;
            loadSnapshot(history[historyIndex]);
        },
        jumpToSnapshot: (index) => {
            if (index < 0 || index >= history.length || index === historyIndex) return;
            historyIndex = index;
            loadSnapshot(history[historyIndex]);
        },
        addTriangle: (idd) => {
            var TRIANGLEe = {
                left: 100,
                top: 100,
                fill: FILL,
                width: 40,
                height: 40,
                angle: 0,
                strokeUniform: true,
                id: 'triangle_' + idd,
                name: 'Triángulo',
                strokeWidth: 0
            }
            const object = new fabric.Triangle({
                ...TRIANGLEe,
                left: (canvas.getWidth() / 2) - TRIANGLEe.width / 2,
                top: (canvas.getHeight() / 2) - TRIANGLEe.height / 2,
                fill: PRINTABLE_BLACK_FILL_COLOR, stroke: strokeColor,
            })
            canvas.add(object)
        },
        addPolygonShape: (numSides, shapeName) => {
            const radius = 40;
            const points = Array.from({ length: numSides }, (_, i) => {
                const angle = (2 * Math.PI * i / numSides) - Math.PI / 2;
                return { x: radius + radius * Math.cos(angle), y: radius + radius * Math.sin(angle) };
            });
            const object = new fabric.Polygon(points, {
                left: (canvas.getWidth() / 2) - radius,
                top: (canvas.getHeight() / 2) - radius,
                fill: PRINTABLE_BLACK_FILL_COLOR,
                strokeWidth: 0,
                name: shapeName,
            });
            canvas.add(object);
        },
        addStar: () => {
            const outer = 40, inner = 16, n = 5;
            const points = Array.from({ length: n * 2 }, (_, i) => {
                const r = i % 2 === 0 ? outer : inner;
                const angle = (Math.PI * i / n) - Math.PI / 2;
                return { x: outer + r * Math.cos(angle), y: outer + r * Math.sin(angle) };
            });
            const object = new fabric.Polygon(points, {
                left: (canvas.getWidth() / 2) - outer,
                top: (canvas.getHeight() / 2) - outer,
                fill: PRINTABLE_BLACK_FILL_COLOR,
                strokeWidth: 0,
                name: 'Estrella',
            });
            canvas.add(object);
        },
        addDiamond: () => {
            const w = 40, h = 50;
            const points = [
                { x: w, y: 0 },
                { x: w * 2, y: h },
                { x: w, y: h * 2 },
                { x: 0, y: h },
            ];
            const object = new fabric.Polygon(points, {
                left: (canvas.getWidth() / 2) - w,
                top: (canvas.getHeight() / 2) - h,
                fill: PRINTABLE_BLACK_FILL_COLOR,
                strokeWidth: 0,
                name: 'Diamante',
            });
            canvas.add(object);
        },
        addLine: () => {
            const object = new fabric.Line(LINE.points, {
                ...LINE.options, stroke: strokeColor
            })
            canvas.add(object)
        },
        addText: (text, fontFamily = "Bebas Neue", style = {}) => {
            const object = new fabric.IText(text, {
                ...TEXT,
                fill: PRINTABLE_BLACK_FILL_COLOR,
                fontStyle: style.fontStyle || 'normal',
                fontWeight: style.fontWeight || 'normal',
            });
            object.set("fontFamily", fontFamily)
            object.set("fontSize", 45)
            object.set({ text: text })
            const centerObject = () => object.set({
                left: canvas.width / 2 - object.width / 2,
                top: canvas.height / 2 - object.height / 2
            });
            centerObject();
            canvas.add(object);
            canvas.setActiveObject(object);
            lastActiveTextObject = object;
            canvas.renderAll();
            // Si el .ttf no está todavía, el ancho de arriba es el de la fuente
            // de reserva: se vuelve a medir y centrar en cuanto llegue.
            if (!isFontLoaded(fontFamily)) {
                const placedAt = { left: object.left, top: object.top };
                ensureFontsLoaded([fontFamily]).then(() => {
                    if (!canvas.lowerCanvasEl) return;
                    object.initDimensions();
                    // Salvo que el usuario ya lo haya movido en ese intervalo
                    if (object.left === placedAt.left && object.top === placedAt.top) {
                        centerObject();
                    }
                    object.setCoords();
                    canvas.requestRenderAll();
                });
            }
            return object;
        },
        updateText: (text) => {
            const textObject = getActiveTextObject()
            if (textObject) {
                applyTextFormatting(textObject, { text }, { commit: true })
            }
        },
        deleteAll: () => {
            canvas.getObjects().forEach((object) => canvas.remove(object))
            canvas.discardActiveObject()
            canvas.renderAll()
        },
        deleteSelected: () => {
            // Con una selección anidada (una pieza elegida dentro de un grupo
            // o de un SVG desde el panel de capas) lo que se quiere borrar es
            // esa pieza, no el grupo entero que la contiene.
            if (selectedNestedObjects.size > 0) {
                deleteNestedSelection()
                return
            }
            canvas.getActiveObjects().forEach((object) => canvas.remove(object))
            canvas.discardActiveObject()
            canvas.renderAll()
        },
        fillColor, strokeColor, setFillColor: (fill) => {
            _setFillColor(fill)
            canvas.getActiveObjects().forEach((object) => object.set({ fill }))
            canvas.renderAll()
        },
        setStrokeColor: (stroke) => {
            _setStrokeColor(stroke)
            canvas.getActiveObjects().forEach((object) => {
                if (object.type === TEXT.type) {
                    object.set({ fill: stroke })
                    return
                }
                object.set({ stroke })
            })
            canvas.renderAll()
        },
        zoomIn: () => {
            zoomCanvasBy(1 / scaleStep)
        },
        zoomOut: () => {
            zoomCanvasBy(scaleStep)
        },
        // Zoom absoluto (1 = 100 %) sobre el centro del lienzo, para el chip
        // del dock de vista: leerlo, fijarlo y saber hasta dónde llega.
        getZoom: () => canvas.getZoom(),
        setZoom: (zoom) => {
            zoomCanvasToPoint(new fabric.Point(canvas.getWidth() / 2, canvas.getHeight() / 2), zoom);
        },
        getZoomLimits: () => ({ min: MIN_CANVAS_ZOOM, max: MAX_CANVAS_ZOOM }),
        resetViewport,
        panViewport: (deltaX, deltaY) => {
            canvas.relativePan(new fabric.Point(deltaX, deltaY));
            canvas.requestRenderAll();
        },
        saveSVG: (ungroupOrNot = false, { forOffset = false } = {}) => withCanonicalFace(() => withAllObjectsVisible(() => withRenderExcludedObjectsSkipped(() => {
            // Intercambiar patrones de vaciado → color real antes de exportar SVG
            const vaciadoList = collectVaciadoObjects(canvas);
            vaciadoList.forEach(({ obj }) => obj.set({ fill: VACIADO_FILL_COLOR }));
            const operationList = collectOperationPreviewObjects(canvas);
            operationList.forEach(({ obj, fill }) => obj.set({ fill }));
            // Antes de desagrupar: al deshacer un grupo se pierde de quién
            // heredaba sus cotas cada hijo.
            const reliefTargets = collectDrawingReliefTargets();

            let svg;
            if (!ungroupOrNot) {
                svg = withDrawingReliefStamped(reliefTargets, () => forOffset
                    ? exportOffsetSVG(canvas)
                    : canvas.toSVG({ suppressPreamble: true }));
            } else {
                // Exclude drawing_doesnt_move objects: they export correctly as-is (as <g> groups)
                // and setActiveObject fails on them in Fabric v6 with selectable=false
                const objects = canvas.getObjects().filter(obj =>
                    obj.id &&
                    !String(obj?.id ?? '').startsWith('background') &&
                    obj.class !== 'drawing_doesnt_move' &&
                    !obj._excludeFromRender
                );
                // Exportar no puede dejar rastro en el lienzo del usuario, y
                // dejaba dos: los grupos deshechos y todo sin poder
                // seleccionarse. Se anota lo que se va a tocar y se devuelve.
                const capturedGroups = captureGroups();
                const wasSelectable = objects.map(object => [object, object.selectable]);
                const activeBefore = canvas.getActiveObject();

                objects.forEach((object) => {
                    canvas.setActiveObject(object);
                    canvas.getActiveObject().set('selectable', false);
                });
                ungroupActiveObjects(canvas);
                // Deshacer la selección activa antes de exportar y de
                // recomponer. `toActiveSelection()` deja a los hijos con
                // coordenadas *relativas a la selección* y con `object.group`
                // apuntando a ella; descartarla es lo que les devuelve las
                // absolutas. Hacerlo después significaba escribirle esas
                // coordenadas encima a objetos que ya estaban dentro del grupo
                // rehecho, y de ahí que el diseño saliera desplazado.
                canvas.discardActiveObject();
                svg = withDrawingReliefStamped(reliefTargets, () => canvas.toSVG({ suppressPreamble: true }));

                restoreGroups(capturedGroups);
                asGroupRestore(() => {
                    wasSelectable.forEach(([object, selectable]) => {
                        if (canvas.getObjects().includes(object)) object.set({ selectable });
                    });
                    if (activeBefore && canvas.getObjects().includes(activeBefore)) {
                        canvas.setActiveObject(activeBefore);
                    }
                });
            }

            // Restaurar patrones de vaciado
            vaciadoList.forEach(({ obj, pattern }) => obj.set({ fill: pattern }));
            operationList.forEach(({ obj, pattern }) => obj.set({ fill: pattern }));

            return svg;
        }))),
        saveSVGOjectsSelect: () => {
            const activeObject = canvas.getActiveObject();
            if (!activeObject) {
                return '';
            }
            if (!(activeObject instanceof fabric.ActiveSelection)) {
                return `
                <svg xmlns="http://www.w3.org/2000/svg">
                  ${activeObject.toSVG()}
                </svg>
                `;
            }
            const activeSelection = activeObject;
            const svgObjects = activeSelection._objects.map(obj => obj.toSVG());
            return `
                <svg xmlns="http://www.w3.org/2000/svg">
                  ${svgObjects.join('\n')}
                </svg>
                `;
        },
        saveJSON: () => withCanonicalFace(() => withAllObjectsVisible(() => {
            // Intercambiar patrones de vaciado → color real para que el JSON sea limpio
            const vaciadoList = collectVaciadoObjects(canvas);
            vaciadoList.forEach(({ obj }) => obj.set({ fill: VACIADO_FILL_COLOR }));
            const operationList = collectOperationPreviewObjects(canvas);
            operationList.forEach(({ obj, fill }) => obj.set({ fill }));

            // Antes del JSON: si el uid naciera al exportar el SVG, el diseño
            // guardado no lo llevaría y al recargar la malla perdería su objeto.
            ensurePartUids();
            const json = canvas.toJSON(serializedEditorProperties);

            // Restaurar patrones
            vaciadoList.forEach(({ obj, pattern }) => obj.set({ fill: pattern }));
            operationList.forEach(({ obj, pattern }) => obj.set({ fill: pattern }));
            return json;
        })),
        copy2: async () => {
            const activeObject = canvas.getActiveObject();
            if (!activeObject) return false;

            clipboardObject = await cloneFabricObject(activeObject);
            clipboardPasteCount = 0;
            _setClipboard?.(clipboardObject);
            return true;
        },
        paste2: async () => {
            if (!clipboardObject) return false;

            const clonedObject = await cloneFabricObject(clipboardObject);
            clipboardPasteCount += 1;
            offsetClonedObject(clonedObject, 20 * clipboardPasteCount);

            canvas.discardActiveObject();

            if (clonedObject.type === 'activeSelection') {
                clonedObject.canvas = canvas;
                clonedObject.forEachObject((object) => {
                    canvas.add(object);
                });
                clonedObject.setCoords();
                canvas.setActiveObject(clonedObject);
            } else {
                canvas.add(clonedObject);
                canvas.setActiveObject(clonedObject);
            }

            canvas.requestRenderAll();
            return true;
        },
        setBaseLayout: (svg, widthMm, heightMm) => {
            const backgroundObjects = canvas.getObjects().filter(obj => obj.id && String(obj?.id ?? '').startsWith('background'));
            backgroundObjects.forEach(obj => canvas.remove(obj));
            const dontMoveObjects = canvas.getObjects().filter(obj => obj.class === 'drawing_doesnt_move');
            dontMoveObjects.forEach(obj => canvas.remove(obj));
            getBaseLayoutToCanvas(svg, canvas, widthMm, heightMm)
        },
        setBaseLayoutOffset: async (svg, widthMm, heightMm) => {
            // Parse first, so an invalid/empty response leaves the design intact.
            const background = await loadOffsetBackground(svg);
            const backgroundObjects = canvas.getObjects().filter(obj => obj.id && String(obj?.id ?? '').startsWith('background'));
            const previousBackgroundColor = getObjectFillColor(
                backgroundObjects.find(object => object.id === 'background'),
            );
            backgroundObjects.forEach(obj => canvas.remove(obj));
            applyFillColor(background, previousBackgroundColor || '#ffffff');
            canvas.add(background);
            pinFloorLayers(canvas);
            refreshBackgroundRulers(canvas, widthMm, heightMm);
            canvas.renderAll();
        },
        /**
         * SVG para el offset/fill "como dibujo": solo lo seleccionado, en
         * coordenadas del lienzo, o null si no hay selección (entonces se usa
         * el diseño entero, igual que para el fondo).
         *
         * Lo no seleccionado se excluye de la exportación en vez de armar un
         * SVG aparte, para que pase por lo mismo que el offset del fondo
         * (vaciados, relieve, texto). La selección se suelta mientras dura:
         * dentro de una `ActiveSelection` las coordenadas son relativas a ella.
         */
        saveSelectionSVGForOffset: () => {
            const selected = canvas.getActiveObjects()
                .filter(object => !isBackgroundObject(object) && !isFixedObject(object));
            if (!selected.length) return null;
            const keep = new Set(selected);
            const activeBefore = canvas.getActiveObject();
            canvas.discardActiveObject();
            const others = canvas.getObjects().filter(object => !keep.has(object));
            const previous = others.map(object => [object, object.excludeFromExport]);
            others.forEach(object => { object.excludeFromExport = true; });
            try {
                return { svg: editorApi.saveSVG(false, { forOffset: true }), objects: selected };
            } finally {
                previous.forEach(([object, value]) => { object.excludeFromExport = value; });
                if (activeBefore) {
                    canvas.setActiveObject(selected.length > 1
                        ? new fabric.ActiveSelection(selected, { canvas })
                        : selected[0]);
                }
                canvas.requestRenderAll();
            }
        },

        /**
         * Añade el resultado de un offset/fill como dibujo, no como fondo.
         *
         * Va justo por debajo del más bajo de `belowObjects` (lo que se usó para
         * calcularlo), para que haga de borde alrededor; sin ellos, justo encima
         * del fondo. El color contrasta con el fondo para que se vea al momento.
         */
        addOffsetDrawing: async (svg, belowObjects = []) => {
            const shape = await loadOffsetBackground(svg);
            const backgroundColor = getObjectFillColor(canvas.getObjects().find(isBackgroundObject));
            const dark = isDarkColor(backgroundColor || '#ffffff');
            applyFillColor(shape, dark ? '#ffffff' : '#000001');
            const used = canvas.getObjects()
                .map(object => /^offset_drawing_(\d+)$/.exec(String(object.id || ''))?.[1])
                .filter(Boolean)
                .map(Number);
            shape.set({
                id: `offset_drawing_${used.length ? Math.max(...used) + 1 : 1}`,
                selectable: true,
                evented: true,
                hoverCursor: 'move',
            });
            const objects = canvas.getObjects();
            const indexes = belowObjects.map(object => objects.indexOf(object)).filter(index => index >= 0);
            canvas.add(shape);
            if (indexes.length) canvas.moveTo(shape, Math.min(...indexes));
            pinFloorLayers(canvas);
            canvas.setActiveObject(shape);
            canvas.requestRenderAll();
            return shape;
        },

        setSizeLayout: (width, height, options = {}) => {
            const newWidth = parseFloat(width);
            const newHeight = parseFloat(height);
            // null dimensions mean "not set" for a new or legacy design. Do
            // not pass them to the historical resize helper, whose fallback
            // value is 1px and would collapse the background on open.
            if (!Number.isFinite(newWidth) || !Number.isFinite(newHeight)
                || newWidth <= 0 || newHeight <= 0) return;
            const fixedLayoutState = captureObjectsLayoutState(canvas, obj => obj.class === 'drawing_doesnt_move');
            const drawingsLayoutState = options.includeDrawings
                ? captureObjectsLayoutState(canvas, obj =>
                    !String(obj?.id || '').startsWith('background')
                    && obj.class !== 'drawing_doesnt_move'
                )
                : null;
            const backgroundObjects = canvas.getObjects().filter(obj => obj.id && String(obj?.id ?? '').startsWith('background') && obj.id !== 'background');
            backgroundObjects.forEach(obj => {
                canvas.remove(obj);
            });
            const resizedBackground = resizeObjectToDimensions('background', newWidth, newHeight, canvas);
            resizeObjectsWithLayoutState(canvas, resizedBackground, fixedLayoutState);
            resizeObjectsWithLayoutState(canvas, resizedBackground, drawingsLayoutState);
            pinFloorLayers(canvas);
            canvas.renderAll()
        },
        // 'conjunto' (una regla para todo el fondo) | 'piezas' (una regla
        // por pieza suelta) | 'ninguna' (sin cotas, para cuando estorban).
        setCotaDisplayMode: (mode) => {
            canvas.__cotaDisplayMode = mode;
            refreshBackgroundRulers(canvas);
        },
        getCotaDisplayMode: () => canvas.__cotaDisplayMode || 'conjunto',
        // Piezas sueltas detectadas en el fondo, para listarlas en el dock.
        getBackgroundPieces: () => getBackgroundClusters(canvas).map((cluster, index) => ({
            index,
            widthMm: cluster.bounds.width / PX_PER_MM,
            heightMm: cluster.bounds.height / PX_PER_MM,
        })),
        // Redimensiona solo una pieza suelta del fondo (por su índice en
        // `getBackgroundPieces`), alrededor de su propio centro — no toca el
        // resto del fondo, los dibujos ni las capas fijas. Como los hijos del
        // grupo de fondo guardan coordenadas relativas al grupo, se sacan
        // primero a coordenadas absolutas (mismo patrón que
        // `groupCenterAndUngroupObjects`: `toActiveSelection` + reagrupar).
        resizeBackgroundPiece: (index, widthMm, heightMm) => {
            const backgroundGroup = canvas.getObjects().find(obj => obj.id === 'background');
            if (!backgroundGroup) return false;

            const clusters = getBackgroundClusters(canvas);
            const cluster = clusters[index];
            if (!cluster) return false;

            const newWidthPx = parseFloat(widthMm) * PX_PER_MM;
            const newHeightPx = parseFloat(heightMm) * PX_PER_MM;
            if (!Number.isFinite(newWidthPx) || !Number.isFinite(newHeightPx) || newWidthPx <= 0 || newHeightPx <= 0) {
                return false;
            }

            const scaleX = newWidthPx / cluster.bounds.width;
            const scaleY = newHeightPx / cluster.bounds.height;
            const clusterCenterX = cluster.bounds.left + cluster.bounds.width / 2;
            const clusterCenterY = cluster.bounds.top + cluster.bounds.height / 2;
            const scaleChild = (child) => {
                const center = child.getCenterPoint();
                const newCenter = new fabric.Point(
                    clusterCenterX + (center.x - clusterCenterX) * scaleX,
                    clusterCenterY + (center.y - clusterCenterY) * scaleY,
                );
                child.set({
                    scaleX: child.scaleX * scaleX,
                    scaleY: child.scaleY * scaleY,
                });
                child.setPositionByOrigin(newCenter, 'center', 'center');
                child.setCoords();
            };

            // Con una única pieza el fondo no está envuelto en grupo
            // (`fabric.util.groupSVGElements` devuelve el objeto suelto
            // cuando el SVG solo trae una forma) — nada que sacar/reagrupar.
            if (!backgroundGroup._objects?.length) {
                scaleChild(backgroundGroup);
                backgroundGroup.setCoords();
            } else {
                backgroundGroup.toActiveSelection();
                canvas.discardActiveObject();
                const children = [...backgroundGroup._objects];
                canvas.add(...children);
                backgroundGroup._restoreObjectsState();
                canvas.remove(backgroundGroup);

                const clusterObjects = new Set(cluster.objects);
                children.forEach((child) => {
                    if (clusterObjects.has(child)) scaleChild(child);
                });

                const regrouped = new fabric.Group(children, {
                    selectable: false,
                    evented: false,
                    hoverCursor: 'default',
                    id: 'background',
                });
                canvas.add(regrouped);
                regrouped.setCoords();
            }

            pinFloorLayers(canvas);
            refreshBackgroundRulers(canvas);
            canvas.requestRenderAll();
            canvas.fire('object:modified');
            return true;
        },
        setColorBaseLayout: (color) => {
            // El fondo es un `fabric.Group` en cuanto el layout tiene más de una
            // figura (y siempre, después de un Offset/Fill). Fabric **no**
            // propaga el `fill` de un grupo a sus hijos: ponerlo ahí dejaba la
            // propiedad escrita en el JSON y el lienzo igual que estaba, que es
            // el "no puedo cambiar el color del fondo" que se veía en diseños
            // de varias piezas. `applyFillColor` baja hasta las hojas — el mismo
            // helper que ya usa `setBaseLayoutOffset` para conservar el color al
            // recalcular el contorno.
            //
            // Con `isBackgroundShapeObject` en vez del filtro a mano quedan
            // fuera las reglas de medida (`background_line`/`background_text`),
            // que llevan prefijo de fondo pero no son la pieza.
            canvas.getObjects()
                .filter(isBackgroundShapeObject)
                .forEach(object => applyFillColor(object, color));
            canvas.renderAll()
        },
        // `objectUid` limita el cambio a un objeto (y sus hijos): es lo que
        // pide el panel de colores 3D al repintar una pieza suelta.
        replaceFillColor: (fromColor, toColor, { backgroundOnly = false, historyLabel = null, objectUid = null } = {}) => {
            const normalizedFrom = normalizeHexColor(fromColor);
            const normalizedTo = normalizeHexColor(toColor);
            if (!normalizedFrom || !normalizedTo) return 0;
            const roots = objectUid ? [findPartObject(objectUid)].filter(Boolean) : canvas.getObjects();
            if (objectUid && !roots.length) return 0;

            let changed = 0;
            const updateObject = (object, inheritedBackground = false) => {
                const isBackground = inheritedBackground || Boolean(String(object?.id ?? '').startsWith('background'));
                if (object._objects) {
                    object._objects.forEach(child => updateObject(child, isBackground));
                    return;
                }
                if (String(object?.id ?? '').startsWith('hole') || isBackground !== backgroundOnly) return;

                const objectColor = normalizeHexColor(object._vaciadoFill || object.fill);
                if (objectColor === normalizedFrom) {
                    delete object._vaciadoFill;
                    object.set({ fill: normalizedTo });
                    changed += 1;
                }
            };

            roots.forEach(object => updateObject(object));
            if (changed) {
                canvas.requestRenderAll();
                if (historyLabel) saveSnapshot(historyLabel);
            }
            return changed;
        },
        /** Nombre legible del objeto de una pieza 3D, o null si ya no está. */
        getPartObjectLabel: (uid) => {
            const object = findPartObject(uid);
            if (!object) return null;
            if (isTextObject(object)) {
                const text = String(object.text || '').replace(/\s+/g, ' ').trim();
                if (text) return text.length > 24 ? `${text.slice(0, 23)}…` : text;
            }
            if (object.name) return String(object.name);
            if (object.type === 'group') return 'Grupo';
            if (object.type === 'path') return 'Dibujo';
            return 'Forma';
        },
        setFontFamily: (fontFamily) => {
            const textObject = getActiveTextObject();
            if (!textObject) return;

            const apply = () => {
                applyTextFormatting(textObject, { fontFamily }, { commit: true });
            };

            apply();
            // Primera vez que se usa esta fuente: se aplica ya (con la de
            // reserva) y se vuelve a aplicar cuando el .ttf está descargado.
            if (!isFontLoaded(fontFamily)) {
                ensureFontsLoaded([fontFamily]).then(() => {
                    if (canvas.lowerCanvasEl) apply();
                });
            }
        },
        setTextStyle: ({ fontStyle, fontWeight } = {}) => {
            const textObject = getActiveTextObject();
            if (!textObject) return;
            applyTextFormatting(textObject, { fontStyle, fontWeight }, { commit: true });
        },
        previewText: ({ text, fontFamily, fontStyle, fontWeight } = {}) => {
            const textObject = getActiveTextObject();
            return applyTextFormatting(textObject, { text, fontFamily, fontStyle, fontWeight });
        },
        restoreTextPreview: ({ text, fontFamily, fontStyle, fontWeight } = {}) => {
            const textObject = getActiveTextObject();
            return applyTextFormatting(textObject, { text, fontFamily, fontStyle, fontWeight });
        },
        addSVG: (media) => {
            if (media) {
                addImageToCanvas(media, canvas)
            }
        },
        onCanvasResize: () => {
        },
        loadJSON: (json) => {
            isLoadingHistory = true;
            return new Promise((resolve) => {
            // Un diseño se abre siempre por la cara de delante, que es la cara
            // en la que está guardado.
            canvas.__customiz3rFace = FRONT_FACE;
            canvas.__customiz3rFaceAxis = null;
            canvas.remove(...canvas.getObjects());
            // Abrir un diseño siempre empieza con todo visible: ocultar es
            // solo de la sesión de edición, nunca se guarda con el diseño.
            hiddenObjects = new Set();
            // Pedir ya los .ttf que usa el diseño: fabric mide y pinta en un
            // canvas, que no dispara la descarga de una fuente (ver fonts.js).
            // No se espera aquí para no retrasar el lienzo — cuando lleguen,
            // refreshCanvasFonts vuelve a medir y repinta.
            ensureFontsLoaded(collectFontFamiliesFromJSON(json));
            canvas.loadFromJSON(json, () => {
                canvas.getObjects().forEach(sanitizeObjectIds);
                canvas.getObjects().forEach(object => {
                    if (object.type === 'text' || object.type === 'i-text') {
                        canvas.remove(object);
                        const newObject = new fabric.IText(object.text, { ...object });
                        canvas.add(newObject);
                    }
                });
                // Restaurar patrón de vaciado en objetos que lo tenían guardado
                restoreVaciadoPatterns(canvas);
                restoreOperationPatterns(canvas);
                groupCenterAndUngroupObjects(canvas);
                regroupAssemblyPieces();
                applyFaceGhosts();
                canvas.renderAll();
                isLoadingHistory = false;
                saveSnapshot('Estado inicial');
                refreshCanvasFonts(canvas);
                resolve();
            });
            });
        },
        bindEvent(eventName, handler) {
            this.canvas.on(eventName, handler);
        },
        getSelectedObjects: () => {
            return canvas.getActiveObjects();
        },
        getLayerSelectedObjects: () => {
            return selectedNestedObjects.size > 0
                ? [...selectedNestedObjects]
                : canvas.getActiveObjects();
        },
        // Ocultar/mostrar por objeto suelto o por categoría entera (Fondo /
        // Agujeros / Fijo / Dibujos), desde el gestor de capas. Ver el bloque
        // "Visibilidad por objeto/categoría" más arriba para el porqué del
        // diseño (no debe afectar guardado, exportación ni historial).
        setObjectVisible: (object, visible) => {
            if (!object) return;
            if (visible) hiddenObjects.delete(object); else hiddenObjects.add(object);
            applyObjectVisibility();
            if (!visible && canvas.getActiveObjects().includes(object)) {
                canvas.discardActiveObject();
                canvas.renderAll();
            }
            canvas.fire('layer:visibility-changed');
        },
        setObjectRenderExcluded: (object, excluded) => {
            if (!object) return;
            object.set({ _excludeFromRender: Boolean(excluded) });
            canvas.requestRenderAll();
            canvas.fire('layer:changed');
        },
        isObjectRenderExcluded: (object) => Boolean(object?._excludeFromRender),
        /**
         * Fija una cota de relieve de un dibujo, en mm.
         *
         * `which` es `'depth'` (cuánto entra) o `'raise'` (cuánto sobresale), y
         * `null` la quita para que el objeto vuelva a heredar — de su grupo, o
         * del valor por defecto. A diferencia de ocultar o excluir del render,
         * esto sí cambia la pieza que se fabrica: dispara `object:modified` para
         * que el diseño quede sin guardar y entre en el historial.
         */
        setObjectDrawingRelief: (object, which, mm) => {
            if (!object) return;
            const property = which === 'raise' ? '_drawingRaiseMm' : '_drawingDepthMm';
            const value = Number(mm);
            object.set({ [property]: Number.isFinite(value) ? value : null });
            canvas.fire('object:modified', { target: object, action: 'drawingRelief' });
            canvas.fire('layer:changed');
        },
        // --- Cara del llavero ---
        getEditingFace,
        /** Da la vuelta al llavero para pintar por la otra cara. */
        setEditingFace,
        /** `'front'` | `'back'` — en qué cara vive este dibujo. */
        getObjectDrawingFace: (object) => faceOf(object),
        /**
         * Manda una capa a la otra cara sin moverla de sitio.
         *
         * Se queda donde está pero cambia de mano: lo que se lee bien por una
         * cara se lee del revés por la otra, así que se espeja sobre su **propio**
         * centro, no sobre el de la placa.
         */
        setObjectDrawingFace: (object, face) => {
            if (!object) return;
            const next = face === BACK_FACE ? BACK_FACE : FRONT_FACE;
            if (next === faceOf(object)) return;
            mirrorObjectAbout(object, object.getCenterPoint().x);
            object.set({ _drawingSide: next === BACK_FACE ? BACK_FACE : null });
            applyFaceGhosts();
            canvas.fire('object:modified', { target: object, action: 'drawingFace' });
            canvas.fire('layer:changed');
        },
        /**
         * `{ depth: {own, inherited}, raise: {own, inherited} }`, en mm.
         *
         * `own` es lo que se fijó en este objeto; `inherited`, lo que le llega
         * del grupo que lo contiene. Ninguno de los dos cae en el valor por
         * defecto: eso lo decide quien pinta el panel, que es quien lo conoce.
         */
        getObjectDrawingRelief: (object) => ({
            depth: {
                own: resolveReliefValue(object, '_drawingDepthMm'),
                inherited: inheritedReliefValue(object, '_drawingDepthMm'),
            },
            raise: {
                own: resolveReliefValue(object, '_drawingRaiseMm'),
                inherited: inheritedReliefValue(object, '_drawingRaiseMm'),
            },
        }),
        /** Mostrar/ocultar de golpe todas las capas de una cara del llavero.
         *
         * El fondo queda fuera: es la placa, la comparten las dos caras y
         * esconderla al ocultar una no tendría sentido. Mismo criterio que el
         * gestor de capas, que tampoco la lista. */
        setFaceVisible: (face, visible) => {
            const target = face === BACK_FACE ? BACK_FACE : FRONT_FACE;
            const objects = canvas.getObjects()
                .filter(object => !isBackgroundObject(object) && faceOf(object) === target);
            objects.forEach(object => {
                if (visible) hiddenObjects.delete(object); else hiddenObjects.add(object);
            });
            applyObjectVisibility();
            if (!visible && canvas.getActiveObjects().some(object => objects.includes(object))) {
                canvas.discardActiveObject();
                canvas.renderAll();
            }
            canvas.fire('layer:visibility-changed');
        },
        setCategoryVisible: (category, visible) => {
            const objects = objectsInCategory(category);
            objects.forEach(object => {
                if (visible) hiddenObjects.delete(object); else hiddenObjects.add(object);
            });
            applyObjectVisibility();
            if (!visible && canvas.getActiveObjects().some(obj => objects.includes(obj))) {
                canvas.discardActiveObject();
                canvas.renderAll();
            }
            canvas.fire('layer:visibility-changed');
        },
        soloCategory: (category) => {
            canvas.getObjects().forEach(object => {
                if (getObjectCategory(object) === category) hiddenObjects.delete(object);
                else hiddenObjects.add(object);
            });
            canvas.discardActiveObject();
            applyObjectVisibility();
            canvas.renderAll();
            canvas.fire('layer:visibility-changed');
        },
        showAllObjects: () => {
            hiddenObjects = new Set();
            applyObjectVisibility();
            canvas.fire('layer:visibility-changed');
        },
        isObjectVisible: (object) => !hiddenObjects.has(object),
        getHiddenObjects: () => new Set(hiddenObjects),
        getObjectCategory,
        selectObject: (object, additive = false) => {
            selectedNestedObjects = new Set();
            if (!additive) {
                canvas.setActiveObject(object);
            } else {
                const selected = canvas.getActiveObjects();
                const next = selected.includes(object)
                    ? selected.filter(item => item !== object)
                    : [...selected, object];

                canvas.discardActiveObject();
                if (next.length === 1) {
                    canvas.setActiveObject(next[0]);
                } else if (next.length > 1) {
                    canvas.setActiveObject(new fabric.ActiveSelection(next, { canvas }));
                }
            }
            canvas.renderAll();
            canvas.fire('layer:nested-selection');
        },
        selectNestedObject: (object, rootGroup, additive = false) => {
            if (!object || !rootGroup) return;
            selectingNestedObject = true;
            canvas.setActiveObject(rootGroup);
            selectingNestedObject = false;
            const next = additive ? new Set(selectedNestedObjects) : new Set();
            if (additive && next.has(object)) {
                next.delete(object);
            } else {
                next.add(object);
            }
            selectedNestedObjects = next;
            if (selectedNestedObjects.size === 0) {
                canvas.discardActiveObject();
            }
            canvas.renderAll();
            canvas.fire('layer:nested-selection', {
                target: object,
                rootGroup,
                selected: [...selectedNestedObjects],
            });
        },
        setColor: (color) => {
            const isVaciado = color === VACIADO_FILL_COLOR;
            const objects = selectedNestedObjects.size > 0
                ? [...selectedNestedObjects]
                : canvas.getActiveObjects();
            let changed = false;
            objects.forEach((object) => {
                changed = objectHasDifferentFill(object, color) || changed;
                if (isVaciado) {
                    applyVaciadoToObject(object);
                } else {
                    removeVaciadoFromObject(object, color);
                }
            });
            canvas.renderAll();
            canvas.fire('layer:changed');
            if (changed) {
                const targetLabel = objects.length === 1 ? objectTypeLabel(objects[0]) : 'Objetos';
                saveSnapshot(`Color de ${targetLabel} cambiado`);
            }
        },

        setHoleOperation: () => {
            const objects = selectedNestedObjects.size > 0
                ? [...selectedNestedObjects]
                : canvas.getActiveObjects();
            if (!objects.length) return 0;
            const counter = { value: nextCustomHoleIndex() };
            objects.forEach(object => applyHoleOperation(object, counter));
            canvas.requestRenderAll();
            canvas.fire('layer:changed');
            saveSnapshot(objects.length === 1 ? 'Objeto convertido en agujero' : 'Objetos convertidos en agujero');
            return objects.length;
        },

        /**
         * Estado del redondeo de los rectángulos seleccionados, en mm.
         *
         * Devuelve `null` cuando la selección no tiene ningún rectángulo, que
         * es lo que el panel usa para pedir que se seleccione uno. Con varios
         * seleccionados se enseña el radio menor y el máximo que los admite a
         * todos, para que subir el valor nunca deforme a ninguno.
         */
        getRectRadius: () => {
            const rects = getSelectedRects();
            if (rects.length === 0) return null;

            const radii = rects.map(visibleRadiusPx);
            const maxRadius = Math.min(...rects.map(maxRadiusPx));
            return {
                count: rects.length,
                radiusMm: Math.min(...radii) / PX_PER_MM,
                maxRadiusMm: maxRadius / PX_PER_MM,
                mixed: Math.max(...radii) - Math.min(...radii) > 0.01,
            };
        },

        /**
         * Redondea las esquinas de los rectángulos seleccionados.
         *
         * El radio se pide en mm de lo que se ve, y se divide por la escala de
         * cada eje al guardarlo: si no, un rectángulo estirado enseñaría
         * esquinas ovaladas en vez del redondeo pedido.
         */
        setRectRadius: (radiusMm) => {
            const rects = getSelectedRects();
            if (rects.length === 0) return null;

            const requestedPx = Math.max(0, (Number(radiusMm) || 0) * PX_PER_MM);
            rects.forEach(rect => {
                const scale = rectScaling(rect);
                const radiusPx = Math.min(requestedPx, maxRadiusPx(rect));
                rect.set({ rx: radiusPx / scale.x, ry: radiusPx / scale.y });
                rect.setCoords();
            });
            canvas.requestRenderAll();
            // Es la vía por la que el diseño queda "sin guardar" y entra en el
            // historial; el snapshot ya viene con debounce, así que arrastrar
            // el deslizador no llena la lista de entradas.
            canvas.fire('object:modified', { target: rects[0], action: 'cornerRadius' });
            return Math.min(...rects.map(visibleRadiusPx)) / PX_PER_MM;
        },
        setHole: (activeHole) => {
            if (activeHole) {
                var str_svg = `
                <svg
   width="12.677477mm"
   height="12.677477mm"
   viewBox="0 0 12.677477 12.677477"
   version="1.1"
   id="svg4696"
   xmlns:xlink="http://www.w3.org/1999/xlink"
   xmlns="http://www.w3.org/2000/svg"
   xmlns:svg="http://www.w3.org/2000/svg">
  <defs
     id="defs4693">
    <linearGradient
       id="linearGradient5454">
      <stop
         style="stop-color:#000000;stop-opacity:1;"
         offset="0"
         id="stop5450" />
      <stop
         style="stop-color:#b3b3b3;stop-opacity:1;"
         offset="1"
         id="stop5452" />
    </linearGradient>
    <linearGradient
       xlink:href="#linearGradient5454"
       id="linearGradient5456"
       x1="45.971851"
       y1="142.58958"
       x2="72.735413"
       y2="142.58958"
       gradientUnits="userSpaceOnUse"
       gradientTransform="matrix(0.47368422,0,0,0.47368422,31.238754,75.047148)" />
  </defs>
  <g
     id="hole_group"
     transform="translate(-53.014895,-136.25085)">
    <circle
       style="opacity:0.77;fill:url(#linearGradient5456);fill-opacity:1;stroke:none;stroke-width:0.469895"
       id="hole_circle"
       cx="59.353634"
       cy="142.58958"
       r="6.3387384" />
  </g>
</svg>
                `
                fabric.loadSVGFromString(str_svg, function(objects, options) {
                    let obj = fabric.util.groupSVGElements(objects, options);
                    obj.id = "hole_group"
                    obj.hoverCursor = 'default'
                    obj.scaleToWidth(50);
                    canvas.add(obj).centerObject(obj).renderAll();
                });
            } else {
                const backgroundObjects = canvas.getObjects().filter(obj => obj.id && String(obj?.id ?? '').startsWith('hole'));
                console.log("backgroundObjects", backgroundObjects)
                backgroundObjects.forEach(obj => {
                    canvas.remove(obj);
                });
            }
        },

        /**
         * Place, resize or remove the hanging-slot markers (lightbox and keyring).
         *
         * Each marker is a *single united shape* (one `<path>`, not a circle and
         * a rectangle grouped on top of each other) so what is on screen is
         * exactly what gets cut, not two shapes that merely happen to overlap.
         *
         * Every dimension AND the slide direction live in the shape's own id
         * (`keyhole_<n>_h<head>_s<shank>_l<slot>_d<dir>`, tenths of a
         * millimetre) rather than in a shared setting: each marker a user
         * places is independently sized. ids are the channel used because
         * they are the one thing already proven to survive the Inkscape
         * round-trip on save (`helpers_2d.svg_to_area_drawing`) untouched —
         * arbitrary custom SVG attributes are not.
         *
         * Scaling and rotation are locked: resizing means typing a new size in
         * the "Colgado" panel (which calls `resizeKeyhole`, regenerating the
         * path), not dragging a handle — dragging would silently distort the
         * circle into an ellipse instead of changing its diameter.
         */
        /** Every hanging-slot marker currently on the canvas, with its parsed size. */
        /**
         * ¿Hay algún agujero en el lienzo?
         *
         * Mira el lienzo en vez de `holeExists`, que sólo cuenta el hueco de
         * colgar del llavero: los agujeros de la operación "Agujero pasante" y
         * las ranuras de la caja de luz no lo tocan, y quien preguntara por esa
         * bandera creía que no había agujeros.
         *
         * Recorre los grupos porque `applyHoleOperation` marca las hojas: un
         * agujero puede vivir dentro de un grupo y no aparecer al mirar sólo el
         * primer nivel.
         */
        hasHoles: () => {
            const walk = (objects) => objects.some(object => (
                isHoleObject(object) || (object._objects?.length ? walk(object._objects) : false)
            ));
            return walk(canvas.getObjects());
        },

        getKeyholes: () => canvas.getObjects()
            .filter(obj => obj.id && String(obj.id).startsWith('keyhole'))
            .map(obj => ({ id: obj.id, ...parseKeyholeId(obj.id) }))
            .filter(marker => marker.headDiameterMm !== undefined),

        /**
         * Add one hanging-slot marker. A panel can carry several — a wide one
         * hangs better on two screws — so each gets its own number.
         */
        // `direction` es hacia dónde sale la ranura desde el círculo. Por
        // defecto hacia arriba, que es la única orientación que cuelga: el
        // tornillo entra por el círculo, la pieza baja y queda apoyada en el
        // extremo alto de la ranura. Con la ranura por debajo, la pieza al
        // bajar devuelve el tornillo al círculo grande y se suelta.
        addKeyhole: ({ headDiameterMm = 8, shankDiameterMm = 4.2, slotLengthMm = 8, direction = 'u' } = {}) => {
            const used = canvas.getObjects()
                .map(obj => parseKeyholeId(obj.id))
                .filter(Boolean)
                .map(marker => parseInt(marker.index, 10));
            const index = used.length ? Math.max(...used) + 1 : 1;

            placeKeyholeShape(index, headDiameterMm, shankDiameterMm, slotLengthMm, direction, (obj) => {
                canvas.add(obj);
                placeMarkerInsideBackground(obj, used.length);
                canvas.setActiveObject(obj);
                canvas.renderAll();
            });
        },

        /**
         * Change one marker's circle/rectangle size (and optionally its
         * direction) while keeping its position. Regenerates the shape rather
         * than scaling it, so the circle stays a circle at the new diameter.
         */
        resizeKeyhole: (keyholeId, { headDiameterMm, shankDiameterMm, slotLengthMm, direction } = {}) => {
            const existing = canvas.getObjects().find(obj => obj.id === keyholeId);
            const current = parseKeyholeId(keyholeId);
            if (!existing || !current) return;

            const center = existing.getCenterPoint();
            asInternalEdit(() => canvas.remove(existing));
            placeKeyholeShape(
                current.index,
                headDiameterMm ?? current.headDiameterMm,
                shankDiameterMm ?? current.shankDiameterMm,
                slotLengthMm ?? current.slotLengthMm,
                direction ?? current.direction,
                (obj) => {
                    canvas.add(obj);
                    obj.setPositionByOrigin(new fabric.Point(center.x, center.y), 'center', 'center');
                    obj.setCoords();
                    canvas.setActiveObject(obj);
                    canvas.renderAll();
                },
            );
        },

        /** Remove one marker by id, or all of them when called with no id. */
        removeKeyhole: (keyholeId) => {
            // Also an internal edit: the user is managing a list inside the
            // panel, not deselecting, so the panel must stay open.
            asInternalEdit(() => {
                canvas.getObjects()
                    .filter(obj => obj.id && String(obj.id).startsWith('keyhole')
                        && (!keyholeId || obj.id === keyholeId))
                    .forEach(obj => canvas.remove(obj));
                canvas.discardActiveObject();
            });
            canvas.renderAll();
        },

        getScrews: () => canvas.getObjects()
            .filter(obj => obj.id && String(obj.id).startsWith('screw'))
            .map(obj => ({ id: obj.id, ...parseScrewId(obj.id) }))
            .filter(marker => marker.headDiameterMm !== undefined),

        /**
         * Add one countersunk screw hole.
         *
         * Los valores por defecto son los de un tornillo de 4 mm de cabeza
         * cónica (DIN 965 M4: cabeza 8 mm, rosca 4). Como la ranura, cada
         * marcador se coloca arrastrándolo por el lienzo.
         */
        addScrew: ({ headDiameterMm = 8, shankDiameterMm = 4.2 } = {}) => {
            const used = canvas.getObjects()
                .map(obj => parseScrewId(obj.id))
                .filter(Boolean)
                .map(marker => parseInt(marker.index, 10));
            const index = used.length ? Math.max(...used) + 1 : 1;

            placeScrewShape(index, headDiameterMm, shankDiameterMm, (obj) => {
                canvas.add(obj);
                placeMarkerInsideBackground(obj, used.length);
                canvas.setActiveObject(obj);
                canvas.renderAll();
            });
        },

        /** Change one marker's diameters, keeping it where the user put it. */
        resizeScrew: (screwId, { headDiameterMm, shankDiameterMm } = {}) => {
            const existing = canvas.getObjects().find(obj => obj.id === screwId);
            const current = parseScrewId(screwId);
            if (!existing || !current) return;

            const center = existing.getCenterPoint();
            asInternalEdit(() => canvas.remove(existing));
            placeScrewShape(
                current.index,
                headDiameterMm ?? current.headDiameterMm,
                shankDiameterMm ?? current.shankDiameterMm,
                (obj) => {
                    canvas.add(obj);
                    obj.setPositionByOrigin(new fabric.Point(center.x, center.y), 'center', 'center');
                    obj.setCoords();
                    canvas.setActiveObject(obj);
                    canvas.renderAll();
                },
            );
        },

        /** Remove one marker by id, or all of them when called with no id. */
        removeScrew: (screwId) => {
            asInternalEdit(() => {
                canvas.getObjects()
                    .filter(obj => obj.id && String(obj.id).startsWith('screw')
                        && (!screwId || obj.id === screwId))
                    .forEach(obj => canvas.remove(obj));
                canvas.discardActiveObject();
            });
            canvas.renderAll();
        },

        /** El perfil de base, con su tamaño en mm, o null si no hay. */
        getNapkinJoint: () => {
            const obj = canvas.getObjects().find(isNapkinJointObject);
            if (!obj) return null;
            return {
                id: obj.id,
                widthMm: obj.getScaledWidth() / PX_PER_MM,
                heightMm: obj.getScaledHeight() / PX_PER_MM,
            };
        },

        /** Añade el perfil de base (uno solo) ajustado al borde inferior. */
        addNapkinJoint: ({ heightMm = 8 } = {}) => {
            if (canvas.getObjects().some(isNapkinJointObject)) return;
            const obj = new fabric.Rect({
                width: 10, height: 10,
                fill: NAPKIN_JOINT_FILL, opacity: 0.55,
                stroke: NAPKIN_JOINT_FILL, strokeWidth: 0,
                lockRotation: true, hoverCursor: 'move',
            });
            obj.id = `${NAPKIN_JOINT_PREFIX}1`;
            obj.class = 'napkin_joint';
            obj.setControlsVisibility({ mtr: false });
            fitNapkinJointToBackground(obj, heightMm);
            canvas.add(obj);
            canvas.setActiveObject(obj);
            canvas.renderAll();
        },

        /** Vuelve a ajustarlo al borde inferior, con otra altura si se pide. */
        fitNapkinJoint: ({ heightMm } = {}) => {
            const obj = canvas.getObjects().find(isNapkinJointObject);
            if (!obj) return;
            fitNapkinJointToBackground(obj, heightMm ?? obj.getScaledHeight() / PX_PER_MM);
            canvas.fire('object:modified', { target: obj });
            canvas.renderAll();
        },

        removeNapkinJoint: () => {
            asInternalEdit(() => {
                canvas.getObjects().filter(isNapkinJointObject).forEach(obj => canvas.remove(obj));
                canvas.discardActiveObject();
            });
            canvas.renderAll();
        },

        /**
         * `{ widthMm, heightMm, fold: 'triangle'|'square', tongueHeightMm, marginMm }`,
         * o null para quitarla. Pinta la servilleta, la línea de la base y la zona
         * donde los dibujos no se imprimen.
         */
        setNapkinGuide: (guide) => {
            canvas.__napkinGuide = guide || null;
            canvas.requestRenderAll();
        },

        getCrocsPins: () => canvas.getObjects()
            .filter(obj => String(obj.id || '').startsWith('crocs_'))
            .map(obj => ({ id: obj.id, ...parseCrocsId(obj.id) }))
            .filter(marker => marker.headDiameterMm !== undefined),

        /**
         * Añade un pin de Crocs. Por defecto, medidas de un Jibbitz: cabeza de
         * 11 mm, cuello de 5 mm y 4 mm de largo (el grueso de la tira).
         */
        addCrocsPin: ({
            headDiameterMm = 11, neckDiameterMm = 5, neckLengthMm = 4, headThicknessMm = 1.5, mode = 'i',
        } = {}) => {
            const used = canvas.getObjects()
                .map(obj => parseCrocsId(obj.id))
                .filter(Boolean)
                .map(marker => parseInt(marker.index, 10));
            const index = used.length ? Math.max(...used) + 1 : 1;

            placeCrocsShape(index, { headDiameterMm, neckDiameterMm, neckLengthMm, headThicknessMm, mode }, (obj) => {
                canvas.add(obj);
                placeMarkerInsideBackground(obj, used.length);
                canvas.setActiveObject(obj);
                canvas.renderAll();
            });
        },

        /** Cambia cotas o modo de un pin sin moverlo de donde lo puso el usuario. */
        updateCrocsPin: (pinId, changes = {}) => {
            const existing = canvas.getObjects().find(obj => obj.id === pinId);
            const current = parseCrocsId(pinId);
            if (!existing || !current) return;

            const center = existing.getCenterPoint();
            asInternalEdit(() => canvas.remove(existing));
            placeCrocsShape(current.index, { ...current, ...changes }, (obj) => {
                canvas.add(obj);
                obj.setPositionByOrigin(new fabric.Point(center.x, center.y), 'center', 'center');
                obj.setCoords();
                canvas.setActiveObject(obj);
                canvas.renderAll();
            });
        },

        removeCrocsPin: (pinId) => {
            asInternalEdit(() => {
                canvas.getObjects()
                    .filter(obj => String(obj.id || '').startsWith('crocs_') && (!pinId || obj.id === pinId))
                    .forEach(obj => canvas.remove(obj));
                canvas.discardActiveObject();
            });
            canvas.renderAll();
        },

        getInternalElements: () => {
            const elements = [];
            // Un silbato o un abrebotellas es una entrada aunque sus piezas
            // anden sueltas: se agrupan por su marca y sólo cuenta la primera.
            const seenAssemblies = new Set();
            canvas.getObjects().forEach((obj) => {
                const assemblyId = assemblyIdOf(obj);
                const kind = assemblyKindOf(assemblyId);
                if (kind) {
                    if (seenAssemblies.has(assemblyId)) return;
                    seenAssemblies.add(assemblyId);
                    const index = assemblyIndexOf(assemblyId);
                    const { label, normalize } = ASSEMBLY_KINDS[kind];
                    elements.push({
                        id: assemblyId,
                        name: obj.type === 'group' ? obj.name : `${label} ${index}`,
                        index,
                        kind,
                        ...normalize(assemblyParamsOf([obj], kind)),
                        ...(kind === 'abrelatas' ? { edgeStatus: abrelatasEdgeStatus([obj]) } : {}),
                    });
                    return;
                }
                const parsed = parseInternalElementId(obj.id);
                if (parsed) elements.push({ id: obj.id, name: obj.name, ...parsed });
            });
            return elements;
        },

        addInternalElements: ({ kind = 'magnet', shape = 'circle', quantity = 1, sizeMm, thicknessMm, bottomOffsetMm, heightMm, ...rest } = {}) => {
            if (ASSEMBLY_PLACERS[kind]) {
                const used = canvas.getObjects()
                    .map(obj => parseInternalElementId(obj._objects?.[0]?.id || obj.id))
                    .filter(Boolean)
                    .map(element => element.index);
                const index = used.length ? Math.max(...used) + 1 : 1;
                ASSEMBLY_PLACERS[kind]({ index, ...rest }, (obj) => {
                    canvas.add(obj);
                    obj.set({ left: canvas.width / 2, top: canvas.height / 2 });
                    obj.setCoords();
                    canvas.setActiveObject(obj);
                });
                canvas.requestRenderAll();
                return;
            }
            const normalizedKind = kind === 'nfc' ? 'nfc' : 'magnet';
            const normalizedShape = shape === 'square' ? 'square' : 'circle';
            const defaults = normalizedKind === 'nfc'
                ? { sizeMm: 25, thicknessMm: 0.8, bottomOffsetMm: 0.8 }
                : { sizeMm: 6, thicknessMm: 2, bottomOffsetMm: 0.8 };
            // Los índices los comparten todos los tipos: también cuentan los de
            // las piezas de un silbato o un abrebotellas agrupados.
            const used = canvas.getObjects()
                .map(obj => parseInternalElementId(obj._objects?.[0]?.id || obj.id))
                .filter(Boolean)
                .map(element => element.index);
            const firstIndex = used.length ? Math.max(...used) + 1 : 1;
            const count = Math.min(Math.max(Math.round(Number(quantity) || 1), 1), 20);
            for (let offset = 0; offset < count; offset += 1) {
                const index = firstIndex + offset;
                placeInternalElementShape({
                    index,
                    kind: normalizedKind,
                    shape: normalizedShape,
                    sizeMm: Number(sizeMm) || defaults.sizeMm,
                    heightMm: Number(heightMm) || Number(sizeMm) || defaults.sizeMm,
                    thicknessMm: Number(thicknessMm) || defaults.thicknessMm,
                    bottomOffsetMm: Number.isFinite(Number(bottomOffsetMm)) ? Number(bottomOffsetMm) : defaults.bottomOffsetMm,
                }, (obj) => {
                    canvas.add(obj);
                    obj.set({
                        left: canvas.width / 2 + (offset - (count - 1) / 2) * (obj.getScaledWidth() + 12),
                        top: canvas.height / 2,
                    });
                    obj.setCoords();
                    canvas.setActiveObject(obj);
                });
            }
            canvas.requestRenderAll();
        },

        updateInternalElement: (elementId, changes = {}) => {
            // El silbato se identifica por su marca, no por un objeto: puede
            // estar agrupado o repartido en piezas sueltas tras un guardado.
            const assemblyKind = assemblyKindOf(elementId);
            const pieces = assemblyKind ? assemblyObjects(elementId) : [];
            if (pieces.length) {
                const index = assemblyIndexOf(elementId);
                const { normalize } = ASSEMBLY_KINDS[assemblyKind];
                const container = pieces.find(piece => piece.type === 'group');

                // Cambiar una cota reconstruye el silbato entero, así que hay
                // que devolverle su sitio Y su giro: sin esto, tocar el ángulo
                // del labio lo enderezaba de golpe.
                //
                // Dónde vive el giro depende de cómo esté: en el grupo si se
                // rotó tal cual, o horneado en cada pieza si el guardado lo
                // deshizo y se volvió a agrupar (`regroupAssemblyPieces`).
                const angle = container
                    ? (container.angle || container._objects?.[0]?.angle || 0)
                    : (pieces[0]?.angle || 0);

                // Con el grupo intacto el centro es exacto. Suelto hay que
                // recomponerlo desde las cajas de las piezas.
                let center;
                if (container) {
                    center = container.getCenterPoint();
                } else {
                    const bounds = pieces.reduce((box, piece) => {
                        const rect = piece.getBoundingRect(true);
                        return {
                            left: Math.min(box.left, rect.left),
                            top: Math.min(box.top, rect.top),
                            right: Math.max(box.right, rect.left + rect.width),
                            bottom: Math.max(box.bottom, rect.top + rect.height),
                        };
                    }, { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity });
                    center = new fabric.Point(
                        (bounds.left + bounds.right) / 2,
                        (bounds.top + bounds.bottom) / 2,
                    );
                }

                const next = {
                    index,
                    ...normalize(assemblyParamsOf(pieces, assemblyKind)),
                    ...changes,
                    name: changes.name ?? container?.name,
                };
                asInternalEdit(() => canvas.remove(...pieces));
                ASSEMBLY_PLACERS[assemblyKind](next, (obj) => {
                    canvas.add(obj);
                    // El giro antes de colocar: `setPositionByOrigin` centra la
                    // caja ya rotada, y al revés el centro saldría desplazado.
                    obj.set({ angle });
                    obj.setPositionByOrigin(center, 'center', 'center');
                    obj.setCoords();
                    canvas.setActiveObject(obj);
                    canvas.requestRenderAll();
                });
                return;
            }

            const requested = parseInternalElementId(elementId);
            const existing = canvas.getObjects().find(obj => (
                obj.id === elementId
                || (requested && parseInternalElementId(obj.id)?.index === requested.index)
            ));
            const current = parseInternalElementId(existing?.id);
            if (!existing || !current) return;
            const center = existing.getCenterPoint();
            const next = { ...current, ...changes, name: changes.name ?? existing.name };
            asInternalEdit(() => canvas.remove(existing));
            placeInternalElementShape(next, (obj) => {
                canvas.add(obj);
                obj.setPositionByOrigin(center, 'center', 'center');
                obj.setCoords();
                canvas.setActiveObject(obj);
                canvas.requestRenderAll();
            });
        },

        removeInternalElement: (elementId) => {
            asInternalEdit(() => {
                canvas.getObjects()
                    // Borrar un silbato o un abrebotellas borra todas sus
                    // piezas, estén agrupadas o sueltas.
                    .filter(obj => (assemblyIdOf(obj)
                        ? (!elementId || assemblyIdOf(obj) === elementId)
                        : String(obj.id || '').startsWith('insert_') && (!elementId || obj.id === elementId)))
                    .forEach(obj => canvas.remove(obj));
                canvas.discardActiveObject();
            });
            canvas.requestRenderAll();
        },
        vectorizeTextClient,
        getVectorizableTextSvg,
        applyVectorizedTextSvg,
        groupActiveObjects: () => {
            console.log("Agrupando objetos activos")
            if (!canvas.getActiveObject()) {
                return;
            }
            if (canvas.getActiveObject().type !== 'activeSelection') {
                return;
            }
            canvas.getActiveObject().toGroup();
            canvas.requestRenderAll();
        },
        ungroupActiveObjects: () => {
            ungroupActiveObjects(canvas)
        },
        sendToBack: () => {
            canvas.getActiveObjects().forEach((object) => {
                canvas.sendToBack(object);
            });
            pinFloorLayers(canvas);
            canvas.fire('layer:reordered');
        },
        bringForward: () => {
            canvas.getActiveObjects().forEach((object) => {
                canvas.bringForward(object);
            });
            pinFloorLayers(canvas);
            canvas.fire('layer:reordered');
        },
        sendBackwards: () => {
            canvas.getActiveObjects().forEach((object) => {
                canvas.sendBackwards(object);
            });
            pinFloorLayers(canvas);
            canvas.fire('layer:reordered');
        },
        bringToFront: () => {
            canvas.getActiveObjects().forEach((object) => {
                canvas.bringToFront(object);
            });
            canvas.fire('layer:reordered');
        },
        /**
         * Mueve la selección `dxMm`/`dyMm` milímetros (flechas del teclado).
         *
         * Se mueve el objeto activo tal cual — una selección múltiple se mueve
         * entera — y se respeta el bloqueo de movimiento de cada eje. Devuelve
         * si se movió algo, para que quien llama sepa si comerse la tecla.
         */
        nudgeSelected: (dxMm, dyMm) => {
            const active = canvas.getActiveObject();
            if (!active || active.isEditing) return false;
            if (canvas.getActiveObjects().some(isBackgroundObject)) return false;

            const dx = active.lockMovementX ? 0 : dxMm * PX_PER_MM;
            const dy = active.lockMovementY ? 0 : dyMm * PX_PER_MM;
            if (!dx && !dy) return false;

            active.set({ left: active.left + dx, top: active.top + dy });
            active.setCoords();
            canvas.requestRenderAll();
            // Mismo evento que arrastrar con el ratón: historial (con debounce,
            // así que mantener la tecla pulsada deja una sola entrada) y
            // "sin guardar".
            canvas.fire('object:modified', { target: active, action: 'drag' });
            return true;
        },
        // edge: 'left' | 'centerH' | 'right' | 'top' | 'centerV' | 'bottom'
        // referenceMode: 'selection' (caja de la propia selección, por defecto)
        // | 'first' (primer objeto seleccionado) | 'biggest' (mayor área)
        // | 'background' (fondo del diseño)
        alignActiveObjects: (edge, referenceMode = 'selection') => {
            const selectedObjects = canvas.getActiveObjects();
            if (selectedObjects.length < 1) return;

            const target = getAlignmentTargetBounds(referenceMode, selectedObjects);
            if (!target) return;

            selectedObjects.forEach((object) => {
                const bounds = getObjectBounds(object, { tightenTextInk: true });
                let dx = 0;
                let dy = 0;
                if (edge === 'left') dx = target.left - bounds.left;
                else if (edge === 'right') dx = (target.left + target.width) - bounds.right;
                else if (edge === 'centerH') dx = target.centerX - (bounds.left + bounds.right) / 2;
                else if (edge === 'top') dy = target.top - bounds.top;
                else if (edge === 'bottom') dy = (target.top + target.height) - bounds.bottom;
                else if (edge === 'centerV') dy = target.centerY - (bounds.top + bounds.bottom) / 2;

                if (!dx && !dy) return;
                object.set({ left: object.left + dx, top: object.top + dy });
                object.setCoords();
            });

            canvas.requestRenderAll();
            canvas.fire('object:modified');
        },
        // axis: 'horizontal' | 'vertical'. Reparte huecos iguales entre los
        // objetos seleccionados manteniendo fijos el primero y el último
        // (según su posición, no según orden de selección). Necesita 3+.
        distributeActiveObjects: (axis) => {
            const selectedObjects = canvas.getActiveObjects();
            if (selectedObjects.length < 3) return;

            const isHorizontal = axis === 'horizontal';
            const startKey = isHorizontal ? 'left' : 'top';
            const endKey = isHorizontal ? 'right' : 'bottom';

            const items = selectedObjects
                .map((object) => ({ object, bounds: getObjectBounds(object, { tightenTextInk: true }) }))
                .sort((a, b) => a.bounds[startKey] - b.bounds[startKey]);

            const span = items[items.length - 1].bounds[endKey] - items[0].bounds[startKey];
            const sumOfSizes = items.reduce(
                (total, item) => total + (item.bounds[endKey] - item.bounds[startKey]),
                0,
            );
            const gap = (span - sumOfSizes) / (items.length - 1);

            let cursor = items[0].bounds[startKey];
            items.forEach(({ object, bounds }) => {
                const size = bounds[endKey] - bounds[startKey];
                const delta = cursor - bounds[startKey];
                if (delta) {
                    object.set(isHorizontal
                        ? { left: object.left + delta }
                        : { top: object.top + delta });
                    object.setCoords();
                }
                cursor += size + gap;
            });

            canvas.requestRenderAll();
            canvas.fire('object:modified');
        },
        // Espeja la selección. `axis`: 'horizontal' (izquierda↔derecha) |
        // 'vertical' (arriba↔abajo). `referenceMode` decide sobre qué eje:
        // 'each' espeja cada objeto sobre su propio centro (se quedan donde
        // están, cambian de mano); el resto — 'selection' | 'first' |
        // 'biggest' | 'background' — usan la misma caja objetivo que alinear,
        // así que además intercambian posiciones dentro de ella.
        mirrorActiveObjects: (axis, referenceMode = 'selection') => {
            const selectedObjects = canvas.getActiveObjects();
            if (!selectedObjects.length) return;

            const isHorizontal = axis !== 'vertical';
            const target = referenceMode === 'each'
                ? null
                : getAlignmentTargetBounds(referenceMode, selectedObjects);
            if (referenceMode !== 'each' && !target) return;

            // `flipX`/`flipY` a mano no valen: se quedarían cortos con objetos
            // rotados o inclinados, y no mueven nada de sitio. Se usa la misma
            // transformación que el cambio de cara (`mirrorObjectAbout`), que
            // fabric premultiplica y vuelve a descomponer.
            //
            // Hay que soltar la selección antes: los objetos de una
            // `ActiveSelection` cuelgan de ella, con coordenadas relativas a su
            // centro, y espejarlos ahí dentro los mandaría a cualquier parte.
            // Mismo baile que `withCanonicalFace`, incluido devolver la
            // selección al terminar.
            canvas.discardActiveObject();
            selectedObjects.forEach((object) => {
                const center = object.getCenterPoint();
                if (isHorizontal) mirrorObjectAbout(object, target ? target.centerX : center.x);
                else mirrorObjectAboutRow(object, target ? target.centerY : center.y);
            });
            restoreSelection(selectedObjects);

            canvas.requestRenderAll();
            canvas.fire('object:modified', {
                target: selectedObjects.length === 1 ? selectedObjects[0] : undefined,
                action: 'mirror',
            });
        },
    };
    return editorApi;
}

const useFabricJSEditor = (props = {}) => {
    const scaleStep = props.scaleStep || 0.5
    const { defaultFillColor, defaultStrokeColor } = props
    const [canvas, setCanvas] = useState(null)
    const [fillColor, setFillColor] = useState(defaultFillColor || FILL)
    const [strokeColor, setStrokeColor] = useState(defaultStrokeColor || "transparent")
    const [selectedObjects, setSelectedObject] = useState([])
    const [clipboard, setClipboard] = useState(null)
    const [baseLayout, setBaseLayout] = useState(null)
    const [windowObj, setWindowObj] = useState(null)
    const [center, setCenter] = useState(null)
    const [historyInfo, setHistoryInfo] = useState({ entries: [], currentIndex: -1 })

    useEffect(() => {
        setCenter([windowObj?.innerWidth / 2, windowObj?.innerHeight / 2])
        const bindEventss = (canvas) => {
            window.addEventListener('orientationchange', function() {
                canvas.calcOffset();
                canvas.requestRenderAll();
            });
        }
        if (windowObj && canvas) {
            bindEventss(canvas, windowObj)
        }
    }, [windowObj]);

    useEffect(() => {
        const bindEvents = (canvas) => {
            let gesture = null;
            let hiddenGestureObject = null;
            let hiddenGestureState = null;
            let multitouchReleaseTimer = null;

            const clampGestureZoom = (zoom) => Math.min(Math.max(zoom, MIN_CANVAS_ZOOM), MAX_CANVAS_ZOOM);
            const zoomCanvasToPointForGesture = (point, nextZoom) => {
                canvas.zoomToPoint(point, clampGestureZoom(nextZoom));
                canvas.requestRenderAll();
            };
            const markMultitouch = (active) => {
                if (multitouchReleaseTimer) clearTimeout(multitouchReleaseTimer);
                if (active) {
                    canvas.__customiz3rMultitouch = true;
                    return;
                }
                multitouchReleaseTimer = setTimeout(() => {
                    canvas.__customiz3rMultitouch = false;
                }, 250);
            };
            const hideGestureSelection = (object) => {
                if (!object || hiddenGestureObject === object) return;
                if (hiddenGestureObject) {
                    hiddenGestureObject.set(hiddenGestureState);
                }
                hiddenGestureObject = object;
                hiddenGestureState = {
                    hasBorders: object.hasBorders,
                    hasControls: object.hasControls,
                };
                object.set({
                    hasBorders: false,
                    hasControls: false,
                });
                canvas.requestRenderAll();
            };
            const restoreGestureSelection = () => {
                if (!hiddenGestureObject) return;
                hiddenGestureObject.set(hiddenGestureState);
                hiddenGestureObject.setCoords();
                hiddenGestureObject = null;
                hiddenGestureState = null;
                canvas.requestRenderAll();
            };

            const touchList = (event) => {
                const touches = event?.touches;
                return touches && touches.length ? Array.from(touches) : [];
            };
            const isTouchEvent = (event) => Boolean(event?.touches || event?.changedTouches || event?.pointerType === 'touch');
            const touchPoint = (touch) => {
                const rect = canvas.upperCanvasEl.getBoundingClientRect();
                return new fabric.Point(touch.clientX - rect.left, touch.clientY - rect.top);
            };
            const midpoint = (pointA, pointB) => new fabric.Point(
                (pointA.x + pointB.x) / 2,
                (pointA.y + pointB.y) / 2
            );
            const distance = (pointA, pointB) => Math.hypot(pointB.x - pointA.x, pointB.y - pointA.y);
            const angle = (pointA, pointB) => Math.atan2(pointB.y - pointA.y, pointB.x - pointA.x) * 180 / Math.PI;
            const canRotateObject = (object) => (
                object &&
                object.selectable !== false &&
                !String(object?.id ?? '').startsWith('background') &&
                !String(object?.id ?? '').startsWith('hole')
            );
            const findEditableTarget = (event) => {
                try {
                    const target = canvas.findTarget(event, true);
                    return canRotateObject(target) ? target : null;
                }
                catch {
                    return null;
                }
            };
            const getGestureTarget = (event, fallbackTarget = null) => {
                const activeObject = canvas.getActiveObject();
                if (canRotateObject(activeObject)) return activeObject;
                if (canRotateObject(fallbackTarget)) return fallbackTarget;
                return findEditableTarget(event);
            };
            const startGesture = (event, fallbackTarget = null) => {
                const touches = touchList(event);
                if (touches.length < 2) return false;

                const pointA = touchPoint(touches[0]);
                const pointB = touchPoint(touches[1]);
                const target = getGestureTarget(event, fallbackTarget);

                gesture = {
                    distance: distance(pointA, pointB),
                    midpoint: midpoint(pointA, pointB),
                    angle: angle(pointA, pointB),
                    target,
                    changedTarget: false,
                };
                canvas.selection = false;
                // El primer dedo ya pudo iniciar un arrastre en fabric; sin
                // cancelarlo, el objeto sigue al gesto de dos dedos.
                if (canvas._currentTransform) {
                    canvas._currentTransform = null;
                    canvas.fire('mouse:up', { e: event });
                }
                markMultitouch(true);
                hideGestureSelection(target || canvas.getActiveObject());
                return true;
            };
            const updateGesture = (event) => {
                const touches = touchList(event);
                if (!gesture || touches.length < 2) return false;

                const pointA = touchPoint(touches[0]);
                const pointB = touchPoint(touches[1]);
                const nextMidpoint = midpoint(pointA, pointB);
                const nextDistance = distance(pointA, pointB);
                const nextAngle = angle(pointA, pointB);
                const deltaX = nextMidpoint.x - gesture.midpoint.x;
                const deltaY = nextMidpoint.y - gesture.midpoint.y;
                const startingZoom = canvas.getZoom();

                if (gesture.distance > 0 && nextDistance > 0) {
                    const zoomFactor = nextDistance / gesture.distance;
                    zoomCanvasToPointForGesture(nextMidpoint, startingZoom * zoomFactor);
                }

                // Dos dedos son siempre navegacion: zoom y desplazamiento del
                // lienzo, nunca transformacion de lo que haya debajo. Antes movian
                // y rotaban el objeto bajo los dedos aunque no estuviera
                // seleccionado, asi que ampliar un diseno grande arrastraba sin
                // querer el path sobre el que cayeran los dedos. Mover y rotar
                // siguen estando: con un dedo y con el tirador de rotacion.
                canvas.relativePan(new fabric.Point(deltaX, deltaY));

                gesture = {
                    ...gesture,
                    distance: nextDistance,
                    midpoint: nextMidpoint,
                    angle: nextAngle,
                };

                canvas.requestRenderAll();
                return true;
            };
            const endGesture = () => {
                if (gesture?.changedTarget && gesture.target) {
                    canvas.fire('object:modified', { target: gesture.target });
                }
                gesture = null;
                canvas.selection = true;
                restoreGestureSelection();
                markMultitouch(false);
            };
            const preventTouchGesture = (event) => {
                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation?.();
            };
            let activeTouchCount = 0;
            const handleNativeTouchStart = (event) => {
                const touches = touchList(event);
                activeTouchCount = touches.length;
                if (touches.length === 1) {
                    hideGestureSelection(findEditableTarget(event));
                    return;
                }
                if (touches.length >= 2) {
                    startGesture(event);
                    preventTouchGesture(event);
                }
            };
            const handleNativeTouchMove = (event) => {
                if (updateGesture(event) || activeTouchCount >= 2) {
                    preventTouchGesture(event);
                }
            };
            const handleNativeTouchEnd = (event) => {
                activeTouchCount = touchList(event).length;
                if (gesture && touchList(event).length < 2) {
                    endGesture();
                    preventTouchGesture(event);
                }
                else if (!gesture && touchList(event).length === 0) {
                    restoreGestureSelection();
                }
            };

            canvas.on('mouse:down', function(options) {
                const touches = touchList(options.e);
                if (touches.length >= 2) {
                    startGesture(options.e, options.target);
                    options.e.preventDefault();
                    options.e.stopPropagation();
                    return;
                }

                const target = options.target;
                if (isTouchEvent(options.e)) {
                    hideGestureSelection(target);
                }
                if (target) {
                    const stackedObjects = canvas.getObjects().filter(obj => {
                        return obj !== target && obj.intersectsWithObject(target);
                    });
                }
            });

            canvas.on('mouse:move', function(options) {
                if (!updateGesture(options.e)) return;
                options.e.preventDefault();
                options.e.stopPropagation();
            });

            canvas.on('mouse:up', function(options) {
                const touches = touchList(options.e);
                if (touches.length < 2) {
                    if (gesture) {
                        endGesture();
                    }
                    else {
                        restoreGestureSelection();
                    }
                }
            });

            canvas.on('mouse:dblclick', function(e) {
                if (e.target && (e.target.type === 'text' || e.target.type === 'i-text')) {
                    console.log("aaaaaaaaaaaaaaa", e.target)
                    e.target.enterEditing();
                    canvas.setActiveObject(e.target);
                }
            });

            canvas.on('mouse:wheel', function(opt) {
                var delta = opt.e.deltaY;
                var zoom = canvas.getZoom();
                zoom *= 0.999 ** delta;
                var zoomPoint = new fabric.Point(opt.e.offsetX, opt.e.offsetY);
                zoomCanvasToPointForGesture(zoomPoint, zoom);
                opt.e.preventDefault();
                opt.e.stopPropagation();
            });

            const touchOptions = { passive: false, capture: true };
            canvas.upperCanvasEl?.addEventListener('touchstart', handleNativeTouchStart, touchOptions);
            canvas.upperCanvasEl?.addEventListener('touchmove', handleNativeTouchMove, touchOptions);
            canvas.upperCanvasEl?.addEventListener('touchend', handleNativeTouchEnd, touchOptions);
            canvas.upperCanvasEl?.addEventListener('touchcancel', handleNativeTouchEnd, touchOptions);

            return () => {
                if (multitouchReleaseTimer) clearTimeout(multitouchReleaseTimer);
                canvas.upperCanvasEl?.removeEventListener('touchstart', handleNativeTouchStart, touchOptions);
                canvas.upperCanvasEl?.removeEventListener('touchmove', handleNativeTouchMove, touchOptions);
                canvas.upperCanvasEl?.removeEventListener('touchend', handleNativeTouchEnd, touchOptions);
                canvas.upperCanvasEl?.removeEventListener('touchcancel', handleNativeTouchEnd, touchOptions);
            };
        }
        let cleanup;
        if (canvas) {
            cleanup = bindEvents(canvas)
        }
        return () => cleanup?.();
    }, [canvas])

    useEffect(() => {
        console.log('BaseLayout:', baseLayout);
    }, [baseLayout])

    useEffect(() => {
        if (canvas) {
        }
    }, [canvas])

    const editor = useMemo(() => canvas ? buildEditor(canvas, clipboard, setClipboard, fillColor, strokeColor, setFillColor, setStrokeColor, scaleStep, window, setHistoryInfo) : undefined, [canvas])
    return {
        selectedObjects, onReady: (canvasReady) => {
            console.log('Fabric canvas ready')
            setCanvas(canvasReady)
            setWindowObj(window)
        }, editor, historyInfo
    }
}

// El panel de elementos internos dibuja sus campos a partir de estos dos: así
// añadir una cota al silbato es tocar `SILBATO_DEFAULTS` y `SILBATO_LIMITS`,
// y no dos ficheros que se desincronizan.
export { SILBATO_DEFAULTS, SILBATO_LIMITS, buildSilbatoGeometry }
export {
    ABRIDOR_DEFAULTS, ABRIDOR_LIMITS, ABRIDOR_POCKET_COLORS, ABRIDOR_POCKET_SHAPES, ABRIDOR_PROFILE_DEPTH_LIMITS,
    ABRIDOR_PROFILE_POINTS, ABRIDOR_WASHERS, abridorDepthColor, buildAbridorGeometry, sampleAbridorProfile,
}
export {
    ABRELATAS_DEFAULTS, ABRELATAS_LIMITS, ABRELATAS_MIN_EDGE_MM, ABRELATAS_MIN_ROOF_MM, ABRELATAS_OVERSHOOT_COLOR,
    ABRELATAS_SHAPES, buildAbrelatasGeometry, buildAbrelatasId,
}
// El gestor de capas mide los agujeros en mm; la conversión vive aquí para no
// tener dos copias del factor que se desincronicen.
export { PX_PER_MM }
export { holeMeasureLabels }
export { buildEditor, useFabricJSEditor }


// Keeps the root <svg> element intact (preserving viewBox) but removes paths
// that don't have the target class. This guarantees both bg and dm groups share
// the same coordinate system so centerObject aligns them perfectly.
function filterSVGByClass(svgString, targetClass) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgString, 'image/svg+xml');
    doc.querySelectorAll('path, rect, circle, ellipse, polygon, line, polyline').forEach(shape => {
        if (shape.getAttribute('class') !== targetClass) {
            shape.remove();
        }
    });
    return new XMLSerializer().serializeToString(doc);
}

// `onReady` avisa cuando el fondo ya esta en el lienzo. `fabric.loadSVGFromString`
// es asincrono, asi que sin el no hay forma de saber desde fuera cuando se puede
// dimensionar el grupo recien cargado.
function getBaseLayoutToCanvas(svg, canvas, widthMm, heightMm, backgroundColor = null, onReady = null) {
    // Quick check for classified elements without a full parse
    const hasBgClass = svg.includes('class="background"');
    const hasDmClass = svg.includes('class="drawing_doesnt_move"');

    if (!hasBgClass && !hasDmClass) {
        // Fallback: no classification, treat all as background (circles, hearts, etc.)
        fabric.loadSVGFromString(svg, function(objects, options) {
            const bgGroup = fabric.util.groupSVGElements(objects, options);
            bgGroup.selectable = false;
            bgGroup.evented = false;
            bgGroup.fill = '#ffffff';
            bgGroup.stroke = null;
            bgGroup.strokeWidth = 0;
            bgGroup.hoverCursor = 'default';
            bgGroup.id = 'background';
            if (backgroundColor) applyFillColor(bgGroup, backgroundColor);
            canvas.add(bgGroup).centerObject(bgGroup);
            bgGroup.setCoords();
            refreshBackgroundRulers(canvas, widthMm, heightMm);
            canvas.renderAll();
            onReady?.();
        });
        return;
    }

    // Load background-only SVG (same root <svg> viewBox -> same coordinate system)
    const bgSVG = filterSVGByClass(svg, 'background');
    fabric.loadSVGFromString(bgSVG, function(bgObjects, bgOptions) {
        const bgGroup = fabric.util.groupSVGElements(bgObjects, bgOptions);
        bgGroup.selectable = false;
        bgGroup.evented = false;
        bgGroup.hoverCursor = 'default';
        bgGroup.id = 'background';
        if (backgroundColor) applyFillColor(bgGroup, backgroundColor);
        // Posicion natural del fondo (en el sistema de coords del viewBox) antes de centrar
        const bgNatLeft = bgGroup.left;
        const bgNatTop = bgGroup.top;
        canvas.add(bgGroup).centerObject(bgGroup);
        bgGroup.setCoords();
        // Desplazamiento aplicado al centrar: lo replicamos en las capas fijas
        const deltaLeft = bgGroup.left - bgNatLeft;
        const deltaTop = bgGroup.top - bgNatTop;

        if (!hasDmClass) {
            refreshBackgroundRulers(canvas, widthMm, heightMm);
            canvas.renderAll();
            onReady?.();
            return;
        }

        // Load doesnt_move-only SVG - mismo viewBox + mismo delta = posicion relativa exacta
        const dmSVG = filterSVGByClass(svg, 'drawing_doesnt_move');
        fabric.loadSVGFromString(dmSVG, function(dmObjects, dmOptions) {
            const dmGroup = fabric.util.groupSVGElements(dmObjects, dmOptions);
            dmGroup.selectable = false;
            dmGroup.evented = false;
            dmGroup.hasControls = false;
            dmGroup.hasBorders = false;
            dmGroup.lockMovementX = true;
            dmGroup.lockMovementY = true;
            dmGroup.hoverCursor = 'default';
            dmGroup.class = 'drawing_doesnt_move';
            dmGroup.id = 'doesnt_move_group';
            dmGroup.left = dmGroup.left + deltaLeft;
            dmGroup.top = dmGroup.top + deltaTop;
            dmGroup.setCoords();
            canvas.add(dmGroup);
            pinFloorLayers(canvas);
            refreshBackgroundRulers(canvas, widthMm, heightMm);
            canvas.renderAll();
            onReady?.();
        });
    });
}

// Caja delimitadora absoluta (espacio del canvas, sin viewport de zoom/pan —
// mismo criterio que el resto del fichero) de un objeto que puede estar
// dentro de un grupo. `getBoundingRect` no compone el transform del grupo
// padre (solo usa `left/top/angle` propios, que dentro de un grupo son
// relativos a él), así que aquí se compone a mano con `calcTransformMatrix`
// (que sí sube por `object.group` recursivamente) sobre las 4 esquinas
// locales del objeto.
//
// Las esquinas tienen que ser las dimensiones SIN escalar
// (`_getNonTransformedDimensions`), no las ya escaladas
// (`_getTransformedDimensions`): `calcTransformMatrix`/`calcOwnMatrix` ya
// incluye `scaleX/scaleY` (`util.composeMatrix`), así que partir de esquinas
// ya escaladas aplicaba la escala dos veces — la pieza salía más grande de
// lo que era en realidad, y hasta con una sola pieza (grupo con un único
// hijo) el bounding box de "por piezas" no coincidía con el de "conjunto".
const getAbsoluteObjectBounds = (object) => {
    const matrix = object.calcTransformMatrix();
    const dim = object._getNonTransformedDimensions();
    const w = dim.x / 2;
    const h = dim.y / 2;
    const corners = [
        { x: -w, y: -h }, { x: w, y: -h }, { x: w, y: h }, { x: -w, y: h },
    ].map((point) => fabric.util.transformPoint(point, matrix));
    const xs = corners.map((point) => point.x);
    const ys = corners.map((point) => point.y);
    const left = Math.min(...xs);
    const top = Math.min(...ys);
    return { left, top, width: Math.max(...xs) - left, height: Math.max(...ys) - top };
};

// Cuenta las letras "M"/"m" (subtrayectos) de un fabric.Path ya parseado —
// fabric normaliza a comandos absolutos en mayúscula al parsear el SVG.
const countSubpaths = (pathObject) => (
    (pathObject.path || []).filter((command) => command[0] === 'M').length
);

// Un "Fill"/"Offset" de dibujos que no se tocan (p. ej. letras separadas)
// puede volver del backend como un único `<path>` con dos subtrayectos
// M...Z independientes en su `d` — Inkscape/Manifold lo unen así, pero
// sigue siendo UN objeto de fabric, no dos. `getBackgroundClusters` agrupa
// por objeto, así que sin esto nunca vería más de una pieza aquí. Parte
// cada subtrayecto en su propio `fabric.Path`, reposicionándolo con el
// mismo truco que `vectorizeTextClient` (el punto local P cae en
// `rotateScale(P - pathOffset) + centro`, así que cambiar de pathOffset
// solo cambia el centro que hay que sumar, no las coordenadas del trazo).
const splitPathIntoSubpaths = (pathObject) => {
    const subpathGroups = [];
    let current = [];
    (pathObject.path || []).forEach((command) => {
        if (command[0] === 'M' && current.length) {
            subpathGroups.push(current);
            current = [];
        }
        current.push(command);
    });
    if (current.length) subpathGroups.push(current);
    if (subpathGroups.length <= 1) return [pathObject];

    const angleRad = fabric.util.degreesToRadians(pathObject.angle || 0);
    const scaleX = pathObject.scaleX ?? 1;
    const scaleY = pathObject.scaleY ?? 1;
    const originalCenter = pathObject.getCenterPoint();
    const originalOffset = pathObject.pathOffset;

    return subpathGroups.map((commands) => {
        const subPath = new fabric.Path(fabric.util.joinPath(commands), {
            fill: pathObject.fill,
            stroke: pathObject.stroke,
            strokeWidth: pathObject.strokeWidth,
            opacity: pathObject.opacity,
        });
        const deltaLocal = new fabric.Point(
            subPath.pathOffset.x - originalOffset.x,
            subPath.pathOffset.y - originalOffset.y,
        );
        const rotatedDelta = fabric.util.rotatePoint(
            new fabric.Point(deltaLocal.x * scaleX, deltaLocal.y * scaleY),
            new fabric.Point(0, 0),
            angleRad,
        );
        subPath.set({
            angle: pathObject.angle,
            scaleX,
            scaleY,
            skewX: pathObject.skewX,
            skewY: pathObject.skewY,
            flipX: pathObject.flipX,
            flipY: pathObject.flipY,
        });
        subPath.setPositionByOrigin(
            new fabric.Point(originalCenter.x + rotatedDelta.x, originalCenter.y + rotatedDelta.y),
            'center',
            'center',
        );
        subPath.setCoords();
        return subPath;
    });
};

// Se llama antes de clusterizar. Idempotente — una vez partido, las
// siguientes llamadas no encuentran nada que dividir y no tocan el canvas.
//
// Dos formas de llegar un fondo con piezas escondidas en un único Path:
//  - Envuelto en grupo (varias formas en el SVG): se mira cada hijo.
//  - **Sin** grupo: `fabric.util.groupSVGElements` devuelve el objeto
//    suelto (no lo envuelve) cuando el SVG solo trae una forma — así que un
//    Fill/Offset de dos dibujos que no se tocan, que Inkscape/Manifold unen
//    en un único `<path>` con dos subtrayectos M...Z, llega como un Path de
//    primer nivel con `id: "background"`, sin `_objects`. Sin este caso, la
//    función se rendía enseguida y nunca detectaba más de una pieza.
const explodeMultiSubpathBackgroundChildren = (canvas) => {
    const backgroundObject = canvas.getObjects().find((obj) => obj.id === 'background');
    if (!backgroundObject) return false;

    if (backgroundObject._objects?.length) {
        const needsSplit = backgroundObject._objects.some(
            (child) => child.type === 'path' && countSubpaths(child) > 1,
        );
        if (!needsSplit) return false;

        backgroundObject.toActiveSelection();
        canvas.discardActiveObject();
        const children = [...backgroundObject._objects];
        canvas.add(...children);
        backgroundObject._restoreObjectsState();
        canvas.remove(backgroundObject);

        const finalChildren = children.flatMap((child) => (
            child.type === 'path' && countSubpaths(child) > 1 ? splitPathIntoSubpaths(child) : [child]
        ));

        const regrouped = new fabric.Group(finalChildren, {
            selectable: false,
            evented: false,
            hoverCursor: 'default',
            id: 'background',
        });
        canvas.add(regrouped);
        regrouped.setCoords();
        pinFloorLayers(canvas);
        canvas.fire('object:modified');
        return true;
    }

    if (backgroundObject.type === 'path' && countSubpaths(backgroundObject) > 1) {
        canvas.remove(backgroundObject);
        const pieces = splitPathIntoSubpaths(backgroundObject);
        const regrouped = new fabric.Group(pieces, {
            selectable: false,
            evented: false,
            hoverCursor: 'default',
            id: 'background',
        });
        canvas.add(regrouped);
        regrouped.setCoords();
        pinFloorLayers(canvas);
        canvas.fire('object:modified');
        return true;
    }

    return false;
};

// Piezas sueltas de "background": bien podría venir vectorizado como un
// grupo de letras que no se tocan (p. ej. tras un Fill/Offset de dibujos
// separados) — hoy el fondo siempre se trata como un único bloque. Agrupa
// los hijos por solape/proximidad de su bounding box (unión de conjuntos
// disjuntos con un margen de tolerancia, para que piezas casi pegadas
// cuenten como una sola). Con una única pieza da un único clúster, así que
// el modo "por piezas" coincide con "conjunto" en las formas normales.
const BACKGROUND_CLUSTER_GAP_PX = 6; // ~1.6mm
const boundsOverlap = (a, b, tolerance) => (
    a.left - tolerance < b.left + b.width
    && a.left + a.width + tolerance > b.left
    && a.top - tolerance < b.top + b.height
    && a.top + a.height + tolerance > b.top
);

const getBackgroundClusters = (canvas) => {
    explodeMultiSubpathBackgroundChildren(canvas);
    const backgroundGroup = canvas.getObjects().find((obj) => obj.id === 'background');
    if (!backgroundGroup) return [];

    const children = backgroundGroup._objects?.length ? backgroundGroup._objects : [backgroundGroup];
    const items = children.map((obj) => ({ obj, bounds: getAbsoluteObjectBounds(obj) }));

    const clusters = [];
    const visited = new Set();
    items.forEach((item, index) => {
        if (visited.has(index)) return;
        const clusterItems = [item];
        visited.add(index);
        for (let i = 0; i < clusterItems.length; i++) {
            items.forEach((other, otherIndex) => {
                if (visited.has(otherIndex)) return;
                if (boundsOverlap(clusterItems[i].bounds, other.bounds, BACKGROUND_CLUSTER_GAP_PX)) {
                    visited.add(otherIndex);
                    clusterItems.push(other);
                }
            });
        }
        const left = Math.min(...clusterItems.map((i) => i.bounds.left));
        const top = Math.min(...clusterItems.map((i) => i.bounds.top));
        const right = Math.max(...clusterItems.map((i) => i.bounds.left + i.bounds.width));
        const bottom = Math.max(...clusterItems.map((i) => i.bounds.top + i.bounds.height));
        clusters.push({
            objects: clusterItems.map((i) => i.obj),
            bounds: { left, top, width: Math.max(right - left, 1), height: Math.max(bottom - top, 1) },
        });
    });

    // Izquierda a derecha, para que "Pieza 1/2/3..." no baile entre refrescos.
    clusters.sort((a, b) => a.bounds.left - b.bounds.left);
    return clusters;
};

// widthMm / heightMm opcionales: si se pasan, el texto muestra ese valor exacto
// en vez de reconvertir píxeles → mm (evita redondeo por dimensiones enteras del SVG).
// `suffix` distingue varias reglas dibujadas a la vez (modo "por piezas").
function drawRulerForBounds(canvas, bounds, widthMm, heightMm, suffix) {
    const rulerLineX = new fabric.Line(
        [bounds.left, bounds.top - 10, bounds.left + bounds.width, bounds.top - 10],
        { stroke: 'red', selectable: false, evented: false, id: `background_line_1_${suffix}` },
    );
    const rulerLineY = new fabric.Line(
        [bounds.left - 10, bounds.top, bounds.left - 10, bounds.top + bounds.height],
        { stroke: 'red', selectable: false, evented: false, id: `background_line_2_${suffix}` },
    );
    canvas.add(rulerLineX, rulerLineY);

    const pxToMm = 0.264583333333333333333333333333333;
    const mmX = widthMm !== undefined ? parseFloat(widthMm) : bounds.width * pxToMm;
    const mmY = heightMm !== undefined ? parseFloat(heightMm) : bounds.height * pxToMm;

    const textX = new fabric.Text(`${parseFloat(mmX.toFixed(2))}mm`, {
        fontSize: 16,
        id: `background_text_1_${suffix}`,
        fill: 'black',
        left: bounds.left + bounds.width / 2,
        top: bounds.top - 25,
        selectable: false,
        evented: false,
    });
    const textY = new fabric.Text(`${parseFloat(mmY.toFixed(2))}mm`, {
        fontSize: 16,
        fill: 'black',
        id: `background_text_2_${suffix}`,
        left: bounds.left - 50,
        top: bounds.top + bounds.height / 2,
        selectable: false,
        evented: false,
    });
    canvas.add(textX, textY);
}

const removeBackgroundRulers = (canvas) => {
    canvas.getObjects()
        .filter((obj) => String(obj?.id ?? '').startsWith('background_line') || String(obj?.id ?? '').startsWith('background_text'))
        .forEach((obj) => canvas.remove(obj));
};

// Único punto que decide qué cotas se ven: "conjunto" (una regla para todo
// el fondo, el comportamiento de siempre), "piezas" (una regla por clúster
// de `getBackgroundClusters`) o "ninguna" (nada — para cuando estorban). El
// modo vive en `canvas.__cotaDisplayMode` (mismo patrón que otros flags
// enganchados al canvas en este fichero, p. ej. `__customiz3rMultitouch`)
// para que tanto los métodos del editor como `getBaseLayoutToCanvas`
// (función de módulo, sin closure sobre el estado de React) lean el mismo
// valor. widthMm/heightMm solo se usan en modo "conjunto": son el valor
// autoritativo (el que puso el usuario o devolvió el backend), no una
// medida geométrica que pueda arrastrar redondeos.
function refreshBackgroundRulers(canvas, widthMm, heightMm) {
    removeBackgroundRulers(canvas);
    const mode = canvas.__cotaDisplayMode || 'conjunto';
    if (mode === 'ninguna') {
        canvas.renderAll();
        return;
    }

    const backgroundGroup = canvas.getObjects().find((obj) => obj.id === 'background');
    if (!backgroundGroup) {
        canvas.renderAll();
        return;
    }
    backgroundGroup.sendToBack();

    if (mode === 'piezas') {
        getBackgroundClusters(canvas).forEach((cluster, index) => {
            drawRulerForBounds(canvas, cluster.bounds, undefined, undefined, `p${index}`);
        });
    } else {
        drawRulerForBounds(canvas, backgroundGroup.getBoundingRect(true, true), widthMm, heightMm, 'all');
    }
    canvas.renderAll();
}

// Un `id` que no sea string rompe todo el código que lo trata como prefijo
// (`id.startsWith('background')`...). Llega de fuera: SVG importados y JSON
// guardados. Se normaliza al entrar, recursivamente por los hijos de grupos:
// number/boolean -> string, cualquier otra cosa (objeto, array, null) -> sin id.
const sanitizeObjectIds = (object) => {
    if (!object) return object;
    if (object.id !== undefined && typeof object.id !== 'string') {
        if (typeof object.id === 'number' || typeof object.id === 'boolean') {
            object.id = String(object.id);
        } else {
            delete object.id;
        }
    }
    if (Array.isArray(object._objects)) object._objects.forEach(sanitizeObjectIds);
    return object;
};

function addImageToCanvas(media, canvas) {
    fabric.loadSVGFromString(media.svg, function(objects, options) {
        let obj = fabric.util.groupSVGElements(objects, options);
        sanitizeObjectIds(obj);
        obj.id = "drawings_" + media.id;
        obj.name = media.nombre || media.name || 'SVG';
        obj.hoverCursor = 'default';
        obj.scaleToWidth(100);
        obj.objectCaching = true;
        canvas.add(obj).centerObject(obj).renderAll();
        obj.setCoords();
        // Un SVG subido puede traer <text> con su propia familia
        refreshCanvasFonts(canvas);
    });
}

const groupCenterAndUngroupObjects = (canvas) => {
    const objects = canvas.getObjects();
    if (objects.length > 0) {
        const group = new fabric.Group(objects);
        canvas.clear();
        group.set({
            left: canvas.width / 2 - group.width / 2,
            top: canvas.height / 2 - group.height / 2
        });
        group.setCoords();
        canvas.add(group);
        group.toActiveSelection();
        canvas.discardActiveObject();
        canvas.add(...group._objects);
        group._restoreObjectsState();
        canvas.remove(group);
        console.log("group centered and ungrouped");
    }
}

const resizeObjectToDimensions = (objectId, desiredWidth, desiredHeight, canvas) => {
    var newWidth = desiredWidth !== null ? parseFloat(desiredWidth) : 1;
    var newHeight = desiredHeight !== null ? parseFloat(desiredHeight) : 1;

    const pxTomm = 0.264583333333333333333333333333333;
    newWidth = newWidth / pxTomm;
    newHeight = newHeight / pxTomm;

    if ((newWidth !== null && isNaN(newWidth)) || (newHeight !== null && isNaN(newHeight))) {
        console.error("Invalid desired width or height");
        return;
    }

    const targetObject = canvas.getObjects().find(obj => obj.id === objectId);
    if (!targetObject) {
        console.error("Object not found");
        return;
    }

    function getFillColor(object) {
        if (object.type === 'group') {
            return object._objects.map(child => getFillColor(child));
        } else {
            return object.fill;
        }
    }

    const prevColor = getFillColor(targetObject);
    const scaleFactorX = newWidth / targetObject.width;
    const scaleFactorY = newHeight / targetObject.height;
    const newScaledWidth = targetObject.width * scaleFactorX;
    const newScaledHeight = targetObject.height * scaleFactorY;
    const widthDifference = newScaledWidth - targetObject.width * targetObject.scaleX;
    const heightDifference = newScaledHeight - targetObject.height * targetObject.scaleY;

    targetObject.set({
        left: targetObject.left - widthDifference / 2,
        top: targetObject.top - heightDifference / 2,
        scaleX: scaleFactorX,
        scaleY: scaleFactorY,
        fill: prevColor
    });

    targetObject.setCoords();
    canvas.renderAll();
    refreshBackgroundRulers(canvas, desiredWidth, desiredHeight);
    return targetObject;
};

const captureObjectsLayoutState = (canvas, includeObject) => {
    const background = canvas.getObjects().find(obj => obj.id === 'background');
    if (!background) return null;

    const backgroundBounds = background.getBoundingRect(true, true);
    if (!backgroundBounds.width || !backgroundBounds.height) return null;

    const objects = canvas.getObjects()
        .filter(obj => obj !== background && includeObject(obj))
        .map(obj => {
            const center = obj.getCenterPoint();
            return {
                obj,
                relativeCenterX: (center.x - backgroundBounds.left) / backgroundBounds.width,
                relativeCenterY: (center.y - backgroundBounds.top) / backgroundBounds.height,
                scaleX: obj.scaleX,
                scaleY: obj.scaleY,
            };
        });

    return { backgroundBounds, objects };
};

const resizeObjectsWithLayoutState = (canvas, background, layoutState) => {
    if (!background || !layoutState?.objects?.length) return;

    const oldBounds = layoutState.backgroundBounds;
    const newBounds = background.getBoundingRect(true, true);
    if (!oldBounds.width || !oldBounds.height || !newBounds.width || !newBounds.height) return;

    const scaleX = newBounds.width / oldBounds.width;
    const scaleY = newBounds.height / oldBounds.height;

    layoutState.objects.forEach(({ obj, relativeCenterX, relativeCenterY, scaleX: oldScaleX, scaleY: oldScaleY }) => {
        if (!canvas.getObjects().includes(obj)) return;

        obj.set({
            scaleX: oldScaleX * scaleX,
            scaleY: oldScaleY * scaleY,
        });
        obj.setPositionByOrigin(
            new fabric.Point(
                newBounds.left + relativeCenterX * newBounds.width,
                newBounds.top + relativeCenterY * newBounds.height,
            ),
            'center',
            'center',
        );
        obj.setCoords();
    });
};

// Mantiene las capas "suelo" en su sitio: el fondo siempre al fondo y, justo encima,
// las capas drawing_doesnt_move. Los objetos del usuario nunca pueden bajar por debajo.
// (Se llama tras cada reordenación de z-index, igual que el re-pin del background.)
const pinFloorLayers = (canvas) => {
    canvas.getObjects()
        .filter(obj => obj.class === 'drawing_doesnt_move')
        .forEach(obj => canvas.sendToBack(obj));
    canvas.getObjects()
        .filter(obj => obj.id && String(obj?.id ?? '').startsWith('background'))
        .forEach(obj => canvas.sendToBack(obj));
};

const ungroupActiveObjects = (canvas) => {
    console.log("Desagrupando objetos activos")
    if (!canvas.getActiveObject()) {
        return;
    }
    if (canvas.getActiveObject().type !== 'group') {
        return;
    }
    canvas.getActiveObject().toActiveSelection();
    canvas.requestRenderAll();
};
